import {
  ArrowLeft,
  ArrowRight,
  Baby,
  BadgeCheck,
  BarChart3,
  Bell,
  BriefcaseBusiness,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleHelp,
  Clock3,
  Download,
  Droplets,
  Eye,
  FileDown,
  FileText,
  Filter,
  Fuel,
  Globe2,
  Heart,
  HeartHandshake,
  Home,
  ImagePlus,
  Info,
  LayoutDashboard,
  Laugh,
  Leaf,
  LogIn,
  LogOut,
  LockKeyhole,
  Mail,
  MapPin,
  Menu,
  Meh,
  Minus,
  Package,
  Palette,
  Pencil,
  PieChart,
  Plus,
  Printer,
  Rocket,
  RefreshCw,
  Save,
  Search,
  Settings2,
  ShieldCheck,
  Smile,
  Shirt,
  ShoppingBag,
  Smartphone,
  Sparkles,
  SprayCan,
  Tag,
  TrendingUp,
  Trash2,
  Upload,
  UserRound,
  Users,
  Utensils,
  Wrench,
  X,
  Frown,
  type LucideIcon,
} from "lucide-react";
import {
  Fragment,
  useEffect,
  createContext,
  useContext,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type FormEvent,
  type ReactNode,
  type SetStateAction,
} from "react";
import * as L from "leaflet";
import "leaflet/dist/leaflet.css";
import coffeeDemandImage from "./assets/demand-products/coffee.jpg";
import cookingOilDemandImage from "./assets/demand-products/cooking-oil.jpg";
import instantNoodlesDemandImage from "./assets/demand-products/instant-noodles.jpg";
import laundryDetergentDemandImage from "./assets/demand-products/laundry-detergent.jpg";
import riceDemandImage from "./assets/demand-products/rice.jpg";
import tissueDemandImage from "./assets/demand-products/tissue.jpg";
import babyProductsCategoryImage from "./assets/categories/baby-products.jpg";
import clothingCategoryImage from "./assets/categories/clothing.jpg";
import foodDiningCategoryImage from "./assets/categories/food-dining.jpg";
import fuelTransportCategoryImage from "./assets/categories/fuel-transport.jpg";
import groceriesCategoryImage from "./assets/categories/groceries.jpg";
import homeCareCategoryImage from "./assets/categories/home-care.jpg";
import mobileDataCategoryImage from "./assets/categories/mobile-data.jpg";
import personalCareCategoryImage from "./assets/categories/personal-care.jpg";
import dealsTailoredBenefitImage from "./assets/member-benefits/deals-tailored.jpg";
import wholesaleTogetherBenefitImage from "./assets/member-benefits/wholesale-together.jpg";
import controlAlwaysBenefitImage from "./assets/member-benefits/control-always.jpg";
import kitanegoCategorySelectionPhoto from "./assets/kitanego-category-selection.jpg";
import kitanegoLogo from "./assets/kitanego-logo.png";
import kitanegoLogoWordmark from "./assets/kitanego-logo-wordmark.png";
import kitanegoLogoWordmarkWhite from "./assets/kitanego-logo-wordmark-white.png";
import AboutUsDialog from "./AboutUsDialog";
import ContactUsDialog from "./ContactUsDialog";
import FaqDialog from "./FaqDialog";
import CommunityDemandSummaryDialog from "./CommunityDemandSummaryDialog";
import DeleteMonthlyDemandDialog from "./DeleteMonthlyDemandDialog";
import MockThree from "./MockThree";
import ProfileEditor from "./ProfileEditor";
import BrandDetailSheet from "./BrandDetailSheet";
import ProductDetailSheet from "./ProductDetailSheet";
import ProductBrandSettings from "./ProductBrandSettings";
import { useProductCatalog } from "./ProductCatalogProvider";
import { loadCatalogProductBrandOptions } from "./catalog-data";
import RegistrationInfoDialog, { type RegistrationInfoTopic } from "./RegistrationInfoDialog";
import RegistrationSuccessDialog from "./RegistrationSuccessDialog";
import { mountInterfaceLocalisation, toChinese, toSabahMalay, translateInterfaceText } from "./i18n";
import { readInitialRegistrationStep } from "./registration-flow";
import { PRIVACY_POLICY_COPY } from "./privacy-policy-copy";
import { HOW_IT_WORKS_COPY } from "./how-it-works-copy";
import { getProductImage } from "./product-images";
import {
  getProductFrequency,
  productFrequencyOptions,
  type ProductFrequency,
  type ProductFrequencyMap,
} from "./product-frequency";
import BrandSelector from "./BrandSelector";
import BrandImage from "./BrandImage";
import { loadManagedBrandSuggestions, loadPublicNewBrands, submitBrandSuggestion, type ManagedBrandSuggestion, type PublicNewBrand } from "./brand-suggestions";
import { decodeBrandProduct, loadAllBrandsForAdmin, loadBrandSuggestions, loadWishlistBrandCatalogue, normalizeBrandName, popularBrandNames, saveCurrentMemberInterests, uploadBrandImage, type BrandSuggestion } from "./brand-data";
import {
  readGlobalDeploymentState,
  writeGlobalDeploymentState,
  writeOpportunitiesEnabled,
  type DeploymentMode,
  type DeploymentState,
} from "./deployment";

const FeatureAvailabilityContext = createContext<{ opportunitiesEnabled: boolean; setOpportunitiesEnabled: (enabled: boolean) => Promise<void> }>({ opportunitiesEnabled: false, setOpportunitiesEnabled: async () => {} });
const useFeatureAvailability = () => useContext(FeatureAvailabilityContext);
import {
  getAdminAuthState,
  getMemberConfirmationSession,
  addWebAppAdmin,
  archiveMemberProfile,
  loadCurrentMemberProfile,
  loadAdminMemberProfiles,
  loadAuditEvents,
  loadManagedAdminUsers,
  loadPublicCommunityMetrics,
  loadPublicProductDemand,
  registerMember,
  sendAdminPasswordReset,
  sendMemberRegistrationLink,
  setMemberPassword,
  signInAdmin,
  signInMember,
  signInWithGoogle,
  signOutAdmin,
  signOutMember,
  removeWebAppAdmin,
  updateMemberAccountStatus,
  updateMemberPlan,
  updateCurrentMemberMobileContact,
  updateAdminPassword,
  MemberAccountStatusError,
  type AdminAuthState,
  type AdminRole,
  type AuditEvent,
  type LiveMemberProfile,
  type ManagedAdminUser,
  type MemberAccountStatus,
  type MemberPlan,
  type PublicCommunityMetrics,
  type PublicProductDemand,
} from "./live-data";
import {
  getLatestNewsTranslation,
  readAdminLatestNews,
  readPublishedLatestNews,
  writeLatestNews,
  type LatestNews,
  type LatestNewsTranslation,
  type LatestNewsTranslations,
} from "./latest-news";
import {
  readAdminScrollingInfo,
  readPublishedScrollingInfo,
  writeScrollingInfo,
  type ScrollingInfo,
} from "./scrolling-info";
import { supabase } from "./supabase";
import { readLandingHeroImages, removeLandingHeroImage, uploadLandingHeroImage, type LandingHeroImage } from "./landing-hero";
import { readGlobalColorTheme, readGlobalEnabledLanguages, writeGlobalColorTheme, writeGlobalEnabledLanguages } from "./site-appearance";
import { deleteMonthlyWishlist, readMonthlyWishlists, requestDemandMonth, saveMonthlyWishlist, takeRequestedDemandMonth, type MonthlyWishlist } from "./monthly-wishlists";
import { takeRequestedWishlistSection } from "./wishlist-navigation";
import { generateMemberSummaryPdf } from "./member-report";
import { generateManagementReportPdf, type ManagementReportCount, type ManagementReportData } from "./management-report";
import { readAdminDemandShowcase, readWeeklyDemandShowcase, refreshAdminDemandShowcase, setDemandShowcaseAutoUpdate, type DemandShowcase } from "./demand-showcase";

type ScrollingInfoSyncStatus = "loading" | "ready" | "error";
const ScrollingInfoContext = createContext<{
  info: ScrollingInfo | null;
  status: ScrollingInfoSyncStatus;
  setInfo: (info: ScrollingInfo | null) => void;
}>({ info: null, status: "loading", setInfo: () => {} });
const useScrollingInfo = () => useContext(ScrollingInfoContext);

type Route =
  | "/"
  | "/how-it-works"
  | "/privacy"
  | "/terms"
  | "/cookies"
  | "/login"
  | "/join"
  | "/home"
  | "/interests"
  | "/on-demand"
  | "/wishlist/new"
  | "/opportunity"
  | "/verify"
  | "/commit"
  | "/profile"
  | "/profile/privacy"
  | "/profile/help"
  | "/admin/login"
  | "/admin"
  | "/admin/members"
  | "/admin/demand"
  | "/admin/opportunities"
  | "/admin/reports"
  | "/admin/admin-settings"
  | "/admin/admin-settings/users"
  | "/admin/admin-settings/registered-users"
  | "/admin/admin-settings/audit-trail"
  | "/admin/settings"
  | "/admin/settings/opportunities"
  | "/admin/settings/appearance"
  | "/admin/settings/languages"
  | "/admin/settings/landing-images"
  | "/admin/settings/product-brand"
  | "/admin/settings/brand-images"
  | "/admin/settings/latest-news"
  | "/admin/settings/scrolling-info"
  | "/admin/settings/demand-showcase"
  | "/admin/settings/users"
  | "/admin/settings/deployment";

type AdminView = "overview" | "members" | "demand" | "opportunities" | "reports" | "settings" | "admin-settings" | "admin-users" | "registered-users" | "audit-trail" | "deployment";
type SettingsSection = "opportunities" | "appearance" | "languages" | "landing-images" | "product-brand" | "brand-images" | "latest-news" | "scrolling-info" | "demand-showcase" | "users";
type Language = "en" | "ms" | "sabah" | "zh";
type ColorTheme = "sunny" | "sabah";
type DeploymentSyncStatus = "loading" | "ready" | "error";
type LatestNewsSyncStatus = "loading" | "ready" | "error";
type LocationFailure = "unsupported" | "denied" | "timeout" | "unavailable" | "outside-sabah" | null;

const InterfaceLanguageContext = createContext<{ language: Language; enabledLanguages: Language[]; onLanguageChange: (language: Language) => void; setEnabledLanguages: (languages: Language[]) => void }>({
  language: "en",
  enabledLanguages: ["en", "ms", "sabah", "zh"],
  onLanguageChange: () => {},
  setEnabledLanguages: () => {},
});

const latestNewsLanguageOptions: Array<{ id: Language; code: string; name: string }> = [
  { id: "en", code: "EN", name: "English" },
  { id: "ms", code: "BM", name: "Bahasa Malaysia" },
  { id: "sabah", code: "BS", name: "Bahasa Sabah" },
  { id: "zh", code: "中文", name: "Chinese" },
];

function createLatestNewsEditorTranslations(news: LatestNews | null): Record<Language, LatestNewsTranslation> {
  const empty = { title: "", body: "" };
  return {
    en: news?.translations.en ?? (news ? { title: news.title, body: news.body } : empty),
    ms: news?.translations.ms ?? empty,
    sabah: news?.translations.sabah ?? empty,
    zh: news?.translations.zh ?? empty,
  };
}

// Phones use the compact Vista member UI; tablets and larger screens use the
// full desktop app shell. Keep this aligned with styles-mock-three.css.
const COMPACT_MEMBER_LAYOUT_QUERY = "(max-width: 767px)";

const isColorTheme = (value: string | null): value is ColorTheme => value === "sunny" || value === "sabah";
const isLanguage = (value: string | null): value is Language => value === "en" || value === "ms" || value === "sabah" || value === "zh";

const settingsSectionByRoute: Partial<Record<Route, SettingsSection>> = {
  "/admin/settings/opportunities": "opportunities",
  "/admin/settings/appearance": "appearance",
  "/admin/settings/languages": "languages",
  "/admin/settings/landing-images": "landing-images",
  "/admin/settings/product-brand": "product-brand",
  "/admin/settings/brand-images": "brand-images",
  "/admin/settings/latest-news": "latest-news",
  "/admin/settings/scrolling-info": "scrolling-info",
  "/admin/settings/demand-showcase": "demand-showcase",
  "/admin/settings/users": "users",
};

const LANDING_COPY = {
  en: {
    languageAria: "Choose language",
    prototypeBadge: "Kitanego",
    navAria: "Main navigation",
    navHow: "How it works",
    navPrivacy: "Privacy",
    exploreDemo: "Login",
    joinFree: "Join now",
    menuOpen: "Open menu",
    menuClose: "Close menu",
    eyebrow: "Purchasing power, together",
    heroLead: "Your everyday choices can become a",
    heroHighlight: "stronger signal.",
    heroLede: "Tell us what you regularly buy. We combine anonymous demand to help open better supplier conversations—without asking you to purchase anything.",
    addSignal: "Add my buying signal",
    exploreMember: "Login",
    trustAria: "Key privacy commitments",
    trustFree: "Free to join",
    trustCard: "No card details",
    trustLocation: "Location only with permission",
    visualAria: "Illustration showing individual buying signals becoming collective demand",
    communityPulse: "Community pulse",
    sampleSignals: "buying signals",
    groceries: "Groceries",
    strongestSignal: "Strongest signal",
    demoArea: "Sabah",
    tickerAria: "How Kitanego works",
    tickerData: "Simple registration",
    tickerDiscount: "Submit product needs",
    tickerPayment: "No payment required",
    tickerPrivate: "Self-managed user details",
    howEyebrow: "One small signal. A clearer picture.",
    howTitle: "A simple journey from routine spending to credible demand.",
    storyOneTitle: "Tell us what matters",
    storyOneBody: "Choose the things you buy regularly, using simple ranges—not exact financial information.",
    storyTwoTitle: "We combine the signal",
    storyTwoBody: "Your choices join anonymous totals by product and area. Suppliers never see a member list.",
    storyThreeTitle: "Opportunities can follow",
    storyThreeBody: "If credible demand exists, the community can explore an offer. You decide whether to take part.",
    pulseEyebrow: "A dashboard worth returning to",
    pulseTitle: "See your signal—and the community forming around it.",
    pulseBody: "Members can update interests, understand what suppliers see, and verify only when a relevant pilot opportunity appears.",
    openDashboard: "Open member dashboard",
    goodMorning: "Good morning",
    signalLive: "Your signal is live.",
    demandPulse: "Demand pulse",
    registered: "REGISTERED",
    profileComplete: "Profile complete",
    benefitsEyebrow: "Tailored to your daily needs",
    benefitsTitle: "The Power of Collective Buying with Kitanego",
    benefitsBody: "Turn your regular purchases into collective buying power. Join our community to signal high demand and unlock exclusive value on the products you already need.",
    benefitsOneTitle: "Deals Tailored to You",
    benefitsOneBody: "Personalized discounts for your household's favorite products.",
    benefitsTwoTitle: "Wholesale Prices Together",
    benefitsTwoBody: "Our big community pools its orders to cut retail costs.",
    benefitsThreeTitle: "Zero obligation",
    benefitsThreeBody: "Submit demands or share opinions freely; you only buy what you want.",
    privacyEyebrow: "Privacy by design",
    privacyVisualAlt: "Hands using Kitanego to choose the groceries category and select rice",
    privacyTitle: "How It Works",
    privacyOneTitle: "Basic details info",
    privacyOneBody: "Verify you're human to keep our insights accurate.",
    privacyTwoTitle: "Supplier supporting documentation",
    privacyTwoBody: "Your feedback as evidence of demand via our primary solution.",
    privacyThreeTitle: "Tell us your monthly needs",
    privacyThreeBody: "We combine everyone's demand to negotiate better prices with suppliers.",
    demandTitle: "Products on demand",
    finalEyebrow: "Shared needs. Fairer prices. For everyone.",
    finalTitle: "Making everyday essentials affordable for everyone, together.",
    joinPrototype: "Let's do it!",
    footerText: "Demand better prices, together.",
    adminPreview: "Admin login",
    footerCompany: "Company",
    footerAbout: "About us",
    footerWho: "Who we are",
    footerHow: "How it works",
    footerSupport: "Support",
    footerContact: "Contact us",
    footerFaq: "FAQ",
    footerLegal: "Legal",
    footerPrivacy: "Privacy policy",
    footerTerms: "Terms of use",
    footerCookies: "Cookie notice",
    footerPrototype: "Access",
    footerDemo: "Login",
    footerCopyright: "© 2026 Kitanego. All rights reserved.",
    footerComingSoon: "This information page is being prepared.",
  },
  ms: {
    languageAria: "Pilih bahasa",
    prototypeBadge: "Kitanego",
    navAria: "Navigasi utama",
    navHow: "Cara ia berfungsi",
    navPrivacy: "Privasi",
    exploreDemo: "Log masuk",
    joinFree: "Daftar sekarang",
    menuOpen: "Buka menu",
    menuClose: "Tutup menu",
    eyebrow: "Kuasa membeli, bersama",
    heroLead: "Pilihan harian anda boleh menjadi",
    heroHighlight: "isyarat yang lebih kuat.",
    heroLede: "Beritahu kami apa yang anda beli secara tetap. Kami menggabungkan permintaan tanpa nama untuk membuka perbualan yang lebih baik dengan pembekal—tanpa meminta anda membeli apa-apa.",
    addSignal: "Tambah isyarat pembelian saya",
    exploreMember: "Log masuk",
    trustAria: "Komitmen privasi utama",
    trustFree: "Percuma untuk disertai",
    trustCard: "Tiada butiran kad",
    trustLocation: "Lokasi hanya dengan kebenaran",
    visualAria: "Ilustrasi isyarat pembelian individu yang membentuk permintaan kolektif",
    communityPulse: "Nadi komuniti",
    sampleSignals: "isyarat pembelian",
    groceries: "Barangan runcit",
    strongestSignal: "Isyarat terkuat",
    demoArea: "Sabah",
    tickerAria: "Cara Kitanego berfungsi",
    tickerData: "Pendaftaran mudah",
    tickerDiscount: "Hantar keperluan produk",
    tickerPayment: "Tiada bayaran diperlukan",
    tickerPrivate: "Butiran pengguna diurus sendiri",
    howEyebrow: "Satu isyarat kecil. Gambaran lebih jelas.",
    howTitle: "Perjalanan ringkas daripada perbelanjaan rutin kepada permintaan yang diyakini.",
    storyOneTitle: "Beritahu kami perkara yang penting",
    storyOneBody: "Pilih barangan yang anda beli secara tetap menggunakan julat mudah—bukan maklumat kewangan yang tepat.",
    storyTwoTitle: "Kami gabungkan isyarat",
    storyTwoBody: "Pilihan anda digabungkan dalam jumlah tanpa nama mengikut produk dan kawasan. Pembekal tidak pernah melihat senarai ahli.",
    storyThreeTitle: "Peluang boleh menyusul",
    storyThreeBody: "Jika terdapat permintaan yang diyakini, komuniti boleh meneroka sesuatu tawaran. Anda tentukan sama ada mahu turut serta.",
    pulseEyebrow: "Papan pemuka yang berguna untuk dikunjungi semula",
    pulseTitle: "Lihat isyarat anda—dan komuniti yang terbentuk di sekelilingnya.",
    pulseBody: "Ahli boleh mengemas kini minat, memahami perkara yang dilihat pembekal dan membuat pengesahan hanya apabila peluang rintis yang berkaitan muncul.",
    openDashboard: "Buka papan pemuka ahli",
    goodMorning: "Selamat pagi",
    signalLive: "Isyarat anda aktif.",
    demandPulse: "Nadi permintaan",
    registered: "BERDAFTAR",
    profileComplete: "Profil lengkap",
    benefitsEyebrow: "Disesuaikan dengan keperluan harian anda",
    benefitsTitle: "Kuasa Pembelian Kolektif bersama Kitanego",
    benefitsBody: "Tukar pembelian tetap anda kepada kuasa pembelian kolektif. Sertai komuniti kami untuk menunjukkan permintaan tinggi dan membuka nilai eksklusif bagi produk yang anda perlukan.",
    benefitsOneTitle: "Tawaran Disesuaikan untuk Anda",
    benefitsOneBody: "Tiada lagi masa terbuang pada diskaun yang tidak berkaitan. Anda mendapat tawaran pilihan untuk produk kegemaran isi rumah anda.",
    benefitsTwoTitle: "Harga Borong Bersama-sama",
    benefitsTwoBody: "Anda mendapat kuasa tawar-menawar kolektif komuniti yang besar untuk menurunkan kos runcit.",
    benefitsThreeTitle: "Kawalan 100%, Sentiasa",
    benefitsThreeBody: "Anda bebas sepenuhnya tanpa langganan, pembaharuan automatik paksa, dan mempunyai keputusan akhir bagi setiap pembelian.",
    privacyEyebrow: "Privasi mengikut reka bentuk",
    privacyVisualAlt: "Tangan menggunakan Kitanego untuk memilih kategori barangan runcit dan produk beras",
    privacyTitle: "Cara Ia Berfungsi",
    privacyOneTitle: "Hanya yang diperlukan",
    privacyOneBody: "Tiada nombor pengenalan, data perbankan atau alamat rumah tepat. Koordinat peranti adalah pilihan dan memerlukan kebenaran.",
    privacyTwoTitle: "Jumlah agregat untuk pembekal",
    privacyTwoBody: "Laporan memaparkan jumlah, bukan nama, e-mel atau nombor telefon.",
    privacyThreeTitle: "Anda kekal mengawal",
    privacyThreeBody: "Betulkan butiran, ubah minat atau minta data dipadamkan.",
    demandTitle: "Produk dalam permintaan",
    finalEyebrow: "Keperluan anda. Harga lebih baik. Untuk semua.",
    finalTitle: "Mari kita lakukan perubahan bersama",
    joinPrototype: "Mulakan",
    footerText: "Peluang untuk mencipta perubahan demi harga produk yang lebih baik bagi menyokong keperluan harian kita.",
    adminPreview: "Log masuk pentadbir",
    footerCompany: "Syarikat",
    footerAbout: "Tentang Kitanego",
    footerWho: "Siapa kami",
    footerHow: "Cara ia berfungsi",
    footerSupport: "Sokongan",
    footerContact: "Hubungi kami",
    footerFaq: "Soalan lazim",
    footerLegal: "Perundangan",
    footerPrivacy: "Dasar privasi",
    footerTerms: "Terma penggunaan",
    footerCookies: "Notis kuki",
    footerPrototype: "Akses",
    footerDemo: "Log masuk",
    footerCopyright: "© 2026 Kitanego. Hak cipta terpelihara.",
    footerComingSoon: "Halaman maklumat ini sedang disediakan.",
  },
} as const;

type LandingCopy = { [Key in keyof typeof LANDING_COPY.ms]: string };

const SABAH_LANDING_COPY: LandingCopy = {
  ...(Object.fromEntries(
    Object.entries(LANDING_COPY.ms).map(([key, value]) => [key, toSabahMalay(value)]),
  ) as unknown as LandingCopy),
  languageAria: "Pilih bahasa bah",
  eyebrow: "Kuasa membeli sama-sama",
  heroLead: "Pilihan harian kamu boleh jadi",
  heroHighlight: "isyarat yang lebih kuat bah.",
  heroLede: "Bagitau kami apa yang kamu selalu beli. Kitaorang gabungkan permintaan tanpa nama supaya senang bincang sama pembekal—kamu nda perlu beli apa-apa.",
  addSignal: "Tambah isyarat belian saya",
  trustFree: "Percuma mau ikut",
  trustCard: "Nda perlu butiran kad",
  trustLocation: "Lokasi kalau kamu kasi izin saja",
  tickerData: "Pendaftaran sinang",
  tickerPayment: "Teda bayaran diperlukan",
  tickerPrivate: "Butiran pengguna diatur sendiri",
  howEyebrow: "Satu isyarat kecil. Gambaran makin jelas.",
  howTitle: "Cara yang senang untuk ubah belanja harian jadi permintaan yang kuat.",
  storyOneTitle: "Bagitau kami apa yang penting",
  storyOneBody: "Pilih barang yang kamu selalu beli pakai julat mudah—nda perlu kasi maklumat kewangan tepat.",
  storyTwoTitle: "Kitaorang gabungkan isyarat",
  storyTwoBody: "Pilihan kamu masuk dalam jumlah tanpa nama ikut produk dan kawasan. Pembekal nda nampak senarai ahli.",
  storyThreeTitle: "Kalau ada peluang, kita jalan",
  storyThreeBody: "Kalau permintaan cukup kuat, komuniti boleh tengok tawaran yang ada. Kamu sendiri pilih mau ikut ka nda.",
  pulseTitle: "Tengok isyarat kamu—sama komuniti yang sedang terbentuk.",
  pulseBody: "Ahli boleh kemas kini minat, faham apa pembekal nampak, dan bikin pengesahan kalau ada peluang yang sesuai.",
  openDashboard: "Buka dashboard ahli",
  signalLive: "Isyarat kamu sudah aktif bah.",
  benefitsTitle: "Gabung Kuasa Membeli dengan Kitanego",
  benefitsBody: "Gabungkan kuasa membeli kamurang kasi jadi kuasa membeli ramai-ramai. Kumpul permintaan supaya dapat buka peluang untuk nego harga yang lebih mantaps.",
  benefitsOneTitle: "Offer yang ngam untuk kamurang",
  benefitsOneBody: "Dapat tawaran pilihan untuk barang favoret keluarga kamurang.",
  benefitsTwoTitle: "Harga borong sama-sama",
  benefitsTwoBody: "Kita kasi combine permintaan supaya kos runcit buli kana kasi turun.",
  benefitsThreeTitle: "Nda wajib beli",
  benefitsThreeBody: "Kasi masuk permintaan atau kongsi pendapat; beli apa yang kamurang mau ja.",
  privacyTitle: "Amaciam ba ni?",
  privacyOneTitle: "Info paling basic saja",
  privacyOneBody: "Mau kasi sah yang kamurang tu wujud, bukan scammer.",
  privacyTwoTitle: "Dokumen sokongan untuk pembekal",
  privacyTwoBody: "Semua info yang kamurang share sama Kitanego diguna sebagai bukti permintaan.",
  privacyThreeTitle: "Kasitau keperluan bulanan kamu",
  privacyThreeBody: "Kita kasi combine semua permintaan untuk runding sama supplier.",
  finalEyebrow: "Keperluan sama. Harga lebih baik. Untuk semua urang.",
  finalTitle: "Sama-sama kita kasi jadi barang harian lagi mampu dibeli.",
  joinFree: "Bah Daftar sekarang!",
  joinPrototype: "Bah kasi jadi!",
  footerText: "Bah mari kita ramai-ramai kasi gabung permintaan untuk harga terbaik.",
};

const ZH_LANDING_COPY = Object.fromEntries(
  Object.entries(LANDING_COPY.en).map(([key, value]) => [key, toChinese(value)]),
) as unknown as LandingCopy;

type MemberState = {
  firstName: string;
  lastName: string;
  email: string;
  avatarUrl: string;
  mobileNumber: string;
  mobileNotificationsConsent: boolean;
  plan?: MemberPlan;
  district: string;
  registrationLocation: RegistrationLocation | null;
  categories: string[];
  products: string[];
  productFrequencies: ProductFrequencyMap;
  brands: string[];
  brandFrequencies: ProductFrequencyMap;
  frequency: string;
  spend: string;
  priceInterest: string;
  registeredAt: string;
  registered: boolean;
  verified: boolean;
  committedQuantity: number;
  ecommerceEnabled?: boolean;
};

type RegistrationLocation = {
  latitude: number;
  longitude: number;
  accuracy: number;
  capturedAt: string;
};

type MemberSetter = Dispatch<SetStateAction<MemberState>>;

type Category = {
  id: string;
  label: string;
  hint: string;
  icon: LucideIcon;
  tone: string;
  image: string;
};

const adminNavItems: Array<{ view: AdminView; label: string; route: Route; icon: LucideIcon; systemAdminOnly?: boolean }> = [
  { view: "overview", label: "Overview", route: "/admin", icon: LayoutDashboard },
  { view: "members", label: "Members", route: "/admin/members", icon: Users },
  { view: "demand", label: "Demand", route: "/admin/demand", icon: PieChart },
  { view: "opportunities", label: "Opportunities", route: "/admin/opportunities", icon: Package },
  { view: "reports", label: "Reports", route: "/admin/reports", icon: BarChart3 },
  { view: "settings", label: "Settings", route: "/admin/settings", icon: Settings2 },
  { view: "admin-settings", label: "Admin Settings", route: "/admin/admin-settings", icon: ShieldCheck, systemAdminOnly: true },
];

const STORAGE_KEY = "kitanego-member-v1";
const LANGUAGE_KEY = "kitanego-language-v1";
const COLOR_THEME_KEY = "kitanego-color-theme-v1";
function readInitialColorTheme(): ColorTheme {
  try {
    const requested = new URLSearchParams(window.location.search).get("theme");
    if (isColorTheme(requested)) return requested;
    const saved = window.localStorage.getItem(COLOR_THEME_KEY);
    return isColorTheme(saved) ? saved : "sunny";
  } catch {
    return "sunny";
  }
}

function readInitialLanguage(): Language {
  try {
    const requested = new URLSearchParams(window.location.search).get("lang");
    if (isLanguage(requested)) return requested;
    const saved = window.localStorage.getItem(LANGUAGE_KEY);
    return isLanguage(saved) ? saved : "en";
  } catch {
    return "en";
  }
}

const initialMember: MemberState = {
  firstName: "",
  lastName: "",
  email: "",
  avatarUrl: "",
  mobileNumber: "",
  mobileNotificationsConsent: false,
  plan: "basic",
  district: "",
  registrationLocation: null,
  categories: [],
  products: [],
  productFrequencies: {},
  brands: [],
  brandFrequencies: {},
  frequency: "",
  spend: "",
  priceInterest: "",
  registeredAt: "",
  registered: false,
  verified: false,
  committedQuantity: 0,
  ecommerceEnabled: false,
};

const categories: Category[] = [
  { id: "groceries", label: "Groceries", hint: "Everyday pantry", icon: ShoppingBag, tone: "mint", image: groceriesCategoryImage },
  { id: "household", label: "Home care", hint: "Cleaning essentials", icon: SprayCan, tone: "sky", image: homeCareCategoryImage },
  { id: "personal", label: "Personal care", hint: "Daily routines", icon: Droplets, tone: "lilac", image: personalCareCategoryImage },
  { id: "baby", label: "Baby products", hint: "Family essentials", icon: Baby, tone: "peach", image: babyProductsCategoryImage },
  { id: "telecom", label: "Mobile & data", hint: "Plans and top-ups", icon: Smartphone, tone: "lime", image: mobileDataCategoryImage },
  { id: "transport", label: "Fuel & transport", hint: "Getting around", icon: Fuel, tone: "sun", image: fuelTransportCategoryImage },
  { id: "dining", label: "Food & dining", hint: "Meals out", icon: Utensils, tone: "coral", image: foodDiningCategoryImage },
  { id: "clothing", label: "Clothing", hint: "Wardrobe basics", icon: Shirt, tone: "rose", image: clothingCategoryImage },
];

const demandProductImages: Record<string, string> = {
  Rice: riceDemandImage,
  "Cooking oil": cookingOilDemandImage,
  "Instant noodles": instantNoodlesDemandImage,
  "Laundry detergent": laundryDetergentDemandImage,
  Coffee: coffeeDemandImage,
  Tissue: tissueDemandImage,
};

const defaultDemandProducts: PublicProductDemand[] = Object.keys(demandProductImages).map((product) => ({ product, memberCount: 0 }));

const frequencies = ["Weekly", "Every two weeks", "Monthly", "Every few months", "Occasionally"];
const spendRanges = ["Under RM50", "RM50–RM100", "RM101–RM250", "RM251–RM500", "Above RM500"];
const priceInterests = ["Definitely interested", "Probably interested", "Maybe if price is better", "Not interested"];
const districts = [
  "Beaufort",
  "Beluran",
  "Kalabakan",
  "Keningau",
  "Kinabatangan",
  "Kota Belud",
  "Kota Kinabalu",
  "Kota Marudu",
  "Kuala Penyu",
  "Kudat",
  "Kunak",
  "Lahad Datu",
  "Membakut",
  "Nabawan",
  "Paitan",
  "Papar",
  "Penampang",
  "Pitas",
  "Putatan",
  "Ranau",
  "Sandakan",
  "Semporna",
  "Sipitang",
  "Sook",
  "Tambunan",
  "Tawau",
  "Telupid",
  "Tenom",
  "Tongod",
  "Tuaran",
];

const SABAH_BOUNDS = { west: 115, east: 119.7, south: 4, north: 7.6 };
const MAP_VIEWBOX = { width: 1000, height: 620, padding: 48 };
const sabahOutline: Array<[number, number]> = [
  [115.18, 6.35], [115.36, 6.92], [115.68, 7.24], [116.27, 7.28], [116.92, 7.08],
  [117.55, 6.72], [118.18, 6.43], [118.8, 6.15], [119.23, 5.65], [119.38, 5.05],
  [119.06, 4.62], [118.6, 4.27], [117.9, 4.08], [117.2, 4.31], [116.67, 4.62],
  [116.2, 4.93], [115.73, 4.93], [115.45, 5.28], [115.46, 5.79], [115.18, 6.35],
];
const sampleRegistrationPoints = [
  { label: "Kota Kinabalu", latitude: 5.9804, longitude: 116.0735, count: 1284 },
  { label: "Sandakan", latitude: 5.8394, longitude: 118.1172, count: 846 },
  { label: "Tawau", latitude: 4.2448, longitude: 117.8912, count: 621 },
  { label: "Keningau", latitude: 5.3378, longitude: 116.1602, count: 388 },
  { label: "Lahad Datu", latitude: 5.02298, longitude: 118.32897, count: 574 },
];

type AdminMemberRow = {
  id: string;
  district: string;
  category: string;
  profile: "Complete" | "Needs attention";
  status: "Verified" | "Registered";
  joined: string;
  location: "Location allowed" | "District only";
  current?: boolean;
};

const sampleAdminMembers: AdminMemberRow[] = [
  { id: "RM-8246", district: "Kota Kinabalu", category: "Groceries", profile: "Complete", status: "Verified", joined: "Today", location: "District only" },
  { id: "RM-8245", district: "Sandakan", category: "Home care", profile: "Complete", status: "Registered", joined: "Today", location: "Location allowed" },
  { id: "RM-8244", district: "Tawau", category: "Mobile & data", profile: "Needs attention", status: "Registered", joined: "Yesterday", location: "District only" },
  { id: "RM-8243", district: "Keningau", category: "Groceries", profile: "Complete", status: "Verified", joined: "Yesterday", location: "Location allowed" },
  { id: "RM-8242", district: "Lahad Datu", category: "Personal care", profile: "Complete", status: "Registered", joined: "14 Oct", location: "District only" },
  { id: "RM-8241", district: "Papar", category: "Baby products", profile: "Complete", status: "Verified", joined: "14 Oct", location: "District only" },
];

const demandCategoryRows = [
  { label: "Groceries", members: 4268, change: "+18.2%", tone: "mint" },
  { label: "Home care", members: 3314, change: "+12.8%", tone: "sky" },
  { label: "Personal care", members: 2740, change: "+9.4%", tone: "lilac" },
  { label: "Mobile & data", members: 2115, change: "+15.1%", tone: "lime" },
  { label: "Transport", members: 1684, change: "+6.7%", tone: "peach" },
];

const momentumProducts = [
  { product: "Rice · 10kg", category: "Groceries", interested: 924, verified: 742, committed: 684, trend: "+21%" },
  { product: "Cooking oil · 5kg", category: "Groceries", interested: 781, verified: 566, committed: 438, trend: "+17%" },
  { product: "Laundry detergent", category: "Home care", interested: 643, verified: 488, committed: 351, trend: "+13%" },
  { product: "Family mobile data", category: "Mobile & data", interested: 578, verified: 426, committed: 312, trend: "+15%" },
];

const adminOpportunities = [
  { id: "rice", title: "Everyday rice · 10kg", category: "Groceries", status: "Live", committed: 684, target: 750, coverage: "12 districts", closes: "30 Nov 2026", tone: "coral" },
  { id: "oil", title: "Cooking oil · 5kg", category: "Groceries", status: "Supplier-ready", committed: 438, target: 600, coverage: "9 districts", closes: "Ready for review", tone: "mint" },
  { id: "data", title: "Family mobile data", category: "Mobile & data", status: "Validating", committed: 312, target: 500, coverage: "7 districts", closes: "Signal building", tone: "sky" },
];

function isWithinSabah(latitude: number, longitude: number) {
  return latitude >= SABAH_BOUNDS.south && latitude <= SABAH_BOUNDS.north && longitude >= SABAH_BOUNDS.west && longitude <= SABAH_BOUNDS.east;
}

function projectSabahCoordinate(latitude: number, longitude: number) {
  const { west, east, south, north } = SABAH_BOUNDS;
  const { width, height, padding } = MAP_VIEWBOX;
  const x = padding + ((longitude - west) / (east - west)) * (width - padding * 2);
  const y = padding + ((north - latitude) / (north - south)) * (height - padding * 2);
  return {
    x: Math.max(padding, Math.min(width - padding, x)),
    y: Math.max(padding, Math.min(height - padding, y)),
  };
}

function useRoute() {
  const getRoute = (): Route => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get("admin-recovery") === "1" || searchParams.get("auth-return") === "admin") return "/admin/login";
    if (searchParams.get("auth-return") === "member") return "/login";
    if (searchParams.get("auth-return") === "registration-password") return "/join";
    const value = window.location.hash.replace(/^#/, "") || "/";
    const allowed: Route[] = ["/", "/how-it-works", "/privacy", "/terms", "/cookies", "/login", "/join", "/home", "/interests", "/on-demand", "/wishlist/new", "/opportunity", "/verify", "/commit", "/profile", "/profile/privacy", "/profile/help", "/admin/login", "/admin", "/admin/members", "/admin/demand", "/admin/opportunities", "/admin/reports", "/admin/admin-settings", "/admin/admin-settings/users", "/admin/admin-settings/registered-users", "/admin/admin-settings/audit-trail", "/admin/settings", "/admin/settings/opportunities", "/admin/settings/appearance", "/admin/settings/languages", "/admin/settings/landing-images", "/admin/settings/product-brand", "/admin/settings/brand-images", "/admin/settings/latest-news", "/admin/settings/scrolling-info", "/admin/settings/demand-showcase", "/admin/settings/users", "/admin/settings/deployment"];
    return allowed.includes(value as Route) ? (value as Route) : "/";
  };

  const [route, setRoute] = useState<Route>(getRoute);

  useEffect(() => {
    const onChange = () => setRoute(getRoute());
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);

  const navigate = (next: Route) => {
    if (window.location.hash === `#${next}`) {
      setRoute(next);
    } else {
      window.location.hash = next;
    }
  };

  return { route, navigate };
}

function App() {
  const { route, navigate } = useRoute();
  const isAdminRoute = route.startsWith("/admin");
  const deploymentPreviewMode = new URLSearchParams(window.location.search).get("deployment-preview");
  const [isCompactMemberLayout, setIsCompactMemberLayout] = useState(() => window.matchMedia(COMPACT_MEMBER_LAYOUT_QUERY).matches);
  const [language, setLanguage] = useState<Language>(readInitialLanguage);
  // Admin Centre is intentionally English-only. Keep the member/public
  // preference intact so returning to those areas restores the chosen language.
  const effectiveInterfaceLanguage: Language = isAdminRoute ? "en" : language;
  const [enabledLanguages, setEnabledLanguages] = useState<Language[]>(["en", "ms", "sabah", "zh"]);
  const [colorTheme, setColorTheme] = useState<ColorTheme>(readInitialColorTheme);
  const [deploymentState, setDeploymentState] = useState<DeploymentState>({ mode: "live", opportunitiesEnabled: false, updatedAt: null });
  const [deploymentSyncStatus, setDeploymentSyncStatus] = useState<DeploymentSyncStatus>("loading");
  const [latestNews, setLatestNews] = useState<LatestNews | null>(null);
  const [latestNewsSyncStatus, setLatestNewsSyncStatus] = useState<LatestNewsSyncStatus>("loading");
  const [scrollingInfo, setScrollingInfo] = useState<ScrollingInfo | null>(null);
  const [scrollingInfoSyncStatus, setScrollingInfoSyncStatus] = useState<ScrollingInfoSyncStatus>("loading");
  const [adminAuth, setAdminAuth] = useState<AdminAuthState>({ session: null, isAdmin: false, role: null });
  const [adminAuthStatus, setAdminAuthStatus] = useState<"loading" | "ready" | "error">("loading");
  const [adminProfiles, setAdminProfiles] = useState<LiveMemberProfile[]>([]);
  const [adminProfilesStatus, setAdminProfilesStatus] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [member, setMember] = useState<MemberState>(() => {
    try {
      const saved = window.localStorage.getItem(STORAGE_KEY);
      if (!saved) return initialMember;
      const parsed = JSON.parse(saved) as Partial<MemberState> & { name?: string };
      const legacyName = parsed.name?.trim() ?? "";
      const [legacyFirstName = "", ...legacyLastNameParts] = legacyName.split(/\s+/).filter(Boolean);
      const { name: _legacyName, ...currentFields } = parsed;
      return {
        ...initialMember,
        ...currentFields,
        firstName: parsed.firstName ?? legacyFirstName,
        lastName: parsed.lastName ?? legacyLastNameParts.join(" "),
      };
    } catch {
      return initialMember;
    }
  });
  const [toast, setToast] = useState("");
  const useVistaLayout = isCompactMemberLayout;

  useEffect(() => {
    const compactMemberLayout = window.matchMedia(COMPACT_MEMBER_LAYOUT_QUERY);
    const updateLayout = () => setIsCompactMemberLayout(compactMemberLayout.matches);
    compactMemberLayout.addEventListener("change", updateLayout);
    return () => compactMemberLayout.removeEventListener("change", updateLayout);
  }, []);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(member));
  }, [member]);

  useEffect(() => {
    let active = true;
    const refreshAdminAccess = async () => {
      try {
        const state = await getAdminAuthState();
        if (!active) return;
        setAdminAuth(state);
        setAdminAuthStatus("ready");
      } catch {
        if (active) setAdminAuthStatus("error");
      }
    };

    void refreshAdminAccess();
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      window.setTimeout(() => {
        void getAdminAuthState(session).then((state) => {
          if (!active) return;
          setAdminAuth(state);
          setAdminAuthStatus("ready");
        }).catch(() => {
          if (active) setAdminAuthStatus("error");
        });
      }, 0);
    });

    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    let active = true;
    const refreshLatestNews = async () => {
      try {
        const next = await readPublishedLatestNews();
        if (!active) return;
        setLatestNews(next);
        setLatestNewsSyncStatus("ready");
      } catch {
        if (active) setLatestNewsSyncStatus("error");
      }
    };
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") void refreshLatestNews();
    };
    void refreshLatestNews();
    const interval = window.setInterval(refreshLatestNews, 30_000);
    window.addEventListener("focus", refreshLatestNews);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      active = false;
      window.clearInterval(interval);
      window.removeEventListener("focus", refreshLatestNews);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, []);

  useEffect(() => {
    let active = true;
    const refreshScrollingInfo = async () => {
      try {
        const next = await readPublishedScrollingInfo();
        if (!active) return;
        setScrollingInfo(next);
        setScrollingInfoSyncStatus("ready");
      } catch {
        if (active) setScrollingInfoSyncStatus("error");
      }
    };
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") void refreshScrollingInfo();
    };
    void refreshScrollingInfo();
    const interval = window.setInterval(refreshScrollingInfo, 30_000);
    window.addEventListener("focus", refreshScrollingInfo);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      active = false;
      window.clearInterval(interval);
      window.removeEventListener("focus", refreshScrollingInfo);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, []);

  useEffect(() => {
    const session = adminAuth.session;
    if (!session || adminAuthStatus !== "ready") return;

    const url = new URL(window.location.href);
    const storedReturnIntent = window.sessionStorage.getItem("kitanego-auth-return");
    const returnIntent = url.searchParams.get("auth-return") ?? (storedReturnIntent === "admin" || storedReturnIntent === "member" ? storedReturnIntent : null);
    // Treat an authenticated visit to /join as a resumable member sign-in too.
    // This covers OAuth returns where the query intent was stripped by an
    // intermediary and users who retry Google while a session already exists.
    const completingMemberLogin = returnIntent === "member" || route === "/login" || route === "/join";
    const metadata = session.user.user_metadata ?? {};
    const fullName = String(metadata.full_name ?? metadata.name ?? "").trim();
    const [derivedFirstName = "", ...derivedLastNameParts] = fullName.split(/\s+/).filter(Boolean);
    const firstName = String(metadata.given_name ?? derivedFirstName).trim();
    const lastName = String(metadata.family_name ?? derivedLastNameParts.join(" ")).trim();
    const email = session.user.email?.trim().toLowerCase() ?? "";
    const avatarUrl = String(metadata.avatar_url ?? metadata.picture ?? "").trim();

    setMember((current) => ({
      ...current,
      firstName: current.firstName || firstName,
      lastName: current.lastName || lastName,
      email: current.email || email,
      avatarUrl: current.avatarUrl || avatarUrl,
    }));

    const clearOAuthReturn = () => {
      const cleaned = new URL(window.location.href);
      cleaned.searchParams.delete("auth-return");
      cleaned.searchParams.delete("code");
      cleaned.searchParams.delete("error");
      cleaned.searchParams.delete("error_code");
      cleaned.searchParams.delete("error_description");
      window.sessionStorage.removeItem("kitanego-auth-return");
      window.history.replaceState(window.history.state, "", `${cleaned.pathname}${cleaned.search}${cleaned.hash}`);
    };

    const continueMemberRegistration = () => {
      const cleaned = new URL(window.location.href);
      cleaned.searchParams.delete("auth-return");
      cleaned.searchParams.delete("code");
      cleaned.searchParams.delete("error");
      cleaned.searchParams.delete("error_code");
      cleaned.searchParams.delete("error_description");
      window.sessionStorage.removeItem("kitanego-auth-return");
      cleaned.searchParams.set("registration-step", "profile");
      cleaned.hash = "/join";
      window.history.replaceState(window.history.state, "", `${cleaned.pathname}${cleaned.search}${cleaned.hash}`);
      navigate("/join");
    };

    if (returnIntent === "admin") {
      clearOAuthReturn();
      if (adminAuth.isAdmin) navigate("/admin");
      return;
    }

    let active = true;
    void loadCurrentMemberProfile().then((profile) => {
      if (!active) return;
      if (profile) {
        setMember((current) => ({
          ...current,
          firstName: profile.firstName,
          lastName: profile.lastName,
          email: profile.email,
          avatarUrl: profile.avatarUrl || current.avatarUrl,
          mobileNumber: profile.mobileNumber,
          mobileNotificationsConsent: profile.mobileNotificationsConsent,
          plan: profile.plan,
          district: profile.district,
          categories: profile.categories,
          products: profile.products,
          brands: profile.brands,
          frequency: profile.frequency,
          spend: profile.spendRange,
          priceInterest: profile.priceInterest,
          registeredAt: profile.createdAt,
          registrationLocation: profile.latitude != null && profile.longitude != null
            ? { latitude: profile.latitude, longitude: profile.longitude, accuracy: profile.accuracy ?? 0, capturedAt: profile.createdAt }
            : null,
          registered: true,
        }));
        if (completingMemberLogin || returnIntent === "registration-password") {
          clearOAuthReturn();
          navigate("/home");
        }
      } else if (completingMemberLogin) {
        continueMemberRegistration();
      }
    }).catch((error: unknown) => {
      if (completingMemberLogin) {
        clearOAuthReturn();
        if (error instanceof MemberAccountStatusError) {
          window.sessionStorage.setItem("kitanego-member-access-error", error.message);
          navigate("/login");
        } else {
          navigate("/join");
        }
      }
    });

    return () => { active = false; };
  }, [adminAuth.session?.user.id, adminAuth.isAdmin, adminAuthStatus, route]);

  useEffect(() => {
    if (!adminAuth.isAdmin) {
      setAdminProfiles([]);
      setAdminProfilesStatus("idle");
      return;
    }

    let active = true;
    const refreshProfiles = () => {
      setAdminProfilesStatus("loading");
      void loadAdminMemberProfiles().then((profiles) => {
        if (!active) return;
        setAdminProfiles(profiles);
        setAdminProfilesStatus("ready");
      }).catch(() => {
        if (active) setAdminProfilesStatus("error");
      });
    };
    refreshProfiles();
    window.addEventListener("kitanego:member-archived", refreshProfiles);
    return () => {
      active = false;
      window.removeEventListener("kitanego:member-archived", refreshProfiles);
    };
  }, [adminAuth.isAdmin]);

  useEffect(() => {
    let active = true;
    const refreshDeploymentState = async () => {
      try {
        const next = await readGlobalDeploymentState();
        if (!active) return;
        setDeploymentState(next);
        setDeploymentSyncStatus("ready");
      } catch {
        if (active) setDeploymentSyncStatus("error");
      }
    };
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") void refreshDeploymentState();
    };
    void refreshDeploymentState();
    const interval = window.setInterval(refreshDeploymentState, 15_000);
    window.addEventListener("focus", refreshDeploymentState);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      active = false;
      window.clearInterval(interval);
      window.removeEventListener("focus", refreshDeploymentState);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(COLOR_THEME_KEY, colorTheme);
    } catch {
      // The selected color theme still applies for this session when storage is unavailable.
    }
    document.querySelector('meta[name="theme-color"]')?.setAttribute("content", colorTheme === "sabah" ? "#1187d2" : "#ffc928");
  }, [colorTheme]);

  useEffect(() => {
    const requestedTheme = new URLSearchParams(window.location.search).get("theme");
    if (isColorTheme(requestedTheme)) return;
    let active = true;
    const refreshColorTheme = async () => {
      try {
        const globalTheme = await readGlobalColorTheme();
        if (active) setColorTheme(globalTheme);
      } catch {
        // Keep the cached/default theme if the global appearance setting is unavailable.
      }
    };
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") void refreshColorTheme();
    };
    void refreshColorTheme();
    const interval = window.setInterval(refreshColorTheme, 30_000);
    window.addEventListener("focus", refreshColorTheme);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      active = false;
      window.clearInterval(interval);
      window.removeEventListener("focus", refreshColorTheme);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, []);

  useEffect(() => {
    let active = true;
    const refreshLanguages = async () => {
      try {
        const available = await readGlobalEnabledLanguages();
        if (active) setEnabledLanguages(available);
      } catch {
        // Keep all languages available when the global preference cannot be read.
      }
    };
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") void refreshLanguages();
    };
    void refreshLanguages();
    const interval = window.setInterval(refreshLanguages, 30_000);
    window.addEventListener("focus", refreshLanguages);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      active = false;
      window.clearInterval(interval);
      window.removeEventListener("focus", refreshLanguages);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    let changed = false;
    if (url.searchParams.has("design")) {
      url.searchParams.delete("design");
      changed = true;
    }
    const requestedTheme = url.searchParams.get("theme");
    if (requestedTheme && !isColorTheme(requestedTheme)) {
      url.searchParams.delete("theme");
      changed = true;
    }
    const requestedLanguage = url.searchParams.get("lang");
    if (requestedLanguage && !isLanguage(requestedLanguage)) {
      url.searchParams.delete("lang");
      changed = true;
    }
    if (changed) window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(LANGUAGE_KEY, language);
    } catch {
      // The selected language still applies for this session when storage is unavailable.
    }
    document.documentElement.lang = effectiveInterfaceLanguage === "zh" ? "zh-CN" : effectiveInterfaceLanguage === "en" ? "en" : "ms";
    document.documentElement.dataset.language = effectiveInterfaceLanguage;
  }, [effectiveInterfaceLanguage, language]);

  useLayoutEffect(() => {
    if (useVistaLayout) return;
    const root = document.querySelector<HTMLElement>(".design-root");
    if (!root) return;
    return mountInterfaceLocalisation(root, effectiveInterfaceLanguage);
  }, [useVistaLayout, effectiveInterfaceLanguage, route]);

  useEffect(() => {
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.scrollTo({ top: 0, behavior: reducedMotion ? "auto" : "smooth" });
    const timer = window.setTimeout(() => {
      const heading = document.querySelector("main h1") as HTMLElement | null;
      heading?.focus({ preventScroll: true });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [route]);

  const notify = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2400);
  };

  const chooseLanguage = (next: Language) => {
    if (!enabledLanguages.includes(next)) return;
    setLanguage(next);
    try {
      const url = new URL(window.location.href);
      if (next !== "en") url.searchParams.set("lang", next);
      else url.searchParams.delete("lang");
      window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
    } catch {
      // URL sharing is optional; the in-session language switch remains functional.
    }
    notify(next === "ms" ? "Bahasa Malaysia dipilih" : next === "sabah" ? "Bahasa Sabah dipilih bah" : next === "zh" ? "已选择中文" : "English selected");
  };

  useEffect(() => {
    if (enabledLanguages.includes(language)) return;
    const fallback = enabledLanguages[0] ?? "en";
    setLanguage(fallback);
    try {
      const url = new URL(window.location.href);
      if (fallback !== "en") url.searchParams.set("lang", fallback);
      else url.searchParams.delete("lang");
      window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
    } catch {
      // The fallback still applies when the URL cannot be updated.
    }
  }, [enabledLanguages, language]);

  const chooseColorTheme = (next: ColorTheme) => {
    setColorTheme(next);
    void writeGlobalColorTheme(next).then((saved) => {
      setColorTheme(saved);
      notify(effectiveInterfaceLanguage === "ms" ? `Tema warna ${saved === "sabah" ? "Sabah" : "Sunny"} dipilih untuk semua peranti` : `${saved === "sabah" ? "Sabah" : "Sunny"} color theme applied to all devices`);
    }).catch(() => {
      notify("Color theme could not be updated globally");
      void readGlobalColorTheme().then(setColorTheme).catch(() => {});
    });
  };

  const openMemberArea = () => navigate(member.registered ? "/home" : "/login");
  const handleMemberSignOut = async () => {
    try {
      await signOutMember();
      setAdminAuth({ session: null, isAdmin: false, role: null });
      setMember({ ...initialMember });
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch {
        // The in-memory sign-out still completes if local storage is unavailable.
      }
      navigate("/");
      notify(language === "ms" ? "Anda telah log keluar" : "You have been signed out");
    } catch {
      notify(language === "ms" ? "Log keluar tidak berjaya. Sila cuba lagi." : "Sign out failed. Please try again.");
      throw new Error("Member sign out failed");
    }
  };

  const adminLastLoginAt = adminAuth.session?.user.last_sign_in_at ?? null;
  const isDeploymentControlRoute = route === "/admin/settings/deployment" && adminAuth.role === "system_admin";
  const isMemberRoute = ["/home", "/interests", "/on-demand", "/wishlist/new", "/opportunity", "/verify", "/commit", "/profile", "/profile/privacy", "/profile/help"].includes(route);
  const vistaRoutes: Route[] = ["/interests", "/on-demand", "/profile", "/profile/privacy", "/profile/help"];
  const useVistaScreen = useVistaLayout && vistaRoutes.includes(route) && (!isMemberRoute || member.registered);
  // The compact Vista UI is intrinsically blue. Apply its matching Sabah
  // palette to user-facing desktop pages so the experience no longer changes
  // to the yellow theme at the responsive breakpoint. Admin keeps its saved
  // theme so appearance settings and previews remain usable.
  const renderedColorTheme: ColorTheme = !useVistaScreen && !isAdminRoute ? "sabah" : colorTheme;
  const updateDeploymentMode = async (mode: DeploymentMode) => {
    const next = await writeGlobalDeploymentState(mode);
    setDeploymentState(next);
    setDeploymentSyncStatus("ready");
  };
  const updateOpportunitiesEnabled = async (enabled: boolean) => {
    const next = await writeOpportunitiesEnabled(enabled);
    setDeploymentState(next);
  };
  const revealFreshAdminLogin = async () => {
    try {
      await signOutAdmin();
    } catch {
      // Still show the login screen if the remote sign-out request is unavailable.
    }
    setAdminAuth({ session: null, isAdmin: false, role: null });
    setAdminAuthStatus("ready");
    navigate("/admin/login");
  };

  useEffect(() => {
    if (deploymentState.opportunitiesEnabled) return;
    if (["/opportunity", "/verify", "/commit"].includes(route)) navigate("/home");
    if (route === "/admin/opportunities") navigate("/admin/settings");
  }, [deploymentState.opportunitiesEnabled, route]);

  let screen: ReactNode;
  if (deploymentPreviewMode === "live") {
    screen = <Landing member={initialMember} isMemberLoggedIn={false} navigate={() => {}} openMemberArea={() => {}} notify={() => {}} language={language} onLanguageChange={() => {}} onSignOut={async () => {}} showLanguageSelector streamlined={false} sunnyVisualOnly />;
  } else if (deploymentPreviewMode === "maintenance") {
    screen = <MaintenancePage language={language} onAdminLoginReveal={async () => {}} />;
  } else if (isAdminRoute && adminAuthStatus === "loading") {
    screen = <DeploymentGatePage />;
  } else if (isAdminRoute && !adminAuth.isAdmin) {
    screen = <AdminLogin
      authUnavailable={adminAuthStatus === "error"}
      signedInEmail={adminAuth.session?.user.email ?? ""}
      onSignedIn={(state) => { setAdminAuth(state); setAdminAuthStatus("ready"); navigate("/admin"); }}
    />;
  } else if (deploymentSyncStatus === "loading" && !isDeploymentControlRoute) {
    screen = <DeploymentGatePage />;
  } else if ((deploymentSyncStatus === "error" || deploymentState.mode === "maintenance") && !isAdminRoute) {
    screen = <MaintenancePage language={language} onAdminLoginReveal={revealFreshAdminLogin} />;
  } else if (route === "/login") {
    screen = <MemberLogin language={language} navigate={navigate} />;
  } else if (useVistaScreen) {
    screen = <MockThree route={route} member={member} setMember={setMember} navigate={navigate} language={language} enabledLanguages={enabledLanguages} onLanguageChange={chooseLanguage} notify={notify} openMemberArea={openMemberArea} onSignOut={handleMemberSignOut} opportunitiesEnabled={deploymentState.opportunitiesEnabled} scrollingMessage={scrollingInfo?.message ?? ""} />;
  } else if (isMemberRoute && !member.registered) {
    screen = <MemberAccessRequired navigate={navigate} />;
  } else switch (route) {
    case "/how-it-works":
      screen = <HowItWorksPage navigate={navigate} />;
      break;
    case "/privacy":
      screen = <PrivacyPolicyPage language={language} navigate={navigate} />;
      break;
    case "/terms":
      screen = <TermsOfUsePage language={language} navigate={navigate} />;
      break;
    case "/cookies":
      screen = <CookieNoticePage language={language} navigate={navigate} />;
      break;
    case "/join":
      screen = <JoinFlow member={member} setMember={setMember} navigate={navigate} notify={notify} requireLocationGate language={language} onSignOut={handleMemberSignOut} />;
      break;
    case "/home":
      screen = <MemberHome member={member} navigate={navigate} isSunny latestNews={latestNews} latestNewsSyncStatus={latestNewsSyncStatus} onSignOut={handleMemberSignOut} />;
      break;
    case "/interests":
      screen = <Interests member={member} setMember={setMember} navigate={navigate} notify={notify} onSignOut={handleMemberSignOut} />;
      break;
    case "/on-demand":
      screen = <OnDemand member={member} navigate={navigate} notify={notify} onSignOut={handleMemberSignOut} />;
      break;
    case "/wishlist/new":
      screen = <MonthlyWishlistFlow member={member} navigate={navigate} notify={notify} onSignOut={handleMemberSignOut} />;
      break;
    case "/opportunity":
      screen = deploymentState.opportunitiesEnabled ? <Opportunity member={member} navigate={navigate} onSignOut={handleMemberSignOut} /> : <MemberHome member={member} navigate={navigate} isSunny latestNews={latestNews} latestNewsSyncStatus={latestNewsSyncStatus} onSignOut={handleMemberSignOut} />;
      break;
    case "/verify":
      screen = deploymentState.opportunitiesEnabled ? <Verify member={member} setMember={setMember} navigate={navigate} notify={notify} /> : <MemberHome member={member} navigate={navigate} isSunny latestNews={latestNews} latestNewsSyncStatus={latestNewsSyncStatus} onSignOut={handleMemberSignOut} />;
      break;
    case "/commit":
      screen = deploymentState.opportunitiesEnabled ? <Commit member={member} setMember={setMember} navigate={navigate} onSignOut={handleMemberSignOut} /> : <MemberHome member={member} navigate={navigate} isSunny latestNews={latestNews} latestNewsSyncStatus={latestNewsSyncStatus} onSignOut={handleMemberSignOut} />;
      break;
    case "/profile":
      screen = <Profile member={member} setMember={setMember} navigate={navigate} notify={notify} onSignOut={handleMemberSignOut} />;
      break;
    case "/profile/privacy":
      screen = <MemberInformationPage kind="privacy" member={member} navigate={navigate} onSignOut={handleMemberSignOut} />;
      break;
    case "/profile/help":
      screen = <MemberInformationPage kind="help" member={member} navigate={navigate} onSignOut={handleMemberSignOut} />;
      break;
    case "/admin/login":
    case "/admin":
      screen = <AdminPreview activeView="overview" member={member} profiles={adminProfiles} profilesStatus={adminProfilesStatus} adminEmail={adminAuth.session?.user.email ?? ""} adminRole={adminAuth.role} adminLastLoginAt={adminLastLoginAt} navigate={navigate} notify={notify} deploymentState={deploymentState} colorTheme={colorTheme} onColorThemeChange={chooseColorTheme} latestNews={latestNews} latestNewsSyncStatus={latestNewsSyncStatus} onLatestNewsChange={(news) => { setLatestNews(news?.published ? news : null); setLatestNewsSyncStatus("ready"); }} onDeploymentModeChange={updateDeploymentMode} onSignOut={async () => { await signOutAdmin(); setAdminAuth({ session: null, isAdmin: false, role: null }); navigate("/admin/login"); }} />;
      break;
    case "/admin/members":
      screen = <AdminPreview activeView="members" member={member} profiles={adminProfiles} profilesStatus={adminProfilesStatus} adminEmail={adminAuth.session?.user.email ?? ""} adminRole={adminAuth.role} adminLastLoginAt={adminLastLoginAt} navigate={navigate} notify={notify} deploymentState={deploymentState} colorTheme={colorTheme} onColorThemeChange={chooseColorTheme} latestNews={latestNews} latestNewsSyncStatus={latestNewsSyncStatus} onLatestNewsChange={(news) => { setLatestNews(news?.published ? news : null); setLatestNewsSyncStatus("ready"); }} onDeploymentModeChange={updateDeploymentMode} onSignOut={async () => { await signOutAdmin(); setAdminAuth({ session: null, isAdmin: false, role: null }); navigate("/admin/login"); }} />;
      break;
    case "/admin/demand":
      screen = <AdminPreview activeView="demand" member={member} profiles={adminProfiles} profilesStatus={adminProfilesStatus} adminEmail={adminAuth.session?.user.email ?? ""} adminRole={adminAuth.role} adminLastLoginAt={adminLastLoginAt} navigate={navigate} notify={notify} deploymentState={deploymentState} colorTheme={colorTheme} onColorThemeChange={chooseColorTheme} latestNews={latestNews} latestNewsSyncStatus={latestNewsSyncStatus} onLatestNewsChange={(news) => { setLatestNews(news?.published ? news : null); setLatestNewsSyncStatus("ready"); }} onDeploymentModeChange={updateDeploymentMode} onSignOut={async () => { await signOutAdmin(); setAdminAuth({ session: null, isAdmin: false, role: null }); navigate("/admin/login"); }} />;
      break;
    case "/admin/opportunities":
      screen = <AdminPreview activeView={deploymentState.opportunitiesEnabled ? "opportunities" : "settings"} member={member} profiles={adminProfiles} profilesStatus={adminProfilesStatus} adminEmail={adminAuth.session?.user.email ?? ""} adminRole={adminAuth.role} adminLastLoginAt={adminLastLoginAt} navigate={navigate} notify={notify} deploymentState={deploymentState} colorTheme={colorTheme} onColorThemeChange={chooseColorTheme} latestNews={latestNews} latestNewsSyncStatus={latestNewsSyncStatus} onLatestNewsChange={(news) => { setLatestNews(news?.published ? news : null); setLatestNewsSyncStatus("ready"); }} onDeploymentModeChange={updateDeploymentMode} onSignOut={async () => { await signOutAdmin(); setAdminAuth({ session: null, isAdmin: false, role: null }); navigate("/admin/login"); }} />;
      break;
    case "/admin/reports":
      screen = <AdminPreview activeView="reports" member={member} profiles={adminProfiles} profilesStatus={adminProfilesStatus} adminEmail={adminAuth.session?.user.email ?? ""} adminRole={adminAuth.role} adminLastLoginAt={adminLastLoginAt} navigate={navigate} notify={notify} deploymentState={deploymentState} colorTheme={colorTheme} onColorThemeChange={chooseColorTheme} latestNews={latestNews} latestNewsSyncStatus={latestNewsSyncStatus} onLatestNewsChange={(news) => { setLatestNews(news?.published ? news : null); setLatestNewsSyncStatus("ready"); }} onDeploymentModeChange={updateDeploymentMode} onSignOut={async () => { await signOutAdmin(); setAdminAuth({ session: null, isAdmin: false, role: null }); navigate("/admin/login"); }} />;
      break;
    case "/admin/admin-settings":
      screen = <AdminPreview activeView="admin-settings" member={member} profiles={adminProfiles} profilesStatus={adminProfilesStatus} adminEmail={adminAuth.session?.user.email ?? ""} adminRole={adminAuth.role} adminLastLoginAt={adminLastLoginAt} navigate={navigate} notify={notify} deploymentState={deploymentState} colorTheme={colorTheme} onColorThemeChange={chooseColorTheme} latestNews={latestNews} latestNewsSyncStatus={latestNewsSyncStatus} onLatestNewsChange={(news) => { setLatestNews(news?.published ? news : null); setLatestNewsSyncStatus("ready"); }} onDeploymentModeChange={updateDeploymentMode} onSignOut={async () => { await signOutAdmin(); setAdminAuth({ session: null, isAdmin: false, role: null }); navigate("/admin/login"); }} />;
      break;
    case "/admin/admin-settings/users":
      screen = <AdminPreview activeView="admin-users" member={member} profiles={adminProfiles} profilesStatus={adminProfilesStatus} adminEmail={adminAuth.session?.user.email ?? ""} adminRole={adminAuth.role} adminLastLoginAt={adminLastLoginAt} navigate={navigate} notify={notify} deploymentState={deploymentState} colorTheme={colorTheme} onColorThemeChange={chooseColorTheme} latestNews={latestNews} latestNewsSyncStatus={latestNewsSyncStatus} onLatestNewsChange={(news) => { setLatestNews(news?.published ? news : null); setLatestNewsSyncStatus("ready"); }} onDeploymentModeChange={updateDeploymentMode} onSignOut={async () => { await signOutAdmin(); setAdminAuth({ session: null, isAdmin: false, role: null }); navigate("/admin/login"); }} />;
      break;
    case "/admin/admin-settings/registered-users":
      screen = <AdminPreview activeView="registered-users" settingsSection="users" member={member} profiles={adminProfiles} profilesStatus={adminProfilesStatus} adminEmail={adminAuth.session?.user.email ?? ""} adminRole={adminAuth.role} adminLastLoginAt={adminLastLoginAt} navigate={navigate} notify={notify} deploymentState={deploymentState} colorTheme={colorTheme} onColorThemeChange={chooseColorTheme} latestNews={latestNews} latestNewsSyncStatus={latestNewsSyncStatus} onLatestNewsChange={(news) => { setLatestNews(news?.published ? news : null); setLatestNewsSyncStatus("ready"); }} onDeploymentModeChange={updateDeploymentMode} onSignOut={async () => { await signOutAdmin(); setAdminAuth({ session: null, isAdmin: false, role: null }); navigate("/admin/login"); }} />;
      break;
    case "/admin/admin-settings/audit-trail":
      screen = <AdminPreview activeView="audit-trail" member={member} profiles={adminProfiles} profilesStatus={adminProfilesStatus} adminEmail={adminAuth.session?.user.email ?? ""} adminRole={adminAuth.role} adminLastLoginAt={adminLastLoginAt} navigate={navigate} notify={notify} deploymentState={deploymentState} colorTheme={colorTheme} onColorThemeChange={chooseColorTheme} latestNews={latestNews} latestNewsSyncStatus={latestNewsSyncStatus} onLatestNewsChange={(news) => { setLatestNews(news?.published ? news : null); setLatestNewsSyncStatus("ready"); }} onDeploymentModeChange={updateDeploymentMode} onSignOut={async () => { await signOutAdmin(); setAdminAuth({ session: null, isAdmin: false, role: null }); navigate("/admin/login"); }} />;
      break;
    case "/admin/settings":
    case "/admin/settings/opportunities":
    case "/admin/settings/appearance":
    case "/admin/settings/languages":
    case "/admin/settings/landing-images":
    case "/admin/settings/product-brand":
    case "/admin/settings/brand-images":
    case "/admin/settings/latest-news":
    case "/admin/settings/scrolling-info":
    case "/admin/settings/demand-showcase":
    case "/admin/settings/users":
      screen = <AdminPreview activeView="settings" settingsSection={settingsSectionByRoute[route]} member={member} profiles={adminProfiles} profilesStatus={adminProfilesStatus} adminEmail={adminAuth.session?.user.email ?? ""} adminRole={adminAuth.role} adminLastLoginAt={adminLastLoginAt} navigate={navigate} notify={notify} deploymentState={deploymentState} colorTheme={colorTheme} onColorThemeChange={chooseColorTheme} latestNews={latestNews} latestNewsSyncStatus={latestNewsSyncStatus} onLatestNewsChange={(news) => { setLatestNews(news?.published ? news : null); setLatestNewsSyncStatus("ready"); }} onDeploymentModeChange={updateDeploymentMode} onSignOut={async () => { await signOutAdmin(); setAdminAuth({ session: null, isAdmin: false, role: null }); navigate("/admin/login"); }} />;
      break;
    case "/admin/settings/deployment":
      screen = <AdminPreview activeView="deployment" member={member} profiles={adminProfiles} profilesStatus={adminProfilesStatus} adminEmail={adminAuth.session?.user.email ?? ""} adminRole={adminAuth.role} adminLastLoginAt={adminLastLoginAt} navigate={navigate} notify={notify} deploymentState={deploymentState} colorTheme={colorTheme} onColorThemeChange={chooseColorTheme} latestNews={latestNews} latestNewsSyncStatus={latestNewsSyncStatus} onLatestNewsChange={(news) => { setLatestNews(news?.published ? news : null); setLatestNewsSyncStatus("ready"); }} deploymentSyncStatus={deploymentSyncStatus} onDeploymentModeChange={updateDeploymentMode} onSignOut={async () => { await signOutAdmin(); setAdminAuth({ session: null, isAdmin: false, role: null }); navigate("/admin/login"); }} />;
      break;
    default:
      screen = <Landing member={member} isMemberLoggedIn={member.registered} navigate={navigate} openMemberArea={openMemberArea} notify={notify} language={language} onLanguageChange={chooseLanguage} onSignOut={handleMemberSignOut} showLanguageSelector streamlined={false} sunnyVisualOnly />;
  }

  return (
    <InterfaceLanguageContext.Provider value={{ language: effectiveInterfaceLanguage, enabledLanguages, onLanguageChange: chooseLanguage, setEnabledLanguages }}><FeatureAvailabilityContext.Provider value={{ opportunitiesEnabled: deploymentState.opportunitiesEnabled, setOpportunitiesEnabled: updateOpportunitiesEnabled }}><ScrollingInfoContext.Provider value={{ info: scrollingInfo, status: scrollingInfoSyncStatus, setInfo: setScrollingInfo }}><div className="design-root" data-design={useVistaScreen ? "sabah-vista" : "sunny-signal"} data-design-key="sunny-signal" data-color-theme={renderedColorTheme}>
      {screen}
      <div className={`toast ${toast ? "toast--visible" : ""}`} aria-live="polite">
        <CheckCircle2 size={18} /> {toast}
      </div>
    </div></ScrollingInfoContext.Provider></FeatureAvailabilityContext.Provider></InterfaceLanguageContext.Provider>
  );
}

type Navigate = (route: Route) => void;

function GoogleIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path fill="#4285F4" d="M21.6 12.23c0-.71-.06-1.4-.18-2.07H12v3.92h5.38a4.6 4.6 0 0 1-2 3.02v2.54h3.24c1.9-1.75 2.98-4.33 2.98-7.41Z" /><path fill="#34A853" d="M12 22c2.7 0 4.97-.9 6.62-2.36l-3.24-2.54c-.9.6-2.05.96-3.38.96-2.6 0-4.81-1.76-5.6-4.13H3.05v2.62A10 10 0 0 0 12 22Z" /><path fill="#FBBC05" d="M6.4 13.93A6.02 6.02 0 0 1 6.09 12c0-.67.11-1.32.31-1.93V7.45H3.05A10 10 0 0 0 2 12c0 1.61.38 3.14 1.05 4.55l3.35-2.62Z" /><path fill="#EA4335" d="M12 5.94c1.47 0 2.79.5 3.83 1.5l2.87-2.87A9.62 9.62 0 0 0 12 2a10 10 0 0 0-8.95 5.45l3.35 2.62c.79-2.37 3-4.13 5.6-4.13Z" /></svg>;
}

function AdminLogin({ authUnavailable, signedInEmail, onSignedIn }: { authUnavailable: boolean; signedInEmail: string; onSignedIn: (state: AdminAuthState) => void }) {
  const [mode, setMode] = useState<"login" | "reset" | "password">(() => new URLSearchParams(window.location.search).get("admin-recovery") === "1" ? "password" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const continueWithGoogle = async () => {
    setError("");
    setMessage("");
    setBusy(true);
    try {
      await signInWithGoogle("admin");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Google sign-in could not be started.");
      setBusy(false);
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    setMessage("");
    setBusy(true);
    try {
      if (mode === "reset") {
        await sendAdminPasswordReset(email.trim());
        setMessage("If this email has an admin account, a secure reset link has been sent.");
        return;
      }
      if (mode === "password") {
        if (password.length < 10) throw new Error("Use at least 10 characters for your password.");
        if (password !== confirmation) throw new Error("The passwords do not match.");
        await updateAdminPassword(password);
        const state = await getAdminAuthState();
        if (!state.isAdmin) throw new Error("This account has not been granted administrator access.");
        onSignedIn(state);
        return;
      }
      const state = await signInAdmin(email.trim(), password);
      onSignedIn(state);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to complete this request.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="admin-login-page">
      <section className="admin-login-panel">
        <Logo />
        <h1>{mode === "login" ? "Sign in" : mode === "reset" ? "Reset your password" : "Create your admin password"}</h1>
        {mode !== "login" && <p>{mode === "reset" ? "We’ll email a secure password-reset link to the address on your account." : "Choose a strong password to finish setting up your administrator access."}</p>}
        {authUnavailable && <div className="admin-login-alert" role="alert"><Wrench size={18} /><span>The authentication service is not ready. Complete the service setup, then try again.</span></div>}
        {mode === "login" && signedInEmail && <div className="admin-login-alert" role="alert"><ShieldCheck size={18} /><span><strong>{signedInEmail}</strong> is signed in but does not have administrator access. Choose the authorised Google account.</span></div>}
        {mode === "login" && <>
          <button type="button" className="google-auth-button" disabled={busy || authUnavailable} onClick={() => { void continueWithGoogle(); }}><GoogleIcon /> Continue with Google</button>
          <div className="auth-divider"><span>or use email and password</span></div>
        </>}
        <form onSubmit={submit} className="admin-login-form">
          {mode !== "password" && <label>Email address<input type="email" required autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} /></label>}
          {mode !== "reset" && <label>Password<input type="password" required minLength={10} autoComplete={mode === "login" ? "current-password" : "new-password"} value={password} onChange={(event) => setPassword(event.target.value)} /></label>}
          {mode === "password" && <label>Confirm password<input type="password" required minLength={10} autoComplete="new-password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} /></label>}
          {error && <p className="admin-login-error" role="alert">{error}</p>}
          {message && <p className="admin-login-success" role="status">{message}</p>}
          <button type="submit" className="button button--ink button--full button--large" disabled={busy || authUnavailable}>{busy ? "Please wait…" : mode === "login" ? "Sign in" : mode === "reset" ? "Send reset link" : "Save password"} <LogIn size={18} /></button>
        </form>
        <div className="admin-login-links">
          {mode === "login" ? <button type="button" onClick={() => { setMode("reset"); setError(""); setMessage(""); }}>Forgot password?</button> : <button type="button" onClick={() => { setMode("login"); setError(""); setMessage(""); }}>Back to sign in</button>}
          <button type="button" onClick={() => { window.location.hash = "/"; }}>Landing page</button>
        </div>
      </section>
    </main>
  );
}

function MemberLogin({ language, navigate }: { language: Language; navigate: Navigate }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(() => {
    const accessError = window.sessionStorage.getItem("kitanego-member-access-error") ?? "";
    window.sessionStorage.removeItem("kitanego-member-access-error");
    return accessError;
  });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const isMalay = language === "ms";

  const continueWithGoogle = async () => {
    setBusy(true);
    setError("");
    try {
      await signInWithGoogle("member");
    } catch {
      setError(isMalay ? "Log masuk Google tidak dapat dimulakan. Sila cuba lagi." : "Google login could not be started. Please try again.");
      setBusy(false);
    }
  };

  const continueWithEmail = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await signInMember(email.trim().toLowerCase(), password);
    } catch (signInError) {
      setError(signInError instanceof MemberAccountStatusError ? signInError.message : isMalay ? "E-mel atau kata laluan tidak sah. Sila cuba lagi." : "Invalid email or password. Please try again.");
      setBusy(false);
    }
  };

  return (
    <main className="admin-login-page member-login-page">
      <header className="member-login-header"><Logo wordmark /><StandardMobileMenu navigate={navigate} /></header>
      <section className="admin-login-panel">
        <img className="member-login-logo" src={kitanegoLogo} alt="Kitanego" />
        <h1>{isMalay ? "Log masuk" : "Sign in"}</h1>
        <p>{isMalay ? "dengan Akaun Google anda untuk meneruskan ke Kitanego." : "with your Google Account to continue to Kitanego."}</p>
        <button type="button" className="google-auth-button" disabled={busy} onClick={() => { void continueWithGoogle(); }}><GoogleIcon /> {busy ? (isMalay ? "Menyambung…" : "Connecting…") : (isMalay ? "Teruskan dengan Google" : "Continue with Google")}</button>
        <div className="auth-divider"><span>{isMalay ? "atau gunakan e-mel dan kata laluan" : "or use email and password"}</span></div>
        <form className="admin-login-form member-login-form" onSubmit={continueWithEmail}>
          <label>{isMalay ? "Alamat e-mel" : "Email address"}<input type="email" required autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} /></label>
          <label>{isMalay ? "Kata laluan" : "Password"}<input type="password" required autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} /></label>
          {error && <p className="admin-login-error" role="alert">{error}</p>}
          <button type="submit" className="button button--ink button--full button--large" disabled={busy}>{busy ? (isMalay ? "Sila tunggu…" : "Please wait…") : (isMalay ? "Log masuk" : "Sign in")} <LogIn size={18} /></button>
        </form>
        <div className="admin-login-links member-login-links">
          <button type="button" className="member-login-home" aria-label={isMalay ? "Pergi ke halaman utama" : "Go to Landing page"} title={isMalay ? "Pergi ke halaman utama" : "Go to Landing page"} onClick={() => navigate("/")}><Home aria-hidden="true" /></button>
        </div>
      </section>
    </main>
  );
}

function MemberAccessRequired({ navigate }: { navigate: Navigate }) {
  return (
    <main className="member-access-page">
      <header className="member-access-header"><Logo wordmark /><StandardMobileMenu navigate={navigate} /></header>
      <section>
        <Logo />
        <span><LockKeyhole size={20} /></span>
        <h1>Create your Kitanego profile first.</h1>
        <p>Your member pages become available after you submit a buying signal.</p>
        <button type="button" className="button button--ink button--large" onClick={() => navigate("/join")}>Get started <ArrowRight size={18} /></button>
        <button type="button" className="text-button" onClick={() => navigate("/")}>Return to home</button>
      </section>
    </main>
  );
}

function DeploymentGatePage() {
  return (
    <main className="maintenance-page deployment-gate-page" aria-busy="true" aria-label="Checking site availability">
      <div className="maintenance-page__glow maintenance-page__glow--one" />
      <div className="maintenance-page__glow maintenance-page__glow--two" />
      <section className="maintenance-card">
        <header className="maintenance-card__header"><img src={kitanegoLogo} alt="Kitanego" /></header>
        <div className="maintenance-icon" aria-hidden="true"><Globe2 /></div>
        <p className="eyebrow">CONNECTING TO KITANEGO</p>
        <h1>Checking site availability…</h1>
        <p className="maintenance-message">This will only take a moment.</p>
      </section>
    </main>
  );
}

function MaintenancePage({ language, onAdminLoginReveal }: { language: Language; onAdminLoginReveal: () => Promise<void> }) {
  const wrenchTapCount = useRef(0);
  const copy = language === "ms"
    ? {
        eyebrow: "Kitanego sedang dikemas kini",
        title: "Kami sedang menyediakan pengalaman yang lebih baik.",
        message: "Laman ini sedang menjalani kemas kini. Sila lawati semula sebentar lagi.",
        description: "Kitanego menggabungkan keperluan produk harian secara tanpa nama supaya permintaan komuniti lebih jelas kepada pembekal.",
      }
    : {
        eyebrow: "Kitanego is being updated",
        title: "We’re preparing a better experience.",
        message: "The site is currently undergoing an update. Please visit again later.",
        description: "Kitanego brings anonymous everyday product needs together so community demand is clearer to suppliers.",
      };

  return (
    <main className="maintenance-page">
      <div className="maintenance-page__glow maintenance-page__glow--one" />
      <div className="maintenance-page__glow maintenance-page__glow--two" />
      <section className="maintenance-card" aria-labelledby="maintenance-title">
        <header className="maintenance-card__header"><img src={kitanegoLogo} alt="Kitanego" /></header>
        <div
          className="maintenance-icon"
          aria-hidden="true"
          onClick={() => {
            wrenchTapCount.current += 1;
            if (wrenchTapCount.current < 10) return;
            wrenchTapCount.current = 0;
            void onAdminLoginReveal();
          }}
        ><Wrench /></div>
        <p className="eyebrow">{copy.eyebrow}</p>
        <h1 id="maintenance-title">{copy.title}</h1>
        <p className="maintenance-message">{copy.message}</p>
        <div className="maintenance-about">
          <p>{copy.description}</p>
        </div>
      </section>
    </main>
  );
}

function PrivacyPolicyPage({ language, navigate }: { language: Language; navigate: Navigate }) {
  const copy = PRIVACY_POLICY_COPY[language];

  return (
    <main className="legal-page">
      <header className="legal-page__header">
        <Logo wordmark />
        <button type="button" className="button button--ink desktop-header-action" onClick={() => navigate("/")}><ArrowLeft size={17} /> {copy.back}</button>
        <StandardMobileMenu navigate={navigate} />
      </header>
      <article className="legal-page__card">
        <p className="eyebrow">{copy.eyebrow}</p>
        <h1>{copy.title}</h1>
        <p className="legal-page__updated">{copy.updated}</p>
        <p>{copy.intro}</p>

        {copy.sections.map((section) => (
          <section key={section.title}>
            <h2>{section.title}</h2>
            {section.paragraphs?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
            {section.bullets && <ul>{section.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}</ul>}
            {section.after?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
          </section>
        ))}
      </article>
    </main>
  );
}

function TermsOfUsePage({ language, navigate }: { language: Language; navigate: Navigate }) {
  const isMalay = language === "ms";

  return (
    <main className="legal-page">
      <header className="legal-page__header">
        <Logo wordmark />
        <button type="button" className="button button--ink desktop-header-action" onClick={() => navigate("/")}><ArrowLeft size={17} /> {isMalay ? "Kembali ke laman utama" : "Back to home"}</button>
        <StandardMobileMenu navigate={navigate} />
      </header>
      <article className="legal-page__card">
        <p className="eyebrow">Terms &amp; conditions</p>
        <h1>Kitanego terms of use</h1>
        <p className="legal-page__updated">Last updated 27 October 2026</p>
        <p>These terms govern your access to and use of the Kitanego website and web app. By creating an account or using the service, you agree to these terms and our privacy policy.</p>

        <section>
          <h2>About Kitanego</h2>
          <p>Kitanego helps members express their everyday product needs so that combined, summarised demand can support supplier discussions. Kitanego may notify members when a pricing opportunity becomes available, but does not guarantee that any product, brand, supplier, price or offer will become available.</p>
        </section>

        <section>
          <h2>Eligibility and your account</h2>
          <p>You must provide accurate, current information and be legally able to agree to these terms. You are responsible for activity under your account and for keeping your sign-in credentials secure. Tell us promptly if you believe your account has been accessed without permission.</p>
        </section>

        <section>
          <h2>Wishlists and demand signals</h2>
          <p>Adding a product, brand, buying frequency, planned amount or opinion to your wishlist expresses interest only. It is not an order, purchase, payment, reservation or binding commitment. You may update your wishlist as your needs change. Any future offer will be presented with its own price, eligibility, quantity, timing and supplier conditions.</p>
        </section>

        <section>
          <h2>Acceptable use</h2>
          <p>You agree not to misuse the service, submit false or misleading information, impersonate another person, interfere with security or availability, attempt unauthorised access, scrape or automate access without permission, distribute malicious code, or use Kitanego in violation of applicable law or another person's rights.</p>
        </section>

        <section>
          <h2>Supplier information and third-party services</h2>
          <p>Product information, supplier terms and pricing may be provided by third parties and can change. Third-party services, including sign-in and hosting providers, operate under their own terms and policies. Kitanego is not responsible for third-party services outside its control.</p>
        </section>

        <section>
          <h2>Intellectual property</h2>
          <p>Kitanego and its licensors retain their rights in the service, branding, software, design and content. These terms give you a limited, personal, non-exclusive and revocable right to use the service for its intended purpose. You may not copy, sell, modify or commercially exploit protected parts of the service without permission.</p>
        </section>

        <section>
          <h2>Service availability and changes</h2>
          <p>We may maintain, improve, suspend or change features of the service. We aim to keep Kitanego available and accurate, but uninterrupted or error-free operation is not guaranteed. We may restrict or end access when reasonably necessary to protect members, comply with law or address a breach of these terms.</p>
        </section>

        <section>
          <h2>Disclaimers and responsibility</h2>
          <p>Kitanego is provided on an “as available” basis to the extent permitted by law. Nothing in these terms excludes rights or remedies that cannot legally be excluded. To the extent permitted by law, Kitanego is not liable for indirect or consequential loss arising from use of, or inability to use, the service.</p>
        </section>

        <section>
          <h2>Governing law</h2>
          <p>These terms are governed by the laws of Malaysia. Any dispute will be handled by the courts with appropriate jurisdiction in Malaysia, unless applicable consumer law requires otherwise.</p>
        </section>

        <section>
          <h2>Changes and contact</h2>
          <p>We may update these terms as Kitanego develops. The date above shows the latest revision, and continued use after an update means the revised terms apply. For questions about these terms, use the Contact us link on the Kitanego homepage.</p>
        </section>
      </article>
    </main>
  );
}

function CookieNoticePage({ language, navigate }: { language: Language; navigate: Navigate }) {
  const isMalay = language === "ms";

  return (
    <main className="legal-page">
      <header className="legal-page__header">
        <Logo wordmark />
        <button type="button" className="button button--ink desktop-header-action" onClick={() => navigate("/")}><ArrowLeft size={17} /> {isMalay ? "Kembali ke laman utama" : "Back to home"}</button>
        <StandardMobileMenu navigate={navigate} />
      </header>
      <article className="legal-page__card">
        <p className="eyebrow">Cookies &amp; storage</p>
        <h1>Kitanego cookie notice</h1>
        <p className="legal-page__updated">Last updated 27 October 2026</p>
        <p>This notice explains how Kitanego uses cookies and similar browser-storage technologies when you visit the website or use the web app.</p>

        <section>
          <h2>What cookies are</h2>
          <p>Cookies are small text files stored on your device by a website. Similar technologies, such as local storage, can remember information in your browser so that a service works consistently between pages and visits.</p>
        </section>

        <section>
          <h2>How Kitanego uses them</h2>
          <p>Kitanego uses cookies or similar storage that are necessary to operate the service, keep sign-in sessions secure, remember basic interface choices and maintain continuity while you complete registration or update a wishlist.</p>
          <ul>
            <li><strong>Authentication:</strong> to keep you signed in and protect access to member or authorised administrator features.</li>
            <li><strong>Security:</strong> to support secure requests, prevent misuse and maintain session integrity.</li>
            <li><strong>Preferences:</strong> to remember settings such as language, display choices or dismissed messages.</li>
            <li><strong>Service continuity:</strong> to preserve appropriate progress and app state as you move through the service.</li>
          </ul>
        </section>

        <section>
          <h2>Third-party services</h2>
          <p>Kitanego uses contracted service providers for identity sign-in, authentication, cloud hosting and data services. These providers may set or access essential cookies when delivering their services and handle them under their own privacy and cookie policies.</p>
        </section>

        <section>
          <h2>Advertising and tracking</h2>
          <p>Kitanego does not use cookies to sell personal information or to deliver third-party behavioural advertising. If optional analytics or other non-essential technologies are introduced, this notice and any required consent controls will be updated before they are used.</p>
        </section>

        <section>
          <h2>Your choices</h2>
          <p>You can review, block or delete cookies through your browser settings. Blocking storage that is essential to authentication or security may prevent sign-in or cause parts of Kitanego to stop working correctly. Signing out ends your active Kitanego session, although some preference storage may remain until you clear it.</p>
        </section>

        <section>
          <h2>Updates and contact</h2>
          <p>We may update this notice when our use of cookies or similar technologies changes. The date above shows the latest revision. If you have questions, use the Contact us link on the Kitanego homepage.</p>
        </section>
      </article>
    </main>
  );
}

function Logo({ inverse = false, wordmark = false, white = false, onClick, ariaLabel = "Kitanego home" }: { inverse?: boolean; wordmark?: boolean; white?: boolean; onClick?: () => void; ariaLabel?: string }) {
  return (
    <button className={`logo ${inverse ? "logo--inverse" : ""} ${wordmark ? "logo--wordmark" : ""}`} onClick={onClick ?? (() => (window.location.hash = "/"))} aria-label={ariaLabel}>
      <img className="logo__image" src={white ? kitanegoLogoWordmarkWhite : wordmark ? kitanegoLogoWordmark : kitanegoLogo} alt="" aria-hidden="true" />
      <span className="logo__wordmark" aria-hidden="true">Kitanego</span>
    </button>
  );
}

function PrototypeBadge({ light = false, label = "Kitanego" }: { light?: boolean; label?: string }) {
  return <span className={`prototype-badge ${light ? "prototype-badge--light" : ""}`}><Sparkles size={13} /> {label}</span>;
}

function LanguageSelector({ value, onChange, ariaLabel }: { value: Language; onChange: (language: Language) => void; ariaLabel: string }) {
  const { enabledLanguages } = useContext(InterfaceLanguageContext);
  const [open, setOpen] = useState(false);
  const selectorRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const languageOptions: Array<{ id: Language; short: string; label: string }> = [
    { id: "en", short: "EN", label: "English" },
    { id: "ms", short: "BM", label: "Bahasa Malaysia" },
    { id: "sabah", short: "BS", label: "Bahasa Sabah" },
    { id: "zh", short: "中文", label: "Chinese" },
  ];
  const availableOptions = languageOptions.filter((option) => enabledLanguages.includes(option.id));
  const selectedOption = availableOptions.find((option) => option.id === value) ?? availableOptions[0];

  useEffect(() => {
    if (!open) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!selectorRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  if (enabledLanguages.length <= 1) return null;
  return (
    <div className={`language-selector ${open ? "is-open" : ""}`} role="group" aria-label={ariaLabel} ref={selectorRef}>
      <button
        type="button"
        className="language-selector__trigger active"
        aria-label={`${ariaLabel}. Current language: ${selectedOption?.label ?? value}`}
        title={selectedOption?.label ?? value}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((current) => !current)}
      >
        <Globe2 size={18} aria-hidden="true" />
      </button>
      {open && <div className="language-selector__menu" id={menuId} role="menu" aria-label={ariaLabel}>
        {availableOptions.filter((option) => option.id !== value).map((option) => (
          <button
            type="button"
            className="language-selector__option"
            role="menuitem"
            key={option.id}
            onClick={() => {
              onChange(option.id);
              setOpen(false);
            }}
          >
            <span className="language-selector__code" aria-hidden="true">{option.short}</span>
            <span>{option.label}</span>
          </button>
        ))}
      </div>}
    </div>
  );
}

function StandardMobileMenu({ navigate, member, authenticated = false, onSignOut, exitLabel = "Home" }: { navigate: Navigate; member?: MemberState; authenticated?: boolean; onSignOut?: () => Promise<void>; exitLabel?: string }) {
  const [open, setOpen] = useState(false);
  const go = (route: Route) => { setOpen(false); navigate(route); };
  return <div className="standard-mobile-menu">
    <button type="button" className="standard-mobile-menu__toggle" aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open} onClick={() => setOpen((current) => !current)}>{open ? <X /> : <Menu />}</button>
    {open && <nav className="standard-mobile-menu__panel" aria-label="Mobile navigation">
      {member || authenticated ? <>
        <button type="button" onClick={() => go("/home")}>Home</button>
        <button type="button" onClick={() => go("/interests")}>Wishlists</button>
        <button type="button" onClick={() => go("/on-demand")}>Demand</button>
        {onSignOut && <button type="button" onClick={() => { setOpen(false); void onSignOut(); }}>Log out</button>}
      </> : <>
        <button type="button" onClick={() => go("/")}>{exitLabel}</button>
        <button type="button" onClick={() => go("/how-it-works")}>How it works</button>
        <button type="button" onClick={() => go("/join")}>Join now</button>
        <button type="button" onClick={() => go("/login")}>Login</button>
      </>}
    </nav>}
  </div>;
}

function HowItWorksPage({ navigate }: { navigate: Navigate }) {
  const { language, onLanguageChange } = useContext(InterfaceLanguageContext);
  const copy = HOW_IT_WORKS_COPY[language];
  const stepAppearance: Array<{ icon: LucideIcon; tone: string }> = [
    { icon: UserRound, tone: "navy" },
    { icon: MapPin, tone: "teal" },
    { icon: ShoppingBag, tone: "blue" },
    { icon: Tag, tone: "green" },
    { icon: CalendarDays, tone: "violet" },
    { icon: PieChart, tone: "orange" },
    { icon: Smile, tone: "pink" },
    { icon: Clock3, tone: "cyan" },
  ];
  const outcomeAppearance = [
    { icon: BarChart3, tone: "cyan" },
    { icon: HeartHandshake, tone: "lime" },
    { icon: TrendingUp, tone: "sun" },
    { icon: Bell, tone: "pink" },
  ];

  return (
    <div className="how-page">
      <header className="how-page__header">
        <Logo wordmark onClick={() => navigate("/")} />
        <div className="how-page__header-actions">
          <LanguageSelector value={language} onChange={onLanguageChange} ariaLabel={copy.languageAria} />
          <button type="button" className="button button--ghost desktop-header-action" onClick={() => navigate("/")}><ArrowLeft size={17} /> {copy.back}</button>
          <StandardMobileMenu navigate={navigate} exitLabel={copy.back} />
        </div>
      </header>

      <main>
        <section className="how-hero">
          <h1>{copy.heroTitle}</h1>
          <p>{copy.heroBody}</p>
          <button type="button" className="button button--lime button--large" onClick={() => document.getElementById("member-flow-title")?.scrollIntoView({ behavior: "smooth" })}>{copy.heroAction} <ArrowRight size={18} /></button>
        </section>

        <section className="how-member-flow" aria-labelledby="member-flow-title">
          <div className="how-section-heading">
            <span>{copy.memberEyebrow}</span>
            <h2 id="member-flow-title">{copy.memberTitle}</h2>
            <p>{copy.memberBody}</p>
          </div>
          <ol className="how-step-grid">
            {copy.steps.map(({ title, description }, index) => {
              const { icon: StepIcon, tone } = stepAppearance[index];
              return (
              <li className={`how-step how-step--${tone}`} key={title}>
                <span className="how-step__number">{String(index + 1).padStart(2, "0")}</span>
                <div className="how-step__icon"><StepIcon aria-hidden="true" /></div>
                <h3>{title}</h3>
                <p>{description}</p>
                {index < copy.steps.length - 1 && <ArrowRight className="how-step__arrow" aria-hidden="true" />}
              </li>
              );
            })}
          </ol>
        </section>

        <section className="how-collective-flow" aria-labelledby="collective-flow-title">
          <div className="how-section-heading how-section-heading--light">
            <span>{copy.collectiveEyebrow}</span>
            <h2 id="collective-flow-title">{copy.collectiveTitle}</h2>
            <p>{copy.collectiveBody}</p>
          </div>
          <div className="how-outcome-flow">
            {copy.outcomes.map(({ label, title, description }, index) => {
              const { icon: OutcomeIcon, tone } = outcomeAppearance[index];
              return <Fragment key={title}>
                <article>
                  <div className={`how-outcome-icon how-outcome-icon--${tone}`}><OutcomeIcon /></div>
                  <span>{label}</span>
                  <h3>{title}</h3>
                  <p>{description}</p>
                </article>
                {index < copy.outcomes.length - 1 && <div className="how-flow-connector"><ArrowRight /></div>}
              </Fragment>;
            })}
          </div>
        </section>

        <section className="how-final">
          <div><span>{copy.finalEyebrow}</span><h2>{copy.finalTitle}</h2><p>{copy.finalBody}</p></div>
          <button type="button" className="button button--lime button--large" onClick={() => navigate("/join")}>{copy.join} <ArrowRight size={18} /></button>
        </section>
      </main>

      <footer className="how-page__footer">
        <Logo inverse wordmark white onClick={() => navigate("/")} />
        <p>{copy.footer}</p>
      </footer>
    </div>
  );
}

function Landing({ member, isMemberLoggedIn, navigate, openMemberArea, language, onLanguageChange, onSignOut, showLanguageSelector, streamlined, sunnyVisualOnly }: { member: MemberState; isMemberLoggedIn: boolean; navigate: Navigate; openMemberArea: () => void; notify: (message: string) => void; language: Language; onLanguageChange: (language: Language) => void; onSignOut: () => Promise<void>; showLanguageSelector: boolean; streamlined: boolean; sunnyVisualOnly: boolean }) {
  const [heroImages, setHeroImages] = useState<LandingHeroImage[]>([]);
  const [activeHeroImage, setActiveHeroImage] = useState(0);
  const [benefitSlide, setBenefitSlide] = useState(0);
  const [privacySlide, setPrivacySlide] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);
  const [demandShowcase, setDemandShowcase] = useState<DemandShowcase | null>(null);
  const footerLogoTapCount = useRef(0);
  const footerLogoResetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const copy = language === "sabah" ? SABAH_LANDING_COPY : language === "zh" ? ZH_LANDING_COPY : LANDING_COPY[language];
  const tickerItems = [copy.tickerData, copy.tickerDiscount, copy.tickerPayment, copy.tickerPrivate];
  const privacyItems = [
    { icon: ShieldCheck, title: copy.privacyOneTitle, body: copy.privacyOneBody },
    { icon: CircleHelp, title: copy.privacyThreeTitle, body: copy.privacyThreeBody },
    { icon: Users, title: copy.privacyTwoTitle, body: copy.privacyTwoBody },
  ];
  const handleFooterLogoTap = () => {
    footerLogoTapCount.current += 1;
    if (footerLogoResetTimer.current) window.clearTimeout(footerLogoResetTimer.current);

    if (footerLogoTapCount.current >= 10) {
      footerLogoTapCount.current = 0;
      navigate("/admin/login");
      return;
    }

    footerLogoResetTimer.current = window.setTimeout(() => {
      footerLogoTapCount.current = 0;
      footerLogoResetTimer.current = null;
    }, 5000);
  };

  useEffect(() => () => {
    if (footerLogoResetTimer.current) window.clearTimeout(footerLogoResetTimer.current);
  }, []);

  useEffect(() => {
    const mobileViewport = window.matchMedia("(max-width: 650px)");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let timer: number | undefined;

    const updateAutoplay = () => {
      if (timer) window.clearInterval(timer);
      timer = undefined;
      if (!mobileViewport.matches || reducedMotion.matches) return;
      timer = window.setInterval(() => {
        setBenefitSlide((current) => (current + 1) % 3);
      }, 4500);
    };

    updateAutoplay();
    mobileViewport.addEventListener("change", updateAutoplay);
    reducedMotion.addEventListener("change", updateAutoplay);

    return () => {
      if (timer) window.clearInterval(timer);
      mobileViewport.removeEventListener("change", updateAutoplay);
      reducedMotion.removeEventListener("change", updateAutoplay);
    };
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setPrivacySlide((current) => (current + 1) % privacyItems.length);
    }, 4500);

    return () => window.clearInterval(timer);
  }, [privacyItems.length]);

  const featuredDemand = useMemo(() => {
    return demandShowcase?.products.length ? demandShowcase.products : defaultDemandProducts;
  }, [demandShowcase]);
  useEffect(() => {
    let active = true;
    void readWeeklyDemandShowcase().then((showcase) => {
      if (active) setDemandShowcase(showcase);
    }).catch(() => {
      if (active) setDemandShowcase(null);
    });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    let active = true;
    void readLandingHeroImages().then((images) => {
      if (active) setHeroImages(images);
    }).catch(() => {
      if (active) setHeroImages([]);
    });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    setActiveHeroImage(0);
    if (heroImages.length < 2) return;
    const timer = window.setInterval(() => setActiveHeroImage((index) => (index + 1) % heroImages.length), 6000);
    return () => window.clearInterval(timer);
  }, [heroImages]);

  return (
    <div className={`landing ${sunnyVisualOnly ? "landing--visual-first" : ""}`}>
      <header className="site-header">
        <Logo wordmark />
        <nav className="site-nav" aria-label={copy.navAria}>
          {!sunnyVisualOnly && <button type="button" className="text-button" onClick={() => navigate("/how-it-works")}>{copy.navHow}</button>}
          {showLanguageSelector && <LanguageSelector value={language} onChange={onLanguageChange} ariaLabel={copy.languageAria} />}
          {!isMemberLoggedIn && <button className="text-button" onClick={openMemberArea}>{copy.exploreDemo}</button>}
          {isMemberLoggedIn
            ? <MemberAccountMenu member={member} navigate={navigate} onSignOut={onSignOut} landingTrigger />
            : <button className="button button--small button--ink" onClick={() => navigate("/join")}>{copy.joinFree} <ArrowRight size={16} /></button>}
        </nav>
        {isMemberLoggedIn
          ? <div className="mobile-member-account">{showLanguageSelector && <LanguageSelector value={language} onChange={onLanguageChange} ariaLabel={copy.languageAria} />}<MemberAccountMenu member={member} navigate={navigate} onSignOut={onSignOut} landingTrigger /></div>
          : <div className="mobile-public-actions">{showLanguageSelector && <LanguageSelector value={language} onChange={onLanguageChange} ariaLabel={copy.languageAria} />}<button className="menu-button" onClick={() => setMenuOpen(!menuOpen)} aria-label={menuOpen ? copy.menuClose : copy.menuOpen} aria-expanded={menuOpen}>
              {menuOpen ? <X /> : <Menu />}
            </button></div>}
        {!isMemberLoggedIn && menuOpen && (
          <div className="mobile-menu">
            {!sunnyVisualOnly && <button type="button" onClick={() => { setMenuOpen(false); navigate("/how-it-works"); }}>{copy.navHow}</button>}
            <button onClick={openMemberArea}>{copy.exploreDemo}</button>
            <button className="button button--ink" onClick={() => navigate("/join")}>{copy.joinFree}</button>
          </div>
        )}
      </header>

      <main>
        {sunnyVisualOnly && <h1 className="sr-only">Kitanego</h1>}
        <section className="hero" id="about">
          {!sunnyVisualOnly && <div className="hero__copy">
            {!streamlined && <PrototypeBadge label={copy.prototypeBadge} />}
            <p className="eyebrow">{copy.eyebrow}</p>
            <h1 tabIndex={-1}>{copy.heroLead} <em>{copy.heroHighlight}</em></h1>
            {!streamlined && <p className="hero__lede">{copy.heroLede}</p>}
            <div className="hero__actions">
              <button className="button button--lime button--large" onClick={() => navigate("/join")}>{copy.addSignal} <ArrowRight size={19} /></button>
              <button className="button button--ghost button--large" onClick={openMemberArea}>{copy.exploreMember}</button>
            </div>
            <div className="trust-row" aria-label={copy.trustAria}>
              <span><Check size={15} /> {copy.trustFree}</span>
              <span><Check size={15} /> {copy.trustCard}</span>
              <span><Check size={15} /> {copy.trustLocation}</span>
            </div>
          </div>}

          <div className={`hero__visual${heroImages.length ? " hero__visual--slideshow" : ""}`} aria-label={copy.visualAria}>
            {heroImages.length > 0 && <div className="hero-slideshow" aria-hidden="true">{heroImages.map((image, index) => <img src={image.imageUrl} alt="" className={index === activeHeroImage ? "active" : ""} key={image.id} />)}</div>}
            <div className="signal-orbit signal-orbit--outer" />
            <div className="signal-orbit signal-orbit--middle" />
            <div className="signal-orbit signal-orbit--inner" />
            <span className="signal-dot signal-dot--one" />
            <span className="signal-dot signal-dot--two" />
            <span className="signal-dot signal-dot--three" />
            <span className="signal-dot signal-dot--four" />
            <div className="signal-core">
              <span>{copy.communityPulse}</span>
              <strong>Kitanego</strong>
              <small>{copy.sampleSignals}</small>
            </div>
            {!sunnyVisualOnly && <div className="floating-card floating-card--top"><ShoppingBag size={20} /><span><strong>{copy.groceries}</strong>{copy.strongestSignal}</span></div>}
            {!sunnyVisualOnly && <div className="floating-card floating-card--bottom"><MapPin size={20} /><span><strong>Kota Kinabalu</strong>{copy.demoArea}</span></div>}
          </div>
        </section>

        {!streamlined && (
          <section className="ticker" aria-label={copy.tickerAria}>
            <div className="ticker__track">
              {[false, true].map((duplicate) => <div className="ticker__group" aria-hidden={duplicate || undefined} key={duplicate ? "duplicate" : "primary"}>{tickerItems.map((item) => <span className="ticker__item" key={item}><b>{item}</b><i /></span>)}</div>)}
            </div>
          </section>
        )}

        {!sunnyVisualOnly && <section className="how-section" id="how">
          {!streamlined && (
            <div className="section-heading">
              <p className="eyebrow">{copy.howEyebrow}</p>
              <h2>{copy.howTitle}</h2>
            </div>
          )}
          <div className="steps-grid">
            <article className="story-card story-card--dark">
              <span className="story-card__number">01</span>
              <div className="story-icon"><HeartHandshake /></div>
              <h3>{copy.storyOneTitle}</h3>
              {!streamlined && <p>{copy.storyOneBody}</p>}
            </article>
            <article className="story-card story-card--mint">
              <span className="story-card__number">02</span>
              <div className="mini-pulse" aria-hidden="true"><i /><i /><i /><i /><i /></div>
              <h3>{copy.storyTwoTitle}</h3>
              <p>{copy.storyTwoBody}</p>
            </article>
            <article className="story-card story-card--coral">
              <span className="story-card__number">03</span>
              <div className="story-icon"><TrendingUp /></div>
              <h3>{copy.storyThreeTitle}</h3>
              <p>{copy.storyThreeBody}</p>
            </article>
          </div>
        </section>}

        {!sunnyVisualOnly && <section className="pulse-preview" id="community">
          <div className="pulse-preview__copy">
            {!streamlined && <p className="eyebrow">{copy.pulseEyebrow}</p>}
            <h2>{copy.pulseTitle}</h2>
            <p>{copy.pulseBody}</p>
            <button className="button button--ink" onClick={openMemberArea}>{copy.openDashboard} <ArrowRight size={17} /></button>
          </div>
          <div className="dashboard-peek" aria-hidden="true">
            <div className="peek-header"><span /><span /><span /></div>
            <div className="peek-welcome"><small>{copy.goodMorning}</small><strong>{copy.signalLive}</strong></div>
            <div className="peek-grid">
              <div className="peek-card peek-card--chart">
                <span>{copy.demandPulse}</span>
                <svg viewBox="0 0 220 80" role="presentation"><path d="M0 65 C35 62 48 44 76 50 S120 76 145 34 S182 18 220 9" fill="none" stroke="currentColor" strokeWidth="5" strokeLinecap="round" /></svg>
              </div>
              <div className="peek-card peek-card--status"><BadgeCheck /><strong>{copy.registered}</strong><span>{copy.profileComplete}</span></div>
            </div>
          </div>
        </section>}

        <section className="member-benefits" aria-labelledby="member-benefits-title">
          <div className="member-benefits__intro">
            <h2 id="member-benefits-title">{copy.benefitsTitle}</h2>
            <p>{copy.benefitsBody}</p>
            <button type="button" className="button button--lime button--large" onClick={() => navigate("/join")}>{copy.joinFree} <ArrowRight size={18} /></button>
          </div>
          <div className="member-benefits__carousel">
            <div className={`member-benefits__grid member-benefits__grid--slide-${benefitSlide}`}>
              <article><img className="member-benefit-image" src={dealsTailoredBenefitImage} alt="Member reviewing tailored grocery savings" loading="lazy" decoding="async" /><span><strong>{copy.benefitsOneTitle}</strong><p>{copy.benefitsOneBody}</p></span></article>
              <article><img className="member-benefit-image" src={wholesaleTogetherBenefitImage} alt="Community buyers and a supplier agreeing on a shared order" loading="lazy" decoding="async" /><span><strong>{copy.benefitsTwoTitle}</strong><p>{copy.benefitsTwoBody}</p></span></article>
              <article><img className="member-benefit-image" src={controlAlwaysBenefitImage} alt="Member choosing household essentials with full control" loading="lazy" decoding="async" /><span><strong>{copy.benefitsThreeTitle}</strong><p>{copy.benefitsThreeBody}</p></span></article>
            </div>
            <div className="member-benefits__dots" aria-label="Member benefit slides">
              {[copy.benefitsOneTitle, copy.benefitsTwoTitle, copy.benefitsThreeTitle].map((title, index) => (
                <button type="button" className={benefitSlide === index ? "active" : ""} onClick={() => setBenefitSlide(index)} aria-label={`Show ${title}`} aria-pressed={benefitSlide === index} key={title} />
              ))}
            </div>
          </div>
        </section>

        <section className="privacy-section" id="privacy">
          <div className="privacy-copy">
            <h2>{copy.privacyTitle}</h2>
            <div className="privacy-list">
              {privacyItems.map(({ icon: PrivacyIcon, title, body }, index) => (
                <div className={privacySlide === index ? "privacy-card privacy-card--active" : "privacy-card"} key={title}>
                  <PrivacyIcon />
                  <span><strong>{title}</strong>{body}</span>
                </div>
              ))}
            </div>
            <div className="privacy-carousel-controls" aria-label="Feature slides">
              <div className="privacy-carousel-dots">
                {privacyItems.map(({ title }, index) => (
                  <button type="button" className={privacySlide === index ? "active" : ""} onClick={() => setPrivacySlide(index)} aria-label={`Show ${title}`} aria-pressed={privacySlide === index} key={title} />
                ))}
              </div>
            </div>
          </div>
          <div className="privacy-art">
            <img src={kitanegoCategorySelectionPhoto} alt={copy.privacyVisualAlt} loading="lazy" decoding="async" />
          </div>
        </section>

        <section className="demand-showcase" aria-labelledby="demand-showcase-title">
          <div className="demand-showcase__head">
            <div><h3 id="demand-showcase-title">{copy.demandTitle}</h3></div>
          </div>
          <div className="demand-marquee" aria-label={copy.demandTitle}>
            <div className="demand-marquee__track">
              {[false, true].map((duplicate) => (
                <div className="demand-marquee__group" role={duplicate ? undefined : "list"} aria-hidden={duplicate || undefined} key={duplicate ? "duplicate" : "primary"}>
                  {featuredDemand.map(({ product }) => {
                    const localizedProduct = translateInterfaceText(product, language);
                    return (
                      <article className="demand-product-card" role={duplicate ? undefined : "listitem"} key={`${duplicate ? "copy" : "item"}-${product}`}>
                        <img src={getProductImage(product)} alt={duplicate ? "" : localizedProduct} loading="lazy" decoding="async" />
                        <div><strong>{localizedProduct}</strong></div>
                      </article>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="final-cta">
          <div>
            <p className="eyebrow">{copy.finalEyebrow}</p>
            <h2>{copy.finalTitle}</h2>
          </div>
          <button className="button button--lime button--large" onClick={() => navigate("/join")}>{copy.joinPrototype} <ArrowRight size={19} /></button>
        </section>
      </main>

      <footer className="site-footer" id="contact">
        <div className="site-footer__main">
          <div className="site-footer__brand">
            <Logo inverse wordmark white onClick={handleFooterLogoTap} ariaLabel="Kitanego" />
            <p>{copy.footerText}</p>
          </div>
          <nav className="site-footer__links" aria-label={language === "ms" ? "Pautan kaki laman" : "Footer links"}>
            <div><strong>{copy.footerCompany}</strong><button type="button" onClick={() => setAboutOpen(true)}>{copy.footerAbout}</button><button type="button" onClick={() => navigate("/how-it-works")}>{copy.footerHow}</button><button type="button" onClick={() => setContactOpen(true)}>{copy.footerContact}</button></div>
            <div><strong>{copy.footerSupport}</strong><button type="button" onClick={() => setFaqOpen(true)}>{copy.footerFaq}</button><button type="button" onClick={() => navigate("/privacy")}>{copy.footerPrivacy}</button><button type="button" onClick={() => navigate("/terms")}>{copy.footerTerms}</button><button type="button" onClick={() => navigate("/cookies")}>{copy.footerCookies}</button></div>
          </nav>
        </div>
        <div className="site-footer__bottom"><span>{copy.footerCopyright}</span></div>
      </footer>
      {aboutOpen && <AboutUsDialog language={language} onClose={() => setAboutOpen(false)} />}
      {contactOpen && <ContactUsDialog language={language} onClose={() => setContactOpen(false)} />}
      {faqOpen && <FaqDialog language={language} onClose={() => setFaqOpen(false)} />}
    </div>
  );
}

type RegistrationAccountStage = "details" | "email-sent" | "password" | "complete";
type RegistrationConfirmationStatus = "idle" | "checking" | "ready" | "invalid";

function JoinFlow({ member, setMember, navigate, notify, requireLocationGate, language, onSignOut }: { member: MemberState; setMember: MemberSetter; navigate: Navigate; notify: (message: string) => void; requireLocationGate: boolean; language: Language; onSignOut: () => Promise<void> }) {
  const productMap = useProductCatalog();
  const { info: scrollingInfo } = useScrollingInfo();
  const { onLanguageChange } = useContext(InterfaceLanguageContext);
  const tx = (value: string) => translateInterfaceText(value, language);
  const localizedScrollingMessage = scrollingInfo?.message ? tx(scrollingInfo.message) : "";
  const [step, setStep] = useState(readInitialRegistrationStep);
  const [registrationDistrict, setRegistrationDistrict] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const startsProfileRegistration = params.get("auth-return") === "member" || params.get("registration-step") === "profile";
      return startsProfileRegistration ? "" : member.district;
    } catch {
      return member.district;
    }
  });
  const [accountStage, setAccountStage] = useState<RegistrationAccountStage>(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("auth-return") === "registration-password") return "password";
      if (params.get("auth-return") === "member" || params.get("registration-step") === "profile") return "complete";
    } catch {
      // Start with account details when URL state is unavailable.
    }
    return "details";
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [registrationLinkBusy, setRegistrationLinkBusy] = useState(false);
  const [registrationLinkError, setRegistrationLinkError] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [confirmationStatus, setConfirmationStatus] = useState<RegistrationConfirmationStatus>(() => accountStage === "password" ? "checking" : "idle");
  const [registrationSuccessOpen, setRegistrationSuccessOpen] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [search, setSearch] = useState("");
  const [locationStatus, setLocationStatus] = useState<"idle" | "requesting" | "error">("idle");
  const [locationError, setLocationError] = useState("");
  const [locationFailure, setLocationFailure] = useState<LocationFailure>(null);
  const [locationGateReady, setLocationGateReady] = useState(!requireLocationGate);
  const [infoTopic, setInfoTopic] = useState<RegistrationInfoTopic | null>(null);
  const locationRequestId = useRef(0);

  useEffect(() => {
    let active = true;
    void supabase.auth.getSession().then(({ data }) => {
      if (!active || !data.session) return;
      setAccountStage("complete");
      setStep((current) => Math.max(current, 1));
    });
    return () => { active = false; };
  }, []);

  const clearRegistrationCallback = () => {
    const cleaned = new URL(window.location.href);
    ["auth-return", "code", "error", "error_code", "error_description"].forEach((key) => cleaned.searchParams.delete(key));
    cleaned.hash = "/join";
    window.history.replaceState(window.history.state, "", `${cleaned.pathname}${cleaned.search}${cleaned.hash}`);
  };

  const update = (patch: Partial<MemberState>) => setMember((current) => ({ ...current, ...patch }));
  const toggleProduct = (label: string) => {
    const selected = member.products.includes(label);
    const linkedCategory = Object.keys(productMap).find((category) => productMap[category]?.includes(label));
    update({
      categories: !selected && linkedCategory && !member.categories.includes(linkedCategory) ? [...member.categories, linkedCategory] : member.categories,
      products: selected ? member.products.filter((item) => item !== label) : [...member.products, label],
    });
  };
  const continueWithGoogle = async () => {
    setSubmitError("");
    setGoogleBusy(true);
    try {
      await signInWithGoogle("member");
    } catch (caught) {
      setSubmitError(caught instanceof Error ? caught.message : "Google sign-in could not be started.");
      setGoogleBusy(false);
    }
  };

  const captureRegistrationLocation = () => {
    if (!("geolocation" in navigator)) {
      setLocationStatus("error");
      setLocationFailure("unsupported");
      setLocationGateReady(false);
      setLocationError(requireLocationGate
        ? "Open Kitanego in a browser with Location Services enabled."
        : "Location is not available in this browser. You can continue with your district only.");
      return;
    }

    const requestId = ++locationRequestId.current;
    if (requireLocationGate) setLocationGateReady(false);
    setLocationStatus("requesting");
    setLocationError("");
    setLocationFailure(null);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (requestId !== locationRequestId.current) return;
        const latitude = Number(position.coords.latitude.toFixed(6));
        const longitude = Number(position.coords.longitude.toFixed(6));
        if (!isWithinSabah(latitude, longitude)) {
          setLocationStatus("error");
          setLocationFailure("outside-sabah");
          setLocationGateReady(false);
          setLocationError(requireLocationGate
            ? "Registration is currently available only in Sabah. Check your device location, then try again."
            : "This location appears outside Kitanego’s current Sabah service area. Check your district or continue using district only.");
          return;
        }
        update({
          registrationLocation: {
            latitude,
            longitude,
            accuracy: Math.round(position.coords.accuracy),
            capturedAt: new Date(position.timestamp || Date.now()).toISOString(),
          },
        });
        setLocationStatus("idle");
        setLocationFailure(null);
        setLocationGateReady(true);
        notify("Registration location captured");
      },
      (error) => {
        if (requestId !== locationRequestId.current) return;
        const failure: LocationFailure = error.code === error.PERMISSION_DENIED ? "denied" : error.code === error.TIMEOUT ? "timeout" : "unavailable";
        const message = requireLocationGate
          ? failure === "denied"
            ? "App location setting is currently inactive or disabled."
            : "Check that Location Services are on, then try again."
          : failure === "denied"
            ? "Location permission was not granted. You can continue with your district only."
            : failure === "timeout"
              ? "Location took too long. Try again or continue with your district."
              : "Your device could not determine a location. Try again or continue with your district.";
        setLocationStatus("error");
        setLocationFailure(failure);
        setLocationGateReady(false);
        setLocationError(message);
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 },
    );
  };

  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      if (!url.searchParams.has("registration-step")) return;
      url.searchParams.delete("registration-step");
      window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
    } catch {
      // Registration still works when URL cleanup is unavailable.
    }
  }, []);

  useEffect(() => {
    if (accountStage !== "password") return;

    let active = true;
    setConfirmationStatus("checking");
    setPasswordError("");
    void getMemberConfirmationSession().then((session) => {
      if (!active) return;
      if (!session) {
        setConfirmationStatus("invalid");
        return;
      }

      const metadata = session.user.user_metadata ?? {};
      const fullName = String(metadata.full_name ?? metadata.name ?? "").trim();
      const [derivedFirstName = "", ...derivedLastNameParts] = fullName.split(/\s+/).filter(Boolean);
      setMember((current) => ({
        ...current,
        firstName: current.firstName || String(metadata.given_name ?? derivedFirstName).trim(),
        lastName: current.lastName || String(metadata.family_name ?? derivedLastNameParts.join(" ")).trim(),
        email: current.email || session.user.email?.trim().toLowerCase() || "",
      }));
      setConfirmationStatus("ready");
    }).catch(() => {
      if (active) setConfirmationStatus("invalid");
    });

    return () => { active = false; };
  }, [accountStage, setMember]);

  useEffect(() => {
    if (!requireLocationGate) {
      setLocationGateReady(true);
      return;
    }

    if (step === 0 && accountStage !== "complete") {
      setLocationGateReady(false);
      return;
    }

    if (member.registrationLocation && isWithinSabah(member.registrationLocation.latitude, member.registrationLocation.longitude)) {
      setLocationGateReady(true);
      setLocationStatus("idle");
      setLocationError("");
      setLocationFailure(null);
      return;
    }

    setLocationGateReady(false);
    setLocationStatus("idle");
    setLocationError("");
    setLocationFailure(null);
    const requestTimer = window.setTimeout(captureRegistrationLocation, 0);
    return () => {
      window.clearTimeout(requestTimer);
      locationRequestId.current += 1;
    };
  }, [requireLocationGate, step, accountStage]);

  useEffect(() => {
    if (!requireLocationGate || !locationGateReady) return;
    const focusTimer = window.setTimeout(() => {
      const heading = document.querySelector(".onboarding-panel h1") as HTMLElement | null;
      heading?.focus({ preventScroll: true });
    }, 80);
    return () => window.clearTimeout(focusTimer);
  }, [locationGateReady, requireLocationGate]);

  const complete = async () => {
    setIsSubmitting(true);
    setSubmitError("");
    try {
      const registeredEmail = await registerMember({
        firstName: member.firstName.trim(),
        lastName: member.lastName.trim(),
        email: member.email.trim().toLowerCase(),
        avatarUrl: member.avatarUrl,
        district: member.district,
        categories: member.categories,
        products: member.products,
        brands: member.brands,
        frequency: member.frequency,
        spendRange: member.spend,
        priceInterest: member.priceInterest,
        latitude: member.registrationLocation?.latitude ?? null,
        longitude: member.registrationLocation?.longitude ?? null,
        accuracy: member.registrationLocation?.accuracy ?? null,
      });
      const catalogue = await loadWishlistBrandCatalogue(true);
      const catalogueKeys = new Set(catalogue.map(({ name }) => name.toLocaleLowerCase()));
      const pendingBrands = member.brands.filter((name) => !catalogueKeys.has(name.toLocaleLowerCase()));
      await Promise.all(pendingBrands.map((name) => submitBrandSuggestion(name)));
      setMember({ ...member, email: registeredEmail, registeredAt: member.registeredAt || new Date().toISOString(), registered: true });
      navigate("/home");
      notify("Your buying signal is live");
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : "";
      setSubmitError(message.includes("session has expired")
        ? message
        : "We could not save your profile. Your choices are still here—please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasCurrentRegistrationLocation = Boolean(member.registrationLocation && isWithinSabah(member.registrationLocation.latitude, member.registrationLocation.longitude));
  const locationRequirementMet = !requireLocationGate || (locationGateReady && hasCurrentRegistrationLocation);
  const accountSetupActive = step === 0 && accountStage !== "complete";
  const locationGateActive = requireLocationGate && !locationRequirementMet && !accountSetupActive;
  const accountEntryActive = !locationGateActive && step === 0;
  const canContinue = [
    Boolean(member.firstName.trim() && member.lastName.trim() && member.email.includes("@")),
    Boolean(locationRequirementMet && registrationDistrict),
    locationRequirementMet && member.products.length > 0,
    locationRequirementMet && member.brands.length > 0,
    Boolean(locationRequirementMet && member.frequency && member.spend && member.priceInterest),
  ][step];

  const passwordRequirements = [
    { label: "At least 12 characters", met: password.length >= 12 },
    { label: "One uppercase letter", met: /[A-Z]/.test(password) },
    { label: "One lowercase letter", met: /[a-z]/.test(password) },
    { label: "One number", met: /\d/.test(password) },
    { label: "One symbol", met: /[^A-Za-z0-9]/.test(password) },
  ];
  const passwordIsStrong = passwordRequirements.every((requirement) => requirement.met);
  const passwordsMatch = Boolean(passwordConfirmation && password === passwordConfirmation);

  const requestRegistrationLink = async () => {
    setRegistrationLinkBusy(true);
    setRegistrationLinkError("");
    try {
      await sendMemberRegistrationLink(
        member.email.trim().toLowerCase(),
        member.firstName.trim(),
        member.lastName.trim(),
      );
      update({ email: member.email.trim().toLowerCase() });
      setAccountStage("email-sent");
    } catch (caught) {
      setRegistrationLinkError(caught instanceof Error ? caught.message : "The confirmation email could not be sent. Please try again.");
    } finally {
      setRegistrationLinkBusy(false);
    }
  };

  const requestReplacementRegistrationLink = async () => {
    clearRegistrationCallback();
    setPasswordError("");
    if (!member.email.trim() || !member.firstName.trim() || !member.lastName.trim()) {
      setConfirmationStatus("idle");
      setAccountStage("details");
      return;
    }
    setAccountStage("email-sent");
    await requestRegistrationLink();
  };

  const returnToRegistrationDetails = () => {
    clearRegistrationCallback();
    setPasswordError("");
    setConfirmationStatus("idle");
    setAccountStage("details");
  };

  const handleDetails = (event: FormEvent) => {
    event.preventDefault();
    if (canContinue && !registrationLinkBusy) void requestRegistrationLink();
  };

  const handlePasswordSetup = async (event: FormEvent) => {
    event.preventDefault();
    if (!passwordIsStrong || !passwordsMatch || passwordBusy) return;
    setPasswordBusy(true);
    setPasswordError("");
    try {
      await setMemberPassword(password);
      setPassword("");
      setPasswordConfirmation("");
      setRegistrationSuccessOpen(true);
    } catch (caught) {
      setPasswordError(caught instanceof Error ? caught.message : "Your password could not be saved. Please request a new confirmation link.");
    } finally {
      setPasswordBusy(false);
    }
  };

  return (
    <div className={`onboarding-shell registration-flow ${accountEntryActive ? "onboarding-shell--auth" : ""}`}>
      {!accountEntryActive && <header className="app-topbar app-topbar--account-only app-topbar--member">
        <div className="app-topbar__tablet-brand"><Logo wordmark onClick={() => navigate("/home")} /></div>
        {localizedScrollingMessage && <div className="member-scrolling-info" aria-label={`Kitanego update: ${localizedScrollingMessage}`}>
          <div className="member-scrolling-info__viewport" aria-hidden="true"><div className="member-scrolling-info__track"><span>{localizedScrollingMessage}</span><span>{localizedScrollingMessage}</span></div></div>
        </div>}
        <div className="app-topbar__member-actions">
          <LanguageSelector value={language} onChange={onLanguageChange} ariaLabel={tx("Choose interface language")} />
          <MemberAccountMenu member={member} navigate={navigate} onSignOut={onSignOut} />
        </div>
      </header>}
      <main className="onboarding-main">
        <section className="onboarding-panel">
          <div className="mobile-progress"><span style={{ width: `${((step + 1) / 5) * 100}%` }} /></div>
          {locationGateActive ? (
            <div className={`location-gate ${locationStatus === "error" ? "location-gate--error" : ""}`} aria-busy={locationStatus === "requesting"}>
              <span className="location-gate__icon" aria-hidden="true"><MapPin /></span>
              {!(locationStatus === "error" && locationFailure === "denied") && <h1 tabIndex={-1}>{locationStatus === "error"
                ? locationFailure === "outside-sabah"
                  ? "You appear to be outside Sabah."
                  : locationFailure === "unsupported"
                    ? "Location isn’t available here."
                    : "We couldn’t confirm your location."
                : "Allow location to continue."}</h1>}
              <p>Location enable required to complete the registration.</p>
              {locationStatus === "error" ? (
                <>
                  <p className="location-gate__error" role="alert">{locationError}</p>
                  {locationFailure !== "unsupported" && <button type="button" className="button button--ink button--large" onClick={captureRegistrationLocation}>Enable setting <ArrowRight size={18} /></button>}
                </>
              ) : (
                <div className="location-gate__status" role="status" aria-live="polite"><Clock3 aria-hidden="true" /><span><strong>Waiting for permission</strong>Choose Allow in your browser to open registration.</span></div>
              )}
            </div>
          ) : <>
          {step === 0 && accountStage === "details" && (
            <form onSubmit={handleDetails} className="form-stack registration-account-form">
              <div className="form-heading">
                <img className="registration-account-logo" src={kitanegoLogo} alt="" aria-hidden="true" />
                <h1 className="registration-account-heading" tabIndex={-1}>Create a Kitanego Account.</h1>
                <p>Use your Google account.</p>
              </div>
              <button type="button" className="google-auth-button" disabled={googleBusy} onClick={() => { void continueWithGoogle(); }}><GoogleIcon /> {googleBusy ? "Connecting…" : "Continue with Google"}</button>
              <div className="auth-divider"><span>or enter your details</span></div>
              <div className="name-field-grid">
                <label className="field-label">First Name<input required value={member.firstName} onChange={(event) => update({ firstName: event.target.value })} placeholder="e.g. Aina" autoComplete="given-name" /></label>
                <label className="field-label">Last Name<input required value={member.lastName} onChange={(event) => update({ lastName: event.target.value })} placeholder="e.g. Rahman" autoComplete="family-name" /></label>
              </div>
              <label className="field-label">Email address<input required type="email" value={member.email} onChange={(event) => update({ email: event.target.value })} placeholder="you@example.com" autoComplete="email" /></label>
              {registrationLinkError && <p className="field-error" role="alert">{registrationLinkError}</p>}
              <button type="submit" className="button button--ink button--large registration-next-button" disabled={!canContinue || registrationLinkBusy}>{registrationLinkBusy ? "Sending…" : "Next"} <ArrowRight size={18} /></button>
              <nav className="google-account-links" aria-label="Kitanego information">
                <button type="button" onClick={() => setInfoTopic("help")}>Help</button>
                <button type="button" onClick={() => navigate("/privacy")}>Privacy</button>
                <button type="button" onClick={() => navigate("/terms")}>Terms</button>
              </nav>
              <div className="registration-auth-links">
                <button type="button" onClick={() => navigate("/login")}>Already have an account? Sign in</button>
                <button type="button" className="registration-home-link" aria-label="Go to Landing page" title="Go to Landing page" onClick={() => navigate("/")}><Home aria-hidden="true" /></button>
              </div>
            </form>
          )}

          {step === 0 && accountStage === "email-sent" && (
            <div className="registration-verification-state" role="status" aria-live="polite">
              <span className="registration-verification-state__icon"><Mail /></span>
              <p className="step-kicker">Verify your email</p>
              <h1 tabIndex={-1}>Check your email.</h1>
              <p>If this email address exists and can receive mail, Kitanego has sent a confirmation link to:</p>
              <strong className="registration-verification-state__email">{member.email}</strong>
              <p>Open the email and click the confirmation link. You will return to Kitanego to create your password.</p>
              {registrationLinkError && <p className="field-error" role="alert">{registrationLinkError}</p>}
              <button type="button" className="button button--ink button--large" disabled={registrationLinkBusy} onClick={() => { void requestRegistrationLink(); }}>{registrationLinkBusy ? "Sending…" : "Resend confirmation link"} <Mail size={18} /></button>
              <button type="button" className="text-button" onClick={() => { setRegistrationLinkError(""); setAccountStage("details"); }}>Use a different email address</button>
            </div>
          )}

          {step === 0 && accountStage === "password" && confirmationStatus === "checking" && (
            <div className="registration-verification-state" role="status" aria-live="polite">
              <span className="registration-verification-state__icon"><Clock3 /></span>
              <p className="step-kicker">Confirming your email</p>
              <h1 tabIndex={-1}>Checking your secure link…</h1>
              <p>Please wait while Kitanego completes your email verification.</p>
            </div>
          )}

          {step === 0 && accountStage === "password" && confirmationStatus === "invalid" && (
            <div className="registration-verification-state" role="alert">
              <span className="registration-verification-state__icon"><CircleHelp /></span>
              <p className="step-kicker">Link unavailable</p>
              <h1 tabIndex={-1}>This confirmation link can’t be used.</h1>
              <p>It may have expired or already been opened. Request a fresh link and continue without re-entering your details.</p>
              {member.email && <strong className="registration-verification-state__email">{member.email}</strong>}
              {member.email && member.firstName && member.lastName
                ? <button type="button" className="button button--ink button--large" disabled={registrationLinkBusy} onClick={() => { void requestReplacementRegistrationLink(); }}>{registrationLinkBusy ? "Sending…" : "Send a new confirmation link"} <Mail size={18} /></button>
                : <button type="button" className="button button--ink button--large" onClick={returnToRegistrationDetails}>Return to registration <ArrowRight size={18} /></button>}
              <button type="button" className="text-button" onClick={returnToRegistrationDetails}>Use a different email address</button>
            </div>
          )}

          {step === 0 && accountStage === "password" && confirmationStatus === "ready" && (
            <form className="form-stack registration-password-form" onSubmit={handlePasswordSetup}>
              <div className="form-heading">
                <span className="registration-verification-state__icon"><ShieldCheck /></span>
                <p className="step-kicker">Email verified</p>
                <h1 tabIndex={-1}>Create your password.</h1>
                <p>Use a strong, unique password to protect your Kitanego account.</p>
              </div>
              <label className="field-label">Password<input type="password" required autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} /></label>
              <ul className="password-requirements" aria-label="Password requirements">
                {passwordRequirements.map((requirement) => <li key={requirement.label} className={requirement.met ? "met" : ""}><CheckCircle2 />{requirement.label}</li>)}
              </ul>
              <label className="field-label">Confirm password<input type="password" required autoComplete="new-password" value={passwordConfirmation} onChange={(event) => setPasswordConfirmation(event.target.value)} /></label>
              {passwordConfirmation && !passwordsMatch && <p className="field-error" role="alert">The passwords do not match.</p>}
              {passwordError && <p className="field-error" role="alert">{passwordError}</p>}
              <button type="submit" className="button button--ink button--large button--full" disabled={!passwordIsStrong || !passwordsMatch || passwordBusy}>{passwordBusy ? "Creating account…" : "Create account"} <ArrowRight size={18} /></button>
            </form>
          )}

          {step === 1 && (
            <div className="form-stack registration-district-step">
              <div className="form-heading">
                <span className="step-kicker">{tx("Step 2 of 5")}</span>
                <h1 tabIndex={-1}>{tx("Choose your district.")}</h1>
              </div>
              <label className="field-label registration-district-field"><span className="sr-only">{tx("District in Sabah")}</span><div className="select-wrap"><select className={registrationDistrict ? undefined : "is-placeholder"} aria-label={tx("District in Sabah")} value={registrationDistrict} onChange={(event) => {
                setRegistrationDistrict(event.target.value);
                if (requireLocationGate) {
                  update({ district: event.target.value });
                  return;
                }
                locationRequestId.current += 1;
                update({ district: event.target.value, registrationLocation: null });
                setLocationStatus("idle");
                setLocationError("");
                setLocationFailure(null);
              }} autoComplete="off"><option value="" disabled>{tx("Select your district")}</option>{districts.map((district) => <option key={district} value={district}>{district}</option>)}</select><ChevronDown size={17} /></div></label>
              {!requireLocationGate && <div className={`location-capture ${member.registrationLocation ? "location-capture--ready" : ""}`} aria-live="polite">
                <div className="location-capture__heading"><span className="location-capture__icon">{member.registrationLocation ? <CheckCircle2 /> : <MapPin />}</span><div><strong>Registration location <em>Optional</em></strong><p>{member.registrationLocation ? "Captured for Kitanego’s protected demand map." : "Allow once to add this registration to the protected demand map."}</p></div></div>
                {member.registrationLocation && <div className="location-capture__result"><div><span>Latitude</span><strong>{member.registrationLocation.latitude.toFixed(5)}</strong></div><div><span>Longitude</span><strong>{member.registrationLocation.longitude.toFixed(5)}</strong></div><div><span>Accuracy</span><strong>±{member.registrationLocation.accuracy} m</strong></div></div>}
                <p className="location-capture__privacy"><ShieldCheck size={15} /> Stored securely for authorised administrators and excluded from supplier reports. You can remove it under Profile &amp; privacy.</p>
                {locationError && <p className="location-capture__error" role="alert">{locationError}</p>}
                <div className="location-capture__actions"><button type="button" className="button button--ghost button--small" onClick={captureRegistrationLocation} disabled={locationStatus === "requesting"} aria-busy={locationStatus === "requesting"}><MapPin size={16} /> {locationStatus === "requesting" ? "Getting location…" : member.registrationLocation ? "Update location" : "Use my current location"}</button>{member.registrationLocation && <button type="button" className="text-button" disabled={locationStatus === "requesting"} onClick={() => { locationRequestId.current += 1; update({ registrationLocation: null }); setLocationStatus("idle"); setLocationError(""); setLocationFailure(null); }}>Remove</button>}</div>
              </div>}
              <FlowActions back={() => setStep(0)} next={() => setStep(2)} disabled={!canContinue} label="Next" />
            </div>
          )}

          {step === 2 && (
            <div className="form-stack form-stack--wide registration-products-step">
              <ProductSelectionFields
                kicker={tx("Step 3 of 5")}
                title={tx("Pick your products")}
                description={tx("Choose the products you buy regularly.")}
                search={search}
                onSearch={setSearch}
                selected={member.products}
                onToggle={toggleProduct}
              />
              <FlowActions back={() => setStep(1)} next={() => setStep(3)} disabled={!canContinue} label="Next" />
            </div>
          )}

          {step === 3 && (
            <div className="form-stack form-stack--wide registration-brands-step">
              <div className="form-heading">
                <span className="step-kicker">{tx("Step 4 of 5")}</span>
                <h1 tabIndex={-1}>{tx("Choose your brands.")}</h1>
                <p>{tx("Select a listed brand or suggest a new brand for review.")}</p>
              </div>
              <BrandSelector selected={member.brands} onChange={async (brands) => { update({ brands }); }} allowSuggestions deferSuggestions language={language} />
              <FlowActions back={() => setStep(2)} next={() => setStep(4)} disabled={!canContinue} label="Next" />
            </div>
          )}

          {step === 4 && (
            <div className="form-stack form-stack--wide registration-rhythm-step">
              <div className="form-heading">
                <span className="step-kicker">{tx("Step 5 of 5")}</span>
                <h1 tabIndex={-1}>{tx("How does this fit your routine?")}</h1>
                <p>{tx("An estimate is enough. We use ranges, not exact spending.")}</p>
              </div>
              <ChoiceGroup title="How often do you usually buy these?" values={frequencies} selected={member.frequency} onSelect={(frequency) => update({ frequency })} />
              <ChoiceGroup title="About how much at a time?" values={spendRanges} selected={member.spend} onSelect={(spend) => update({ spend })} />
              <ChoiceGroup title="If a better price became available…" values={priceInterests} selected={member.priceInterest} onSelect={(priceInterest) => update({ priceInterest })} />
              {submitError && <p className="field-error" role="alert">{submitError}</p>}
              <FlowActions back={() => setStep(3)} next={() => { void complete(); }} disabled={!canContinue || isSubmitting} label={isSubmitting ? "Saving…" : "Submit"} final />
            </div>
          )}
          </>}
        </section>
      </main>
      {registrationSuccessOpen && <RegistrationSuccessDialog onProceed={() => {
        setRegistrationSuccessOpen(false);
        setAccountStage("complete");
        setStep(1);
      }} />}
      {infoTopic && <RegistrationInfoDialog topic={infoTopic} language={language} onClose={() => setInfoTopic(null)} />}
    </div>
  );
}

function ProductSelectionFields({ kicker, title, description, search, onSearch, selected, onToggle }: { kicker?: string; title: string; description: string; search: string; onSearch: (value: string) => void; selected: string[]; onToggle: (product: string) => void }) {
  const productMap = useProductCatalog();
  const { language } = useContext(InterfaceLanguageContext);
  const normalizedSearch = search.trim().toLowerCase();
  const visibleGroups = categories.map((category) => ({
    ...category,
    products: (productMap[category.id] ?? []).filter((product) => product.toLowerCase().includes(normalizedSearch)),
  })).filter((category) => category.products.length > 0);

  return <>
    <div className="form-heading">
      {kicker && <span className="step-kicker">{kicker}</span>}
      <h1 tabIndex={-1}>{title}</h1>
      <p>{description}</p>
    </div>
    <label className="search-field"><Search size={20} /><input type="search" value={search} onChange={(event) => onSearch(event.target.value)} placeholder={translateInterfaceText("Search products", language)} aria-label={translateInterfaceText("Search products", language)} /></label>
    <div className="product-groups">
      {visibleGroups.map((category) => <section className="product-group" key={category.id}>
        <h3>{translateInterfaceText(category.label, language)}</h3>
        <div className="product-chips">
          {category.products.map((product) => {
            const isSelected = selected.includes(product);
            return <button type="button" key={product} className={isSelected ? "selected" : ""} aria-pressed={isSelected} onClick={() => onToggle(product)}><img src={getProductImage(product)} alt="" loading="lazy" decoding="async" /><span>{translateInterfaceText(product, language)}</span>{isSelected && <Check size={16} aria-hidden="true" />}</button>;
          })}
        </div>
      </section>)}
      {visibleGroups.length === 0 && <div className="product-search-empty" role="status"><Search aria-hidden="true" /><strong>{translateInterfaceText("No matching products", language)}</strong><p>{translateInterfaceText("Try another product name or clear your search.", language)}</p><button type="button" className="text-button" onClick={() => onSearch("")}>{translateInterfaceText("Clear search", language)}</button></div>}
    </div>
  </>;
}

function FlowActions({ back, next, disabled, label, final = false }: { back: () => void; next: () => void; disabled: boolean; label: string; final?: boolean }) {
  const { language } = useContext(InterfaceLanguageContext);
  return <div className="flow-actions"><button className="button button--ghost" onClick={back}><ArrowLeft size={17} /> {translateInterfaceText("Back", language)}</button><button className={`button ${final ? "button--lime" : "button--ink"}`} onClick={next} disabled={disabled}>{translateInterfaceText(label, language)} <ArrowRight size={17} /></button></div>;
}

function ChoiceGroup({ title, values, selected, onSelect, visual = "radio" }: { title: string; values: string[]; selected: string; onSelect: (value: string) => void; visual?: "radio" | "faces" }) {
  const { language } = useContext(InterfaceLanguageContext);
  const faceIcons = [Laugh, Smile, Meh, Frown];
  if (visual === "faces") return <fieldset className="choice-group choice-group--faces"><legend>{translateInterfaceText(title, language)}</legend><div className="face-choice-grid">{values.map((value, index) => { const FaceIcon = faceIcons[index] ?? Meh; return <button type="button" key={value} className={selected === value ? "selected" : ""} aria-pressed={selected === value} onClick={() => onSelect(value)}><FaceIcon aria-hidden="true" /><span>{translateInterfaceText(value, language)}</span></button>; })}</div></fieldset>;
  return <fieldset className="choice-group"><legend>{translateInterfaceText(title, language)}</legend><div className="choice-grid">{values.map((value) => <label key={value} className={selected === value ? "selected" : ""}><input type="radio" name={title} checked={selected === value} onChange={() => onSelect(value)} /><span>{translateInterfaceText(value, language)}</span></label>)}</div></fieldset>;
}

function MemberAccountMenu({ member, navigate, onSignOut, profileTrigger = false, landingTrigger = false }: { member: MemberState; navigate: Navigate; onSignOut: () => Promise<void>; profileTrigger?: boolean; landingTrigger?: boolean }) {
  const [open, setOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const tooltipId = useId();
  const memberName = [member.firstName, member.lastName].filter(Boolean).join(" ") || "Member";

  useEffect(() => {
    if (!open) return;
    const closeOnOutsidePress = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutsidePress);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePress);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const logOut = async () => {
    setSigningOut(true);
    try {
      await onSignOut();
      setOpen(false);
    } finally {
      setSigningOut(false);
    }
  };

  return <div className={`member-account ${profileTrigger ? "member-account--profile" : ""} ${landingTrigger ? "member-account--landing" : ""}`} data-open={open || undefined} ref={menuRef}>
    <button className={`member-account__trigger ${landingTrigger ? "landing-member-button" : ""}`} type="button" aria-label={`Open account options for ${memberName}`} aria-haspopup="menu" aria-expanded={open} aria-controls={open ? menuId : undefined} aria-describedby={!profileTrigger && !landingTrigger ? tooltipId : undefined} onClick={() => setOpen((current) => !current)}>
      <MemberAvatar member={member} />
      {!profileTrigger && !landingTrigger && <span id={tooltipId} className="profile-avatar-tooltip" role="tooltip">{memberName} · Member</span>}
    </button>
    {open && <div className="member-account__menu" id={menuId} role="menu" aria-label="Member account options">
      <div className="member-account__identity" role="none"><span><strong>{memberName}</strong><em>{member.email}</em><small>Member</small></span></div>
      <button type="button" role="menuitem" onClick={() => { setOpen(false); navigate("/home"); }}><LayoutDashboard size={18} /><span><strong>Dashboard</strong></span><ChevronRight size={17} /></button>
      <button type="button" role="menuitem" onClick={() => { setOpen(false); navigate("/profile"); }}><ShieldCheck size={18} /><span><strong>Profile settings</strong></span><ChevronRight size={17} /></button>
      <button className="member-account__logout" type="button" role="menuitem" disabled={signingOut} onClick={() => { void logOut(); }}><LogOut size={18} /><span><strong>{signingOut ? "Logging out…" : "Log out"}</strong></span></button>
    </div>}
  </div>;
}

function AppShell({ member, navigate, active, onSignOut, children }: { member: MemberState; navigate: Navigate; active: string; onSignOut: () => Promise<void>; children: ReactNode }) {
  const { opportunitiesEnabled } = useFeatureAvailability();
  const { info: scrollingInfo } = useScrollingInfo();
  const { language, onLanguageChange } = useContext(InterfaceLanguageContext);
  const localizedScrollingMessage = scrollingInfo?.message ? translateInterfaceText(scrollingInfo.message, language) : "";
  const memberNavigationLabel = translateInterfaceText("Member navigation", language);
  const nav = [
    { id: "home", label: translateInterfaceText("Home", language), icon: Home, route: "/home" as Route },
    { id: "interests", label: translateInterfaceText("Wishlists", language), icon: Heart, route: "/interests" as Route },
    { id: "on-demand", label: translateInterfaceText("Demand", language), icon: CalendarDays, route: "/on-demand" as Route },
    { id: "opportunities", label: translateInterfaceText("Opportunities", language), icon: BriefcaseBusiness, route: "/opportunity" as Route },
  ].filter((item) => opportunitiesEnabled || item.id !== "opportunities");
  return (
    <div className={`app-shell app-shell--${active}`}>
      <aside className="app-sidebar">
        <Logo wordmark />
        <nav aria-label={memberNavigationLabel}>{nav.map((item) => { const Icon = item.icon; return <button key={item.id} className={active === item.id ? "active" : ""} onClick={() => navigate(item.route)}><Icon size={19} /> {item.label}{item.id === "opportunities" && <span className="nav-dot" />}</button>; })}</nav>
      </aside>
      <div className="app-content">
        <header className="app-topbar app-topbar--account-only app-topbar--member">
          <div className="app-topbar__tablet-brand"><Logo wordmark /></div>
          {localizedScrollingMessage && <div className="member-scrolling-info" aria-label={`Kitanego update: ${localizedScrollingMessage}`}>
            <div className="member-scrolling-info__viewport" aria-hidden="true"><div className="member-scrolling-info__track"><span>{localizedScrollingMessage}</span><span>{localizedScrollingMessage}</span></div></div>
          </div>}
          <div className="app-topbar__member-actions">
            <LanguageSelector value={language} onChange={onLanguageChange} ariaLabel="Choose interface language" />
            <MemberAccountMenu member={member} navigate={navigate} onSignOut={onSignOut} />
          </div>
        </header>
        <main className="app-main">{children}</main>
      </div>
      <nav className="bottom-nav bottom-nav--member" aria-label={memberNavigationLabel} style={{ gridTemplateColumns: `repeat(${nav.length}, 1fr)` }}>{nav.map((item) => { const Icon = item.icon; return <button key={item.id} className={active === item.id ? "active" : ""} aria-current={active === item.id ? "page" : undefined} onClick={() => navigate(item.route)}><Icon size={23} /><span>{item.label}</span></button>; })}</nav>
    </div>
  );
}

function getTimeGreeting(date = new Date()) {
  const hour = date.getHours();
  if (hour >= 5 && hour < 12) return "Good morning";
  if (hour >= 12 && hour < 17) return "Good afternoon";
  if (hour >= 17 && hour < 21) return "Good evening";
  return "Good night";
}

function getMembershipTier(member: MemberState) {
  return member.plan === "premium" ? "Premium" : member.plan === "pro" ? "Pro" : "Basic";
}

type WishlistAvatarItem = { label: string; image?: string };

function WishlistAvatarStack({ items, brands = false }: { items: WishlistAvatarItem[]; brands?: boolean }) {
  const maxCircles = 5;
  const visibleItems = items.slice(0, items.length > maxCircles ? maxCircles - 1 : maxCircles);
  if (visibleItems.length === 0) return null;

  return (
    <div className="wishlist-avatar-stack" aria-label={items.map((item) => item.label).join(", ")}>
      {visibleItems.map((item, index) => (
        <span className="wishlist-avatar" key={`${item.label}-${index}`} title={item.label}>
          {item.image
            ? brands
              ? <BrandImage src={item.image} alt={item.label} loading="lazy" decoding="async" />
              : <img src={item.image} alt="" loading="lazy" decoding="async" />
            : <span aria-hidden="true">{item.label.trim().charAt(0).toUpperCase()}</span>}
        </span>
      ))}
      {items.length > visibleItems.length && <span className="wishlist-avatar wishlist-avatar--more" title={`${items.length - visibleItems.length} more selections`}>+{items.length - visibleItems.length}</span>}
    </div>
  );
}

function MemberHome({ member, navigate, isSunny, latestNews, latestNewsSyncStatus, onSignOut }: { member: MemberState; navigate: Navigate; isSunny: boolean; latestNews: LatestNews | null; latestNewsSyncStatus: LatestNewsSyncStatus; onSignOut: () => Promise<void> }) {
  const { opportunitiesEnabled } = useFeatureAvailability();
  const { language } = useContext(InterfaceLanguageContext);
  const firstName = member.firstName || "Member";
  const [greeting, setGreeting] = useState(() => getTimeGreeting());
  const [lastLoginAt, setLastLoginAt] = useState<string | null>(null);
  const [monthlyWishlists, setMonthlyWishlists] = useState<MonthlyWishlist[]>([]);
  const [monthlyWishlistsStatus, setMonthlyWishlistsStatus] = useState<"loading" | "ready" | "error">("loading");
  const [communityProductDemand, setCommunityProductDemand] = useState<PublicProductDemand[]>([]);
  const [communityMetrics, setCommunityMetrics] = useState<PublicCommunityMetrics | null>(null);
  const [communityBrandImages, setCommunityBrandImages] = useState<Map<string, string>>(() => new Map());
  const [newBrands, setNewBrands] = useState<PublicNewBrand[]>([]);
  const [communityDemandStatus, setCommunityDemandStatus] = useState<"loading" | "ready" | "error">("loading");
  const [communitySummary, setCommunitySummary] = useState<"brands" | "top-products" | "new-brands" | null>(null);
  const membershipTier = getMembershipTier(member);
  const selectedNewsTranslation = latestNews ? getLatestNewsTranslation(latestNews, language) : null;
  const fallbackNewsTitle = latestNewsSyncStatus === "loading" ? "Loading latest news…" : latestNewsSyncStatus === "error" ? "Latest news is temporarily unavailable" : "No latest news yet";
  const fallbackNewsBody = latestNewsSyncStatus === "error" ? "Please check again shortly." : "Updates posted by Kitanego will appear here.";
  const newsTitleSource = selectedNewsTranslation?.title ?? fallbackNewsTitle;
  const newsBodySource = selectedNewsTranslation?.body ?? fallbackNewsBody;
  const hasAuthoredNewsTranslation = Boolean(latestNews?.translations[language]);
  const localizedNewsTitle = hasAuthoredNewsTranslation ? newsTitleSource : translateInterfaceText(newsTitleSource, language);
  const localizedNewsBody = hasAuthoredNewsTranslation
    ? newsBodySource
    : translateInterfaceText(newsBodySource.replace(/\s+/g, " ").trim(), language);
  const localizedGreeting = translateInterfaceText(`${greeting}, ${firstName}.`, language);
  const localizedMonthlyDemandHeading = translateInterfaceText("Your monthly Demand", language);
  const localizedLatestUpdatesHeading = translateInterfaceText("Latest updates", language);

  useEffect(() => {
    const updateGreeting = () => setGreeting(getTimeGreeting());
    updateGreeting();
    const interval = window.setInterval(updateGreeting, 60_000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    let active = true;
    void readMonthlyWishlists().then((wishlists) => {
      if (!active) return;
      setMonthlyWishlists(wishlists);
      setMonthlyWishlistsStatus("ready");
    }).catch(() => {
      if (active) setMonthlyWishlistsStatus("error");
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    const refreshCommunityDemand = async (showLoading = false) => {
      if (showLoading) setCommunityDemandStatus("loading");
      try {
        const [products, brands, metrics, approvedNewBrands] = await Promise.all([
          loadPublicProductDemand(),
          loadBrandSuggestions("", 500).catch(() => []),
          loadPublicCommunityMetrics(),
          loadPublicNewBrands(),
        ]);
        if (!active) return;
        setCommunityProductDemand(products);
        setCommunityMetrics(metrics);
        setCommunityBrandImages(new Map(brands.filter((brand) => brand.imageUrl).map((brand) => [brand.name.toLocaleLowerCase(), brand.imageUrl])));
        setNewBrands(approvedNewBrands);
        setCommunityDemandStatus("ready");
      } catch {
        if (active) setCommunityDemandStatus("error");
      }
    };
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") void refreshCommunityDemand();
    };
    void refreshCommunityDemand(true);
    const refreshInterval = window.setInterval(() => { void refreshCommunityDemand(); }, 60_000);
    window.addEventListener("focus", refreshWhenVisible);
    window.addEventListener("kitanego:member-archived", refreshWhenVisible);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      active = false;
      window.clearInterval(refreshInterval);
      window.removeEventListener("focus", refreshWhenVisible);
      window.removeEventListener("kitanego:member-archived", refreshWhenVisible);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, []);

  useEffect(() => {
    let active = true;
    void supabase.auth.getSession().then(({ data }) => {
      if (active) setLastLoginAt(data.session?.user.last_sign_in_at ?? null);
    });
    return () => { active = false; };
  }, []);

  const lastLoginLabel = lastLoginAt
    ? new Intl.DateTimeFormat(language === "zh" ? "zh-CN" : language === "en" ? "en-MY" : "ms-MY", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", timeZone: "Asia/Kuching" }).format(new Date(lastLoginAt))
    : "This session";

  const openDemandMonth = (month: string) => {
    requestDemandMonth(month);
    navigate("/on-demand");
  };

  const communityProducts = communityProductDemand
    .filter((item) => !decodeBrandProduct(item.product) && item.memberCount > 0)
    .sort((a, b) => b.memberCount - a.memberCount || a.product.localeCompare(b.product))
  const communityBrands = communityProductDemand
    .map((item) => ({ ...item, brand: decodeBrandProduct(item.product) }))
    .filter((item): item is PublicProductDemand & { brand: string } => Boolean(item.brand) && item.memberCount > 0)
    .map((item) => ({ ...item, image: communityBrandImages.get(item.brand.toLocaleLowerCase()) ?? "" }))
    .sort((a, b) => b.memberCount - a.memberCount || a.brand.localeCompare(b.brand));
  const totalCommunityBrandSelections = communityBrands.reduce((total, item) => total + item.memberCount, 0);
  const highDemandProducts = communityProducts.filter((item) => item.memberCount >= 2);
  const highDemandProductCount = communityMetrics?.highDemandProductCount ?? highDemandProducts.length;
  const highDemandProductLabel = translateInterfaceText(highDemandProductCount === 1 ? "high demand product" : "high demand products", language);
  const selectedBrandsLabel = translateInterfaceText("brands selected", language);
  const newBrandsLabel = translateInterfaceText(newBrands.length === 1 ? "new brand added" : "new brands added", language);

  return (
    <AppShell member={member} navigate={navigate} active="home" onSignOut={onSignOut}>
      <section className="dashboard-heading"><div>{!isSunny && <span className="status-chip"><i /> {member.verified ? "VERIFIED" : "REGISTERED"}</span>}<h1 className="member-page-title" tabIndex={-1}>{localizedGreeting}</h1><p className="member-last-login">{translateInterfaceText(`Last login: ${lastLoginLabel}`, language)}</p></div><button type="button" className="home-new-demand-button" aria-label="Register new monthly demand" title="Register new monthly demand" onClick={() => navigate("/wishlist/new")}><Plus size={24} /></button></section>

      <section className="member-hero-grid">
        <article className="signal-card latest-news-card">
          <div className="signal-card__body latest-news-card__body">
            <div className="latest-news-card__icon"><Bell aria-hidden="true" /></div>
            <div><h2>{localizedNewsTitle}</h2><p className="latest-news-card__message"><span key={`${language}:${latestNews?.updatedAt ?? `${localizedNewsTitle}:${localizedNewsBody}`}`} className="latest-news-card__slide-copy">{localizedNewsBody}</span></p>{latestNews?.updatedAt && <small>{translateInterfaceText(`Updated ${new Date(latestNews.updatedAt).toLocaleDateString(language === "zh" ? "zh-CN" : language === "en" ? "en-MY" : "ms-MY", { day: "numeric", month: "short", year: "numeric" })}`, language)}</small>}</div>
          </div>
        </article>
        <article className="signal-card monthly-wishlists-card">
          <div className="monthly-wishlists-card__head"><div><h2>{localizedMonthlyDemandHeading}</h2></div></div>
          {monthlyWishlistsStatus === "loading" && <p className="monthly-wishlists-card__state">Loading your wishlists…</p>}
          {monthlyWishlistsStatus === "error" && <p className="monthly-wishlists-card__state">Your monthly wishlists are temporarily unavailable.</p>}
          {monthlyWishlistsStatus === "ready" && monthlyWishlists.length === 0 && <div className="monthly-wishlists-card__empty"><CalendarDays aria-hidden="true" /><p>No monthly wishlist yet.</p></div>}
          {monthlyWishlists.length > 0 && <div className="monthly-wishlist-list">{monthlyWishlists.map((wishlist) => { const monthLabel = new Intl.DateTimeFormat(language === "zh" ? "zh-CN" : language === "en" ? "en-MY" : "ms-MY", { month: "short", year: "numeric", timeZone: "Asia/Kuching" }).format(new Date(`${wishlist.month}-01T00:00:00+08:00`)); const demandCountLabel = translateInterfaceText(`${wishlist.products.length} ${wishlist.products.length === 1 ? "product" : "products"} · ${wishlist.brands.length} ${wishlist.brands.length === 1 ? "brand" : "brands"}`, language); return <button type="button" key={wishlist.id} aria-label={`Open ${monthLabel} demand`} onClick={() => openDemandMonth(wishlist.month)}><time dateTime={wishlist.month}>{monthLabel}</time><span>{demandCountLabel}</span></button>; })}</div>}
        </article>
        {opportunitiesEnabled && <article className="status-card">
          {!isSunny && <div className="card-topline"><span>Account status</span><BadgeCheck size={20} /></div>}
          <strong className="status-card__label">{membershipTier}</strong>
          <p>{membershipTier === "Basic" ? "Your account is on the Basic plan." : membershipTier === "Pro" ? "Your Pro plan includes access to member Opportunities information." : "Your Premium plan includes full member access."}</p>
          <button type="button" className="button button--ink status-card__upgrade" onClick={() => navigate(membershipTier === "Basic" ? "/profile" : "/opportunity")}>{membershipTier === "Basic" ? "View account" : "View opportunities"} <ArrowRight size={16} /></button>
        </article>}
      </section>

      <section className="dashboard-section">
        <div className="section-row"><div>{!isSunny && <p className="eyebrow">Live member demand</p>}<h2>{localizedLatestUpdatesHeading}</h2></div></div>
        <div className="pulse-stat-grid">
          <button type="button" className="community-top-products-card" onClick={() => setCommunitySummary("top-products")} aria-label="Open high-demand products summary"><div className="pulse-stat-copy"><strong>{communityDemandStatus === "ready" ? highDemandProductCount.toLocaleString("en-MY") : "—"}</strong><span>{highDemandProductLabel}</span></div>{communityDemandStatus === "loading" && <p>Loading community demand…</p>}{communityDemandStatus === "error" && <p>Community demand is temporarily unavailable.</p>}{communityDemandStatus === "ready" && highDemandProducts.length === 0 && <p>No high-demand products yet.</p>}{highDemandProducts.length > 0 && <WishlistAvatarStack items={highDemandProducts.map((item) => ({ label: item.product, image: getProductImage(item.product) }))} />}</button>
          <button type="button" onClick={() => setCommunitySummary("brands")} aria-label="Open brands summary"><div className="pulse-stat-copy"><strong>{communityDemandStatus === "ready" ? (communityMetrics?.uniqueBrandCount ?? communityBrands.length).toLocaleString("en-MY") : "—"}</strong><span>{selectedBrandsLabel}</span></div><WishlistAvatarStack brands items={communityBrands.map((item) => ({ label: item.brand, image: item.image }))} /></button>
          <button type="button" onClick={() => setCommunitySummary("new-brands")} aria-label="Open new brands summary"><div className="pulse-stat-copy"><strong>{communityDemandStatus === "ready" ? newBrands.length.toLocaleString("en-MY") : "—"}</strong><span>{newBrandsLabel}</span></div><WishlistAvatarStack brands items={newBrands.map((brand) => ({ label: brand.name, image: brand.imageUrl }))} /></button>
        </div>
      </section>

      {communitySummary === "brands" && <CommunityDemandSummaryDialog kind="brands" language={language} totalSelections={communityMetrics?.totalBrandSelections ?? totalCommunityBrandSelections} items={communityBrands.map((item) => ({ label: item.brand, memberCount: item.memberCount, image: item.image }))} onClose={() => setCommunitySummary(null)} />}
      {communitySummary === "top-products" && <CommunityDemandSummaryDialog kind="top-products" language={language} totalSelections={communityMetrics?.activeMemberCount ?? 0} items={highDemandProducts.map((item) => ({ label: item.product, memberCount: item.memberCount, image: getProductImage(item.product) }))} onClose={() => setCommunitySummary(null)} />}
      {communitySummary === "new-brands" && <CommunityDemandSummaryDialog kind="new-brands" language={language} totalSelections={newBrands.length} items={newBrands.map((brand) => ({ label: brand.name, memberCount: 0, image: brand.imageUrl }))} onClose={() => setCommunitySummary(null)} />}

      {opportunitiesEnabled && <section className="dashboard-section opportunity-section">
        <div className="section-row"><div><h2>No latest opportunities yet</h2></div></div>
        <article className="content-card member-empty-card"><Info /><div><h3>We’ll show verified opportunities here.</h3><p>{membershipTier === "Basic" ? "Opportunity information is available on Pro and Premium plans." : "Kitanego will only publish an opportunity after live demand is ready for supplier review."}</p></div></article>
      </section>}

    </AppShell>
  );
}

function currentKuchingMonth() {
  const parts = new Intl.DateTimeFormat("en", { year: "numeric", month: "2-digit", timeZone: "Asia/Kuching" }).formatToParts(new Date());
  const year = parts.find((part) => part.type === "year")?.value ?? String(new Date().getFullYear());
  const month = parts.find((part) => part.type === "month")?.value ?? String(new Date().getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

function MonthlyWishlistFlow({ member, navigate, notify, onSignOut }: { member: MemberState; navigate: Navigate; notify: (message: string) => void; onSignOut: () => Promise<void> }) {
  const productMap = useProductCatalog();
  const { info: scrollingInfo } = useScrollingInfo();
  const { language, onLanguageChange } = useContext(InterfaceLanguageContext);
  const interfaceLocale = language === "zh" ? "zh-CN" : language === "en" ? "en-MY" : "ms-MY";
  const localizedScrollingMessage = scrollingInfo?.message ? translateInterfaceText(scrollingInfo.message, language) : "";
  const minimumMonth = currentKuchingMonth();
  const [step, setStep] = useState(0);
  const [month, setMonth] = useState("");
  const [products, setProducts] = useState<string[]>([]);
  const [brands, setBrands] = useState<string[]>([]);
  const [frequency, setFrequency] = useState("");
  const [spendRange, setSpendRange] = useState("");
  const [priceInterest, setPriceInterest] = useState("");
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const selectedCategories = Object.keys(productMap).filter((category) => products.some((product) => productMap[category]?.includes(product)));
  const availableMonths = useMemo(() => {
    const [startYear, startMonth] = minimumMonth.split("-").map(Number);
    return Array.from({ length: 12 }, (_, offset) => {
      const date = new Date(Date.UTC(startYear, startMonth - 1 + offset, 1));
      const value = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
      return {
        value,
        shortLabel: new Intl.DateTimeFormat(interfaceLocale, { month: "short", timeZone: "UTC" }).format(date),
        longLabel: new Intl.DateTimeFormat(interfaceLocale, { month: "long", year: "numeric", timeZone: "UTC" }).format(date),
        year: date.getUTCFullYear(),
      };
    });
  }, [interfaceLocale, minimumMonth]);
  const monthLabel = month
    ? new Intl.DateTimeFormat(interfaceLocale, { month: "long", year: "numeric", timeZone: "Asia/Kuching" }).format(new Date(`${month}-01T00:00:00+08:00`))
    : translateInterfaceText("your selected month", language);
  const canContinue = [Boolean(month), products.length > 0, true, Boolean(frequency && spendRange && priceInterest), true][step];

  const toggleProduct = (product: string) => setProducts((current) => current.includes(product) ? current.filter((item) => item !== product) : [...current, product]);
  const submit = async () => {
    if (saving) return;
    setSaving(true);
    setError("");
    try {
      await saveMonthlyWishlist({ month, categories: selectedCategories, products, brands, frequency, spendRange, priceInterest });
      notify(language === "zh" ? `${monthLabel}心愿单已保存` : `${monthLabel} ${translateInterfaceText("wishlist saved", language)}`);
      navigate("/home");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : translateInterfaceText("Your monthly wishlist could not be saved. Please try again.", language));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="onboarding-shell monthly-wishlist-flow">
      <header className="app-topbar app-topbar--account-only app-topbar--member">
        <div className="app-topbar__tablet-brand"><Logo wordmark onClick={() => navigate("/home")} /></div>
        {localizedScrollingMessage && <div className="member-scrolling-info" aria-label={`Kitanego update: ${localizedScrollingMessage}`}>
          <div className="member-scrolling-info__viewport" aria-hidden="true"><div className="member-scrolling-info__track"><span>{localizedScrollingMessage}</span><span>{localizedScrollingMessage}</span></div></div>
        </div>}
        <div className="app-topbar__member-actions">
          <LanguageSelector value={language} onChange={onLanguageChange} ariaLabel={translateInterfaceText("Choose interface language", language)} />
          <MemberAccountMenu member={member} navigate={navigate} onSignOut={onSignOut} />
        </div>
      </header>
      <main className="onboarding-main">
        <section className="onboarding-panel">
          <div className="mobile-progress"><span style={{ width: `${((step + 1) / 5) * 100}%` }} /></div>
          {step === 0 && <div className="form-stack monthly-wishlist-month-step"><div className="form-heading"><h1 tabIndex={-1}>{translateInterfaceText("Monthly Wishlist", language)}</h1><p>{translateInterfaceText("When do you plan to buy?", language)}</p></div><div className="wishlist-month-picker" role="group" aria-label={translateInterfaceText("Choose a wishlist month", language)}><div className="wishlist-month-picker__heading"><CalendarDays aria-hidden="true" /><div><strong>{translateInterfaceText("Choose a month", language)}</strong><span>{translateInterfaceText("Select one month to continue", language)}</span></div></div><div className="wishlist-month-picker__grid">{availableMonths.map((option) => <button type="button" key={option.value} className={month === option.value ? "selected" : ""} aria-pressed={month === option.value} aria-label={option.longLabel} onClick={() => setMonth(option.value)}><span>{option.shortLabel}</span><small>{option.year}</small>{month === option.value && <Check aria-hidden="true" />}</button>)}</div></div></div>}
          {step === 1 && <div className="form-stack form-stack--wide registration-products-step"><ProductSelectionFields title={translateInterfaceText("Pick your products", language)} description={translateInterfaceText("Choose your shopping list for this month.", language)} search={search} onSearch={setSearch} selected={products} onToggle={toggleProduct} /></div>}
          {step === 2 && <div className="form-stack form-stack--wide registration-brands-step"><div className="form-heading"><p className="step-kicker">{monthLabel.toUpperCase()}</p><h1 tabIndex={-1}>{translateInterfaceText("Choose your brands.", language)}</h1><p>{translateInterfaceText("Select brands from the list, search, or add a new brand. This step is optional.", language)}</p></div><BrandSelector selected={brands} onChange={async (next) => { setBrands(next); }} language={language} /></div>}
          {step === 3 && <div className="form-stack registration-rhythm-step"><div className="form-heading"><h1 tabIndex={-1}>{translateInterfaceText("Budget spending plan", language)}</h1><p>{translateInterfaceText("How do you plan on buying the items on your wishlist?", language)}</p></div><ChoiceGroup title={translateInterfaceText("How often do you buy these products?", language)} values={frequencies} selected={frequency} onSelect={setFrequency} /><ChoiceGroup title={translateInterfaceText("Expected monthly spend", language)} values={spendRanges} selected={spendRange} onSelect={setSpendRange} /><ChoiceGroup title={translateInterfaceText("Hoping to find a more budget-friendly option", language)} values={priceInterests} selected={priceInterest} onSelect={setPriceInterest} visual="faces" /></div>}
          {step === 4 && <div className="form-stack monthly-wishlist-review"><div className="form-heading"><h1 tabIndex={-1}>{language === "zh" ? `检查${monthLabel}` : `${translateInterfaceText("Review", language)} ${monthLabel}.`}</h1><p>{translateInterfaceText("Your product categories are created automatically from your selected products.", language)}</p></div><dl><div><dt>{translateInterfaceText("Products", language)}</dt><dd>{products.length}</dd></div><div><dt>{translateInterfaceText("Categories", language)}</dt><dd>{selectedCategories.length}</dd></div><div><dt>{translateInterfaceText("Brands", language)}</dt><dd>{brands.length}</dd></div><div><dt>{translateInterfaceText("Buying frequency", language)}</dt><dd>{translateInterfaceText(frequency, language)}</dd></div><div><dt>{translateInterfaceText("Expected spend", language)}</dt><dd>{spendRange}</dd></div></dl>{error && <p className="field-error" role="alert">{error}</p>}</div>}
          <div className="flow-actions monthly-wishlist-actions"><button type="button" className="button button--ghost" onClick={() => step === 0 ? navigate("/on-demand") : setStep((current) => current - 1)}><ArrowLeft size={18} /> {translateInterfaceText("Back", language)}</button>{step < 4 ? <button type="button" className="button button--ink" disabled={!canContinue} onClick={() => setStep((current) => current + 1)}>{translateInterfaceText("Next", language)} <ArrowRight size={18} /></button> : <button type="button" className="button button--ink" disabled={saving} onClick={() => { void submit(); }}>{translateInterfaceText(saving ? "Submitting…" : "Submit", language)} <Check size={18} /></button>}</div>
        </section>
      </main>
    </div>
  );
}

function OnDemand({ member, navigate, notify, onSignOut }: { member: MemberState; navigate: Navigate; notify: (message: string) => void; onSignOut: () => Promise<void> }) {
  const productMap = useProductCatalog();
  const { language } = useContext(InterfaceLanguageContext);
  const interfaceLocale = language === "zh" ? "zh-CN" : language === "en" ? "en-MY" : "ms-MY";
  const productsLabel = translateInterfaceText("products", language);
  const brandsLabel = translateInterfaceText("brands", language);
  const categoriesLabel = translateInterfaceText("categories", language);
  const demandSubtitle = translateInterfaceText("Your monthly demand.", language);
  const chooseDemandLabel = translateInterfaceText("Choose a monthly demand", language);
  const updateLabel = translateInterfaceText("Update", language);
  const formatDemandMonthHeading = (month: string, label: string) => {
    const monthLabel = new Intl.DateTimeFormat(interfaceLocale, { month: "long", year: "numeric", timeZone: "Asia/Kuching" }).format(new Date(`${month}-01T00:00:00+08:00`));
    return language === "ms" || language === "sabah"
      ? `${label.charAt(0).toLocaleUpperCase()}${label.slice(1)} ${monthLabel}`
      : `${monthLabel} ${label}`;
  };
  const [wishlists, setWishlists] = useState<MonthlyWishlist[]>([]);
  const [selectedMonth, setSelectedMonth] = useState(takeRequestedDemandMonth);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [section, setSection] = useState<"products" | "brands" | "categories">("products");
  const [editingProducts, setEditingProducts] = useState(false);
  const [editingBrands, setEditingBrands] = useState(false);
  const [draftProducts, setDraftProducts] = useState<string[]>([]);
  const [brandCatalogue, setBrandCatalogue] = useState<BrandSuggestion[]>([]);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<MonthlyWishlist | null>(null);
  const [deleting, setDeleting] = useState(false);
  const currentMonth = currentKuchingMonth();
  const activeWishlist = wishlists.find((wishlist) => wishlist.month === selectedMonth) ?? null;
  const selectedYear = Number((activeWishlist?.month || selectedMonth || currentMonth).slice(0, 4));
  const yearWishlists = wishlists.filter((wishlist) => Number(wishlist.month.slice(0, 4)) === selectedYear).sort((left, right) => left.month.localeCompare(right.month));
  const selectedCategories = Object.keys(productMap).filter((category) => (editingProducts ? draftProducts : activeWishlist?.products ?? []).some((product) => productMap[category]?.includes(product)));

  useEffect(() => {
    let active = true;
    void readMonthlyWishlists().then((items) => {
      if (!active) return;
      setWishlists(items);
      const sorted = [...items].sort((left, right) => right.month.localeCompare(left.month));
      setSelectedMonth((requestedMonth) => sorted.some((item) => item.month === requestedMonth)
        ? requestedMonth
        : sorted.some((item) => item.month === currentMonth) ? currentMonth : (sorted[0]?.month ?? ""));
      setStatus("ready");
    }).catch(() => { if (active) setStatus("error"); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    setDraftProducts(activeWishlist?.products ?? []);
    setEditingProducts(false);
    setEditingBrands(false);
  }, [activeWishlist?.id]);

  useEffect(() => {
    let active = true;
    // Demand uses the complete Wishlist brand catalogue so its artwork is
    // identical even for brands beyond the first suggestion page.
    void loadBrandSuggestions("", 500).then((items) => { if (active) setBrandCatalogue(items); }).catch(() => { if (active) setBrandCatalogue([]); });
    return () => { active = false; };
  }, []);

  const persist = async (wishlist: MonthlyWishlist, patch: Partial<MonthlyWishlist>) => {
    setSaving(true);
    try {
      const next = { ...wishlist, ...patch };
      const saved = await saveMonthlyWishlist({ month: next.month, categories: next.categories, products: next.products, brands: next.brands, frequency: next.frequency, spendRange: next.spendRange, priceInterest: next.priceInterest });
      setWishlists((current) => current.map((item) => item.id === wishlist.id ? saved : item));
      return true;
    } catch (caught) {
      notify(caught instanceof Error ? caught.message : "This monthly wishlist could not be updated.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveProducts = async () => {
    if (!activeWishlist) return;
    const nextCategories = Object.keys(productMap).filter((category) => draftProducts.some((product) => productMap[category]?.includes(product)));
    if (await persist(activeWishlist, { products: draftProducts, categories: nextCategories })) {
      setEditingProducts(false);
      notify("Monthly product choices saved");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteMonthlyWishlist(deleteTarget.id);
      const remaining = wishlists.filter((wishlist) => wishlist.id !== deleteTarget.id);
      setWishlists(remaining);
      setSelectedMonth(remaining[0]?.month ?? "");
      setEditingProducts(false);
      setEditingBrands(false);
      setDeleteTarget(null);
      notify("Monthly demand deleted");
    } catch (caught) {
      notify(caught instanceof Error ? caught.message : "This monthly demand could not be deleted.");
    } finally {
      setDeleting(false);
    }
  };

  return <AppShell member={member} navigate={navigate} active="on-demand" onSignOut={onSignOut}>
    <section className="dashboard-heading compact"><div><h1 className="member-page-title" tabIndex={-1}>{translateInterfaceText("Demand", language)}</h1><p>{demandSubtitle}</p></div></section>
    {status === "loading" && <section className="content-card on-demand-state">Loading monthly wishlists…</section>}
    {status === "error" && <section className="content-card on-demand-state">Monthly wishlists are temporarily unavailable.</section>}
    {status === "ready" && yearWishlists.length === 0 && <section className="content-card on-demand-empty"><CalendarDays /><h2>No monthly demand yet.</h2><p>Create your first demand to start viewing monthly demand.</p></section>}
    {activeWishlist && <>
      <section className="interest-summary interest-summary--brands on-demand-summary" role="tablist" aria-label="Demand sections">
        <button type="button" role="tab" aria-selected={section === "products"} onClick={() => setSection("products")}><span>{activeWishlist.products.length}</span><p>{productsLabel}</p></button>
        <button type="button" role="tab" aria-selected={section === "brands"} onClick={() => setSection("brands")}><span>{activeWishlist.brands.length}</span><p>{brandsLabel}</p></button>
        <button type="button" role="tab" aria-selected={section === "categories"} onClick={() => setSection("categories")}><span>{activeWishlist.categories.length}</span><p>{categoriesLabel}</p></button>
      </section>
      <div className="on-demand-detail-panel">
      <div className="on-demand-month-row">
        <h2 className="on-demand-month-title">{chooseDemandLabel}</h2>
        <div className="on-demand-month-controls">
        <section className="on-demand-months" aria-label={`Monthly wishlists for ${selectedYear}`}><div className="on-demand-months__rail">{yearWishlists.map((wishlist) => { const label = new Intl.DateTimeFormat(interfaceLocale, { month: "short", timeZone: "Asia/Kuching" }).format(new Date(`${wishlist.month}-01T00:00:00+08:00`)); return <button type="button" key={wishlist.id} className={wishlist.month === selectedMonth ? "active" : ""} aria-pressed={wishlist.month === selectedMonth} onClick={() => setSelectedMonth(wishlist.month)}><span>{label}</span></button>; })}</div><strong className="on-demand-year">{selectedYear}</strong></section>
        <button type="button" className="button button--ink new-wishlist-button on-demand-month-add" aria-label="Add new wishlist" onClick={() => navigate("/wishlist/new")}><Plus size={18} /><span>New Wishlist</span></button>
        </div>
      </div>
      {section === "products" && <section className="content-card product-actions-panel"><div className="section-row"><div><h2>{formatDemandMonthHeading(activeWishlist.month, productsLabel)}</h2><p>{translateInterfaceText(activeWishlist.frequency, language)} · {activeWishlist.spendRange}</p></div></div>{editingProducts ? <div className="product-editor">{Object.values(productMap).flat().map((product) => { const selected = draftProducts.includes(product); return <div key={product} className={`product-editor__row ${selected ? "selected" : ""}`}><button type="button" className="product-editor__check" aria-pressed={selected} aria-label={selected ? `Unselect ${product}` : `Select ${product}`} onClick={() => setDraftProducts((current) => selected ? current.filter((item) => item !== product) : [...current, product])}>{selected && <Check size={15} />}</button><img src={getProductImage(product)} alt="" /><strong>{product}</strong></div>; })}</div> : <div className="saved-product-list on-demand-products">{activeWishlist.products.map((product) => <div key={product}><img src={getProductImage(product)} alt="" /><span><strong>{product}</strong><small>{translateInterfaceText(activeWishlist.frequency, language)}</small></span></div>)}</div>}{editingProducts ? <div className="demand-edit-actions"><button type="button" className="product-edit-action demand-delete-action" disabled={saving} aria-label="Delete this monthly demand" onClick={() => setDeleteTarget(activeWishlist)}><Trash2 size={19} /></button><button type="button" className="product-edit-action product-edit-done" disabled={saving || draftProducts.length === 0} onClick={() => { void saveProducts(); }}>{saving ? "Saving…" : translateInterfaceText("Save", language)} <Check size={18} /></button></div> : <button type="button" className="product-edit-action product-edit-add" onClick={() => setEditingProducts(true)}><Plus size={18} /> {updateLabel}</button>}</section>}
      {section === "brands" && <section className="content-card product-actions-panel on-demand-brand-panel"><div className="section-row"><div><h2>{formatDemandMonthHeading(activeWishlist.month, brandsLabel)}</h2><p>{translateInterfaceText(activeWishlist.frequency, language)} · {activeWishlist.spendRange}</p></div></div>{editingBrands ? <BrandSelector selected={activeWishlist.brands} hideSearchHeader variant="desktop" onChange={async (brands) => { if (await persist(activeWishlist, { brands })) notify("Monthly brand choices saved"); }} /> : <div className="saved-brand-list">{activeWishlist.brands.map((brand) => { const imageUrl = brandCatalogue.find((item) => item.name.toLocaleLowerCase() === brand.toLocaleLowerCase())?.imageUrl; return <div key={brand}>{imageUrl ? <BrandImage src={imageUrl} alt={`${brand} product`} loading="lazy" decoding="async" /> : <span className="saved-brand-list__fallback" aria-hidden="true">{brand.slice(0, 1).toUpperCase()}</span>}<span><strong>{brand}</strong><small>Selected brand</small></span></div>; })}</div>}{editingBrands ? <div className="demand-edit-actions"><button type="button" className="product-edit-action demand-delete-action" disabled={saving} aria-label="Delete this monthly demand" onClick={() => setDeleteTarget(activeWishlist)}><Trash2 size={19} /></button><button type="button" className="product-edit-action product-edit-done" disabled={saving} onClick={() => setEditingBrands(false)}>{translateInterfaceText("Save", language)} <Check size={18} /></button></div> : <button type="button" className="product-edit-action product-edit-add" onClick={() => setEditingBrands(true)}><Plus size={18} /> {updateLabel}</button>}</section>}
      {section === "categories" && <section className="content-card"><div className="section-row"><div><h2>Your product category</h2><p>Automatically based on this month’s selected products.</p></div></div>{selectedCategories.length ? <div className="category-grid category-grid--app">{categories.filter((category) => selectedCategories.includes(category.id)).map((category) => { const Icon = category.icon; return <div key={category.id} className="category-option category-option--visual selected"><span className="category-option__visual"><img src={category.image} alt="" /><span className="category-option__icon"><Icon /></span></span><span className="category-option__copy"><strong>{category.label}</strong><small>{category.hint}</small></span></div>; })}</div> : <p className="wishlist-category-empty">Select a product to automatically add its category.</p>}</section>}
      </div>
    </>}
    {deleteTarget && <DeleteMonthlyDemandDialog monthLabel={new Intl.DateTimeFormat("en-MY", { month: "long", year: "numeric", timeZone: "Asia/Kuching" }).format(new Date(`${deleteTarget.month}-01T00:00:00+08:00`))} busy={deleting} onCancel={() => { if (!deleting) setDeleteTarget(null); }} onConfirm={() => { void confirmDelete(); }} />}
  </AppShell>;
}

function Interests({ member, setMember, navigate, notify, onSignOut }: { member: MemberState; setMember: MemberSetter; navigate: Navigate; notify: (message: string) => void; onSignOut: () => Promise<void> }) {
  const productMap = useProductCatalog();
  const { language } = useContext(InterfaceLanguageContext);
  const tr = (value: string) => translateInterfaceText(value, language);
  const localizedFrequencyLabels = Object.fromEntries(productFrequencyOptions.map((frequency) => [frequency, tr(frequency)])) as Record<ProductFrequency, string>;
  const wishlistTitle = translateInterfaceText("My Wishlists", language);
  const wishlistSubtitle = translateInterfaceText("Regular products and brands.", language);
  const productsLabel = translateInterfaceText("products", language);
  const brandsLabel = translateInterfaceText("brands", language);
  const categoriesLabel = translateInterfaceText("categories", language);
  const purchaseFrequencyHeading = translateInterfaceText("How often do I buy", language);
  const productSearchPlaceholder = translateInterfaceText("Search or add a product", language);
  const favouriteBrandsHeading = translateInterfaceText("My favourite brands", language);
  const brandSearchPlaceholder = translateInterfaceText("Search or add a brand", language);
  const yourBrandsHeading = translateInterfaceText("My brand lists", language);
  const productCategoryHeading = translateInterfaceText("My product category", language);
  const productCategorySubtitle = translateInterfaceText("Listed based on my wishlists product", language);
  const [editingProducts, setEditingProducts] = useState(false);
  const [editingBrands, setEditingBrands] = useState(false);
  const [productQuery, setProductQuery] = useState("");
  const [brandQuery, setBrandQuery] = useState("");
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const [selectedBrand, setSelectedBrand] = useState<string | null>(null);
  const [brandCatalogue, setBrandCatalogue] = useState<BrandSuggestion[]>([]);
  const [brandProductNames, setBrandProductNames] = useState<Map<string, string[]>>(() => new Map());
  const [savingInterests, setSavingInterests] = useState(false);
  const [interestSection, setInterestSection] = useState<"categories" | "products" | "brands">(takeRequestedWishlistSection);
  useEffect(() => {
    if (interestSection !== "products" || editingProducts) return;
    const cards = Array.from(document.querySelectorAll<HTMLElement>("#interest-panel .saved-product-list > div"));
    const cleanups = cards.map((card) => {
      const open = () => {
        const visibleName = card.querySelector("strong")?.textContent?.trim();
        const product = member.products.find((candidate) => tr(candidate) === visibleName) ?? visibleName;
        if (product) setSelectedProduct(product);
      };
      const keydown = (event: KeyboardEvent) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); open(); } };
      card.setAttribute("role", "button"); card.setAttribute("tabindex", "0"); card.classList.add("product-detail-trigger");
      card.addEventListener("click", open); card.addEventListener("keydown", keydown);
      return () => { card.removeEventListener("click", open); card.removeEventListener("keydown", keydown); };
    });
    return () => cleanups.forEach((cleanup) => cleanup());
  }, [editingProducts, interestSection, language, member.products, member.productFrequencies, productQuery]);
  useEffect(() => {
    if (interestSection !== "brands") return;
    let current = true;
    void Promise.all([loadBrandSuggestions("", 500), loadCatalogProductBrandOptions()]).then(([suggestions, links]) => {
      if (!current) return;
      setBrandCatalogue(suggestions);
      const productsByBrand = new Map<string, string[]>();
      links.forEach((link) => {
        const key = link.brandName.toLocaleLowerCase();
        const products = productsByBrand.get(key) ?? [];
        if (!products.some((product) => product.toLocaleLowerCase() === link.productName.toLocaleLowerCase())) products.push(link.productName);
        productsByBrand.set(key, products);
      });
      setBrandProductNames(productsByBrand);
    }).catch(() => {
      if (!current) return;
      setBrandCatalogue(popularBrandNames.map((name) => ({ name, memberCount: 0, source: "popular" as const, imageUrl: "", storagePath: "" })));
      setBrandProductNames(new Map());
    });
    return () => { current = false; };
  }, [interestSection, language]);
  const persistInterests = async (successMessage: string) => {
    setSavingInterests(true);
    try {
      await saveCurrentMemberInterests(member);
      notify(successMessage);
      return true;
    } catch (error) {
      notify(error instanceof Error ? error.message : "Unable to save your wishlist. Please try again.");
      return false;
    } finally {
      setSavingInterests(false);
    }
  };
  const catalogueProducts = Array.from(new Set(Object.values(productMap).flat()));
  const availableProducts = Array.from(new Set([...catalogueProducts, ...member.products]));
  const normalizedProductQuery = productQuery.trim().toLocaleLowerCase();
  const filteredAvailableProducts = availableProducts.filter((product) => product.toLocaleLowerCase().includes(normalizedProductQuery));
  const unselectedAvailableProducts = filteredAvailableProducts.filter((product) => !member.products.includes(product));
  const suggestedProducts = catalogueProducts.filter((product) => product.toLocaleLowerCase().includes(normalizedProductQuery) && !member.products.includes(product)).slice(0, 6);
  const exactProductMatch = availableProducts.some((product) => product.toLocaleLowerCase() === normalizedProductQuery);
  const validCustomProduct = productQuery.trim().length >= 2 && /^[\p{L}\p{N}][\p{L}\p{N}&+'’.\- ]*$/u.test(productQuery.trim());
  const selectedProducts = member.products.filter((product) => availableProducts.includes(product));
  const availableBrands: BrandSuggestion[] = brandCatalogue;
  const normalizedBrandQuery = brandQuery.trim().toLocaleLowerCase();
  const filteredAvailableBrands = availableBrands.filter((brand) =>
    brand.name.toLocaleLowerCase().includes(normalizedBrandQuery)
    && !member.brands.some((selected) => selected.toLocaleLowerCase() === brand.name.toLocaleLowerCase())
  );
  const suggestedBrands = filteredAvailableBrands.slice(0, 6);
  const exactBrandMatch = availableBrands.some((brand) => brand.name.toLocaleLowerCase() === normalizedBrandQuery);
  const validBrandSuggestion = brandQuery.trim().length >= 2 && /^[\p{L}\p{N}][\p{L}\p{N}&+'’\.\- ]*$/u.test(brandQuery.trim());
  const selectedBrands = member.brands.filter((brand) => brand.toLocaleLowerCase().includes(normalizedBrandQuery));
  const brandDetails = (name: string) => availableBrands.find((brand) => brand.name.toLocaleLowerCase() === name.toLocaleLowerCase());
  const categoriesForProducts = (products: string[]) => Object.keys(productMap).filter((category) => products.some((product) => productMap[category]?.includes(product)));
  const selectedCategories = categoriesForProducts(selectedProducts);
  const toggleProduct = (product: string) => {
    product = product.trim();
    if (!product) return;
    const selected = member.products.includes(product);
    const nextProducts = selected ? member.products.filter((value) => value !== product) : [...member.products, product];
    const nextFrequencies = { ...member.productFrequencies };
    if (selected) delete nextFrequencies[product];
    else nextFrequencies[product] = getProductFrequency(member.productFrequencies, product, member.frequency);
    setMember({
      ...member,
      categories: categoriesForProducts(nextProducts),
      products: nextProducts,
      productFrequencies: nextFrequencies,
    });
    notify(selected ? `${product} removed` : `${product} added`);
  };
  const addSearchedProduct = async (product = productQuery.trim()) => {
    if (!product) {
      setEditingProducts(true);
      return;
    }
    if (member.products.includes(product)) {
      setProductQuery("");
      return;
    }
    const previous = member;
    const nextProducts = [...member.products, product];
    const nextMember = {
      ...member,
      categories: categoriesForProducts(nextProducts),
      products: nextProducts,
      productFrequencies: { ...member.productFrequencies, [product]: getProductFrequency(member.productFrequencies, product, member.frequency) },
    };
    setMember(nextMember);
    setProductQuery("");
    try {
      await saveCurrentMemberInterests(nextMember);
      notify(`${product} added`);
    } catch (error) {
      setMember(previous);
      notify(error instanceof Error ? error.message : "Unable to add this product. Please try again.");
    }
  };
  const updateSelectedProductFrequency = async (frequency: ProductFrequency) => {
    if (!selectedProduct) return;
    const previous = member;
    const current = member.productFrequencies[selectedProduct];
    const nextFrequencies = { ...member.productFrequencies };
    if (current === frequency) delete nextFrequencies[selectedProduct];
    else nextFrequencies[selectedProduct] = frequency;
    const nextMember = { ...member, productFrequencies: nextFrequencies };
    setMember(nextMember);
    try {
      await saveCurrentMemberInterests(nextMember);
      notify(current === frequency ? `${selectedProduct} frequency cleared` : `${selectedProduct} set to ${frequency.toLowerCase()}`);
    } catch (error) {
      setMember(previous);
      notify(error instanceof Error ? error.message : "Unable to update this product.");
    }
  };
  const removeSelectedProduct = async () => {
    if (!selectedProduct) return;
    const product = selectedProduct;
    const previous = member;
    const nextProducts = member.products.filter((value) => value !== product);
    const nextFrequencies = { ...member.productFrequencies };
    delete nextFrequencies[product];
    const nextMember = { ...member, products: nextProducts, categories: categoriesForProducts(nextProducts), productFrequencies: nextFrequencies };
    setMember(nextMember);
    setSelectedProduct(null);
    try {
      await saveCurrentMemberInterests(nextMember);
      notify(`${product} removed`);
    } catch (error) {
      setMember(previous);
      notify(error instanceof Error ? error.message : "Unable to remove this product.");
    }
  };
  const selectProductFrequency = (product: string, frequency: ProductFrequency) => {
    if (!availableProducts.includes(product)) return;
    const nextProducts = member.products.includes(product) ? member.products : [...member.products, product];
    setMember({
      ...member,
      categories: categoriesForProducts(nextProducts),
      products: nextProducts,
      productFrequencies: { ...member.productFrequencies, [product]: frequency },
    });
    notify(`${product} set to ${frequency.toLowerCase()}`);
  };
  const productsByFrequency = productFrequencyOptions.map((frequency) => ({
    frequency,
    products: selectedProducts.filter((product) => getProductFrequency(member.productFrequencies, product, member.frequency) === frequency && product.toLocaleLowerCase().includes(normalizedProductQuery)),
  })).filter((group) => group.products.length > 0);
  const confirmProductEditing = async () => {
    if (await persistInterests("Product choices saved")) setEditingProducts(false);
  };
  const toggleBrand = (value: string) => {
    const brand = normalizeBrandName(value);
    if (!brand) return;
    const selected = member.brands.some((name) => name.toLocaleLowerCase() === brand.toLocaleLowerCase());
    const nextBrandFrequencies = { ...member.brandFrequencies };
    if (selected) {
      Object.keys(nextBrandFrequencies).forEach((name) => {
        if (name.toLocaleLowerCase() === brand.toLocaleLowerCase()) delete nextBrandFrequencies[name];
      });
    } else {
      nextBrandFrequencies[brand] = getProductFrequency(member.brandFrequencies, brand, member.frequency);
    }
    setMember({
      ...member,
      brands: selected
        ? member.brands.filter((name) => name.toLocaleLowerCase() !== brand.toLocaleLowerCase())
        : [...member.brands, brand],
      brandFrequencies: nextBrandFrequencies,
    });
    notify(selected ? `${brand} removed` : `${brand} added`);
  };
  const addSearchedBrand = async (value = brandQuery) => {
    const requestedBrand = normalizeBrandName(value);
    if (!requestedBrand) {
      setEditingBrands(true);
      return;
    }
    const listedBrand = availableBrands.find((item) => item.name.toLocaleLowerCase() === requestedBrand.toLocaleLowerCase());
    if (!listedBrand) {
      notify("Choose a brand listed in the product catalogue");
      return;
    }
    const brand = listedBrand.name;
    if (member.brands.some((name) => name.toLocaleLowerCase() === brand.toLocaleLowerCase())) {
      setBrandQuery("");
      return;
    }
    const previous = member;
    const nextMember = { ...member, brands: [...member.brands, brand], brandFrequencies: { ...member.brandFrequencies, [brand]: getProductFrequency(member.brandFrequencies, brand, member.frequency) } };
    setMember(nextMember);
    setBrandQuery("");
    try {
      await saveCurrentMemberInterests(nextMember);
      notify(`${brand} added`);
    } catch (error) {
      setMember(previous);
      notify(error instanceof Error ? error.message : "Unable to add this brand. Please try again.");
    }
  };
  const suggestSearchedBrand = async () => {
    const brand = normalizeBrandName(brandQuery);
    if (!validBrandSuggestion || exactBrandMatch || !brand) return;
    const previous = member;
    try {
      await submitBrandSuggestion(brand);
      const nextMember = member.brands.some((name) => name.toLocaleLowerCase() === brand.toLocaleLowerCase())
        ? member
        : { ...member, brands: [...member.brands, brand], brandFrequencies: { ...member.brandFrequencies, [brand]: getProductFrequency(member.brandFrequencies, brand, member.frequency) } };
      setMember(nextMember);
      await saveCurrentMemberInterests(nextMember);
      setBrandQuery("");
      notify(tr("Brand submitted and added to your list. It is under review."));
    } catch (error) {
      setMember(previous);
      notify(error instanceof Error ? error.message : "Unable to submit this brand suggestion.");
    }
  };
  const updateSelectedBrandFrequency = async (frequency: ProductFrequency) => {
    if (!selectedBrand) return;
    const previous = member;
    const current = member.brandFrequencies[selectedBrand];
    const nextFrequencies = { ...member.brandFrequencies };
    if (current === frequency) delete nextFrequencies[selectedBrand];
    else nextFrequencies[selectedBrand] = frequency;
    const nextMember = { ...member, brandFrequencies: nextFrequencies };
    setMember(nextMember);
    try {
      await saveCurrentMemberInterests(nextMember);
      notify(current === frequency ? `${selectedBrand} frequency cleared` : `${selectedBrand} set to ${frequency.toLowerCase()}`);
    } catch (error) {
      setMember(previous);
      notify(error instanceof Error ? error.message : "Unable to update this brand.");
    }
  };
  const removeSelectedBrand = async () => {
    if (!selectedBrand) return;
    const brand = selectedBrand;
    const previous = member;
    const nextBrandFrequencies = { ...member.brandFrequencies };
    Object.keys(nextBrandFrequencies).forEach((name) => { if (name.toLocaleLowerCase() === brand.toLocaleLowerCase()) delete nextBrandFrequencies[name]; });
    const nextMember = { ...member, brands: member.brands.filter((name) => name.toLocaleLowerCase() !== brand.toLocaleLowerCase()), brandFrequencies: nextBrandFrequencies };
    setMember(nextMember);
    setSelectedBrand(null);
    try {
      await saveCurrentMemberInterests(nextMember);
      notify(`${brand} removed`);
    } catch (error) {
      setMember(previous);
      notify(error instanceof Error ? error.message : "Unable to remove this brand.");
    }
  };
  const confirmBrandEditing = async () => {
    if (await persistInterests("Brand choices saved")) setEditingBrands(false);
  };
  return <>
    <AppShell member={member} navigate={navigate} active="interests" onSignOut={onSignOut}>
      <div className="interests-sticky-masthead">
        <section className="dashboard-heading compact"><div><h1 className="member-page-title" tabIndex={-1}>{wishlistTitle}</h1><p>{wishlistSubtitle}</p></div></section>
        <section className="interest-summary interest-summary--brands" role="tablist" aria-label={tr("Interest sections")}>
          <button id="interest-tab-products" type="button" role="tab" aria-selected={interestSection === "products"} aria-controls="interest-panel" onClick={() => setInterestSection("products")}><span>{selectedProducts.length}</span><p>{productsLabel}</p></button>
          <button id="interest-tab-brands" type="button" role="tab" aria-selected={interestSection === "brands"} aria-controls="interest-panel" onClick={() => setInterestSection("brands")}><span>{member.brands.length}</span><p>{brandsLabel}</p></button>
          <button id="interest-tab-categories" type="button" role="tab" aria-selected={interestSection === "categories"} aria-controls="interest-panel" onClick={() => setInterestSection("categories")}><span>{selectedCategories.length}</span><p>{categoriesLabel}</p></button>
        </section>
        {interestSection === "products" && <div className="product-sticky-controls">
          <div className="section-row"><div><h2>{purchaseFrequencyHeading}</h2></div></div>
          <div className="product-search-row">
            <label className="brand-search product-search"><Search aria-hidden="true" /><input type="search" value={productQuery} onChange={(event) => setProductQuery(event.target.value)} placeholder={productSearchPlaceholder} aria-label={productSearchPlaceholder} autoComplete="off" />{productQuery && <button type="button" onClick={() => setProductQuery("")} aria-label={tr("Clear product search")}><X /></button>}</label>
            <button type="button" className={`product-add-icon ${editingProducts ? "product-add-icon--confirm" : ""}`} disabled={savingInterests} aria-label={editingProducts ? tr("Save and confirm product choices") : productQuery.trim() ? `${tr("Add")} ${tr(productQuery.trim())}` : tr("Open product editor")} onClick={() => { if (editingProducts) void confirmProductEditing(); else void addSearchedProduct(); }}>{editingProducts ? <Check /> : <Plus />}</button>
          </div>
          {!editingProducts && productQuery && <div className="product-search-suggestions" aria-live="polite">{suggestedProducts.map((product) => <button type="button" key={product} onClick={() => addSearchedProduct(product)}><img src={getProductImage(product)} alt="" /><span><strong>{tr(product)}</strong><small>{tr("Add existing product")}</small></span><Plus /></button>)}{validCustomProduct && !exactProductMatch && <button type="button" className="product-search-suggestion--custom" onClick={() => addSearchedProduct()}><Package /><span><strong>{tr("Add")} “{productQuery.trim()}”</strong><small>{tr("Suggest a new product")}</small></span><Plus /></button>}</div>}
        </div>}
        {interestSection === "brands" && <div className="product-sticky-controls brand-sticky-controls">
          <div className="section-row"><div><h2>{favouriteBrandsHeading}</h2></div></div>
          <div className="product-search-row">
            <label className="brand-search product-search"><Search aria-hidden="true" /><input type="search" value={brandQuery} onChange={(event) => setBrandQuery(event.target.value)} placeholder={brandSearchPlaceholder} aria-label={brandSearchPlaceholder} autoComplete="off" />{brandQuery && <button type="button" onClick={() => setBrandQuery("")} aria-label={tr("Clear brand search")}><X /></button>}</label>
            <button type="button" className={`product-add-icon ${editingBrands ? "product-add-icon--confirm" : ""}`} disabled={savingInterests || (!editingBrands && Boolean(brandQuery.trim()) && !exactBrandMatch)} aria-label={editingBrands ? tr("Save and confirm brand choices") : exactBrandMatch ? `${tr("Add")} ${brandQuery.trim()}` : brandQuery.trim() ? tr("Choose a listed brand") : tr("Open brand editor")} onClick={() => { if (editingBrands) void confirmBrandEditing(); else void addSearchedBrand(); }}>{editingBrands ? <Check /> : <Plus />}</button>
          </div>
          {!editingBrands && brandQuery && <div className="product-search-suggestions brand-search-suggestions" aria-live="polite">{suggestedBrands.map((brand) => <button type="button" key={brand.name} onClick={() => addSearchedBrand(brand.name)}>{brand.imageUrl ? <BrandImage src={brand.imageUrl} alt={`${brand.name} product`} loading="lazy" decoding="async" /> : <span className="brand-search-suggestions__fallback" aria-hidden="true">{brand.name.slice(0, 1).toUpperCase()}</span>}<span><strong>{brand.name}</strong><small>Add listed brand</small></span><Plus /></button>)}{validBrandSuggestion && !exactBrandMatch && <button type="button" className="product-search-suggestion--custom" onClick={() => { void suggestSearchedBrand(); }}><Tag /><span><strong>Suggest “{normalizeBrandName(brandQuery)}”</strong><small>Send to Web App Admin for review</small></span><Plus /></button>}{suggestedBrands.length === 0 && !exactBrandMatch && !validBrandSuggestion && <p className="product-search-empty">Enter at least two letters to search or suggest a brand.</p>}</div>}
        </div>}
      </div>
      {interestSection === "categories" && <section id="interest-panel" role="tabpanel" aria-labelledby="interest-tab-categories" className="content-card"><div className="section-row"><div><h2>{productCategoryHeading}</h2><p>{productCategorySubtitle}</p></div></div>{selectedCategories.length ? <div className="category-grid category-grid--app">{categories.filter((category) => selectedCategories.includes(category.id)).map((category) => { const Icon = category.icon; return <div key={category.id} className="category-option category-option--visual selected"><span className="category-option__visual"><img src={category.image} alt="" loading="lazy" decoding="async" /><span className="category-option__icon"><Icon /></span></span><span className="category-option__copy"><strong>{tr(category.label)}</strong><small>{tr(category.hint)}</small></span></div>; })}</div> : <p className="wishlist-category-empty">{tr("Select a product to automatically add its category.")}</p>}</section>}
      {interestSection === "products" && <section id="interest-panel" role="tabpanel" aria-labelledby="interest-tab-products" className="content-card product-actions-panel product-actions-panel--body">{editingProducts && <div id="regular-product-editor" className="product-editor" aria-label={tr("Choose your regular products")}>{unselectedAvailableProducts.length ? unselectedAvailableProducts.map((product) => <div key={product} className="product-editor__row"><button type="button" className="product-editor__check" aria-label={`${tr("Select")} ${tr(product)}`} aria-pressed={false} onClick={() => toggleProduct(product)} /><img src={getProductImage(product)} alt="" loading="lazy" decoding="async" /><strong>{tr(product)}</strong><div className="product-frequency-selector" aria-label={`${tr("How often you buy")} ${tr(product)}`}>{productFrequencyOptions.map((option) => <button type="button" key={option} aria-pressed={false} onClick={() => selectProductFrequency(product, option)}>{localizedFrequencyLabels[option]}</button>)}</div></div>) : <p>{tr(productQuery ? "No products match your search." : "Select at least one category to see its products.")}</p>}</div>}{!editingProducts && <div className="product-frequency-groups">{productsByFrequency.map(({ frequency, products }) => <section key={frequency} className="product-frequency-group"><h3>{localizedFrequencyLabels[frequency]}</h3><div className="saved-product-list">{products.map((product) => <div key={product}><img src={getProductImage(product)} alt={`${tr(product)} ${tr("product")}`} loading="lazy" decoding="async" /><span><strong>{tr(product)}</strong><small>{localizedFrequencyLabels[frequency]}</small></span></div>)}</div></section>)}{productQuery && productsByFrequency.length === 0 && <p className="product-search-empty">{tr("No selected products match your search.")}</p>}</div>}</section>}
      {interestSection === "brands" && <section id="interest-panel" role="tabpanel" aria-labelledby="interest-tab-brands" className="content-card product-actions-panel product-actions-panel--body brand-actions-panel">{editingBrands && <div id="regular-brand-editor" className="product-editor brand-editor" aria-label={tr("Choose your regular brands")}>{filteredAvailableBrands.length ? filteredAvailableBrands.map((brand) => { const selected = member.brands.some((name) => name.toLocaleLowerCase() === brand.name.toLocaleLowerCase()); const linkedProducts = brandProductNames.get(brand.name.toLocaleLowerCase()) ?? []; return <div key={brand.name} className={`product-editor__row brand-editor__row ${selected ? "selected" : ""}`}><button type="button" className="product-editor__check" aria-label={`${tr(selected ? "Unselect" : "Select")} ${brand.name}`} aria-pressed={selected} onClick={() => toggleBrand(brand.name)}>{selected && <Check size={15} />}</button>{brand.imageUrl ? <BrandImage src={brand.imageUrl} alt={`${brand.name} ${tr("product")}`} loading="lazy" decoding="async" /> : <span className="brand-editor__fallback" aria-hidden="true">{brand.name.slice(0, 1).toUpperCase()}</span>}<span className="brand-editor__copy"><strong>{brand.name}</strong><small>{linkedProducts.length > 0 ? linkedProducts.map(tr).join(", ") : tr("No linked products yet")}</small></span></div>; }) : <p>{tr("No brands match your search.")}</p>}</div>}{!editingBrands && <div className="product-frequency-groups brand-groups"><section className="product-frequency-group"><h3>{yourBrandsHeading}</h3>{selectedBrands.length ? <div className="saved-brand-list">{selectedBrands.map((name) => { const brand = brandDetails(name); const linkedProducts = brandProductNames.get(name.toLocaleLowerCase()) ?? []; return <div key={name} className="product-detail-trigger" role="button" tabIndex={0} onClick={() => setSelectedBrand(name)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); setSelectedBrand(name); } }}>{brand?.imageUrl ? <BrandImage src={brand.imageUrl} alt={`${name} ${tr("product")}`} loading="lazy" decoding="async" /> : <span className="saved-brand-list__fallback" aria-hidden="true">{name.slice(0, 1).toUpperCase()}</span>}<span><strong>{name}</strong><small>{brand ? (linkedProducts.length > 0 ? linkedProducts.map(tr).join(", ") : tr("No linked products yet")) : tr("Under review")}</small></span></div>; })}</div> : <p className="product-search-empty">{tr(brandQuery ? "No selected brands match your search." : "Add a brand with the search or plus button above.")}</p>}</section></div>}</section>}
    </AppShell>
    {selectedProduct && <ProductDetailSheet product={selectedProduct} displayName={tr(selectedProduct)} frequency={member.productFrequencies[selectedProduct]} frequencyLabels={localizedFrequencyLabels} labels={{ details: tr("PRODUCT CATEGORY"), frequency: tr("How often do I buy this?"), remove: tr("Remove product"), close: tr("Close product details") }} onClose={() => setSelectedProduct(null)} onToggleFrequency={updateSelectedProductFrequency} onRemove={removeSelectedProduct} />}
    {selectedBrand && <BrandDetailSheet brand={selectedBrand} imageUrl={brandDetails(selectedBrand)?.imageUrl} frequency={getProductFrequency(member.brandFrequencies, selectedBrand, member.frequency)} frequencyLabels={localizedFrequencyLabels} labels={{ details: tr("BRAND DETAILS"), frequency: tr("How often do I buy this brand?"), remove: tr("Remove brand"), close: tr("Close brand details"), enlarge: `${tr("Enlarge")} ${selectedBrand} ${tr("image")}`, closeImage: tr("Close enlarged image") }} onClose={() => setSelectedBrand(null)} onToggleFrequency={updateSelectedBrandFrequency} onRemove={removeSelectedBrand} />}
  </>;
}

function ProDiamondIcon() {
  return (
    <svg className="pro-diamond-icon" viewBox="0 0 80 72" fill="none" aria-hidden="true">
      <path d="M15 26h50l11 12-36 30L4 38l11-12Z" />
      <path d="M4 38h72M27 26l-9 12 22 30 22-30-9-12M40 14V4M22 18l-6-9M58 18l6-9M10 23l-7-6M70 23l7-6" />
    </svg>
  );
}

function Opportunity({ member, navigate, onSignOut }: { member: MemberState; navigate: Navigate; onSignOut: () => Promise<void> }) {
  const needsPlanUpgrade = getMembershipTier(member) === "Basic";
  return (
    <AppShell member={member} navigate={navigate} active="opportunities" onSignOut={onSignOut}>
      <button className="back-link" onClick={() => navigate("/home")}><ArrowLeft size={17} /> Back to home</button>
      <section className="content-card live-empty-page">{needsPlanUpgrade ? <ProDiamondIcon /> : <BriefcaseBusiness />}<h1 className="member-page-title">{needsPlanUpgrade ? "A Pro or Premium plan is required to access Opportunities info." : "No live opportunities yet."}</h1><p>{needsPlanUpgrade ? "Your membership plan is managed by Kitanego." : "When a verified supplier opportunity matches your buying signal, it will appear here. Kitanego does not show sample offers as live data."}</p><button type="button" className="button button--ink" onClick={() => navigate("/home")}>Return to home</button></section>
    </AppShell>
  );

  return (
    <AppShell member={member} navigate={navigate} active="opportunities" onSignOut={onSignOut}>
      <button className="back-link" onClick={() => navigate("/home")}><ArrowLeft size={17} /> Back to home</button>
      <section className="opportunity-hero">
        <div className="opportunity-detail-visual"><div className="grain grain--a" /><div className="grain grain--b" /><div className="grain grain--c" /><div className="large-rice-bag"><Leaf /><strong>Everyday<br />rice</strong><span>10kg</span><small>Prototype product</small></div></div>
        <div className="opportunity-detail-copy"><div className="title-badges"><span className="pilot-badge">Pilot opportunity</span><span className="sample-label">Illustrative</span></div><h1 className="member-page-title" tabIndex={-1}>Everyday rice · 10kg</h1><p className="opportunity-lede">Help demonstrate credible demand for a household staple. A supplier discussion may follow if enough verified members record interest.</p><div className="fact-grid"><div><Clock3 /><span><small>Interest closes</small><strong>30 November 2026</strong></span></div><div><MapPin /><span><small>Area</small><strong>Greater Kota Kinabalu</strong></span></div><div><Package /><span><small>Limit</small><strong>Up to 3 units</strong></span></div><div><ShieldCheck /><span><small>Today</small><strong>No payment</strong></span></div></div><div className="detail-progress"><div><span>1,026 units expressed</span><strong>Target 1,500</strong></div><div className="progress-track"><i style={{ width: "68.4%" }} /></div><small>Prototype progress based on illustrative data</small></div></div>
      </section>
      <section className="opportunity-layout">
        <div className="opportunity-body">
          <article className="content-card"><p className="eyebrow">What this means</p><h2>A signal of intent—not a purchase.</h2><div className="meaning-grid"><div><CheckCircle2 /><span><strong>You choose a quantity</strong>It helps measure the number of units members may want.</span></div><div><CheckCircle2 /><span><strong>Your phone is verified</strong>One mobile number helps protect the credibility of the count.</span></div><div><X /><span><strong>No payment or order</strong>Nothing is charged, reserved, delivered or guaranteed in this prototype.</span></div></div></article>
          <article className="content-card supplier-preview"><div><p className="eyebrow">Supplier evidence preview</p><h2>Only the combined picture.</h2><p>Suppliers would see aggregate evidence such as:</p></div><div className="evidence-paper"><div><span>Verified interest</span><strong>742 members</strong></div><div><span>Potential demand</span><strong>1,026 units</strong></div><div><span>Top area</span><strong>Kota Kinabalu · aggregated</strong></div><small>Names, emails and phone numbers excluded</small></div></article>
        </div>
        <aside className="commit-panel"><span className="status-chip"><i /> {member.verified ? "VERIFIED" : "REGISTERED"}</span><h2>{member.committedQuantity ? "Your interest is recorded" : "Add your signal to this pilot"}</h2>{member.committedQuantity ? <><div className="committed-quantity"><CheckCircle2 /><span><strong>{member.committedQuantity} × 10kg</strong>Committed to this pilot only</span></div><button className="button button--ghost button--full" onClick={() => navigate("/commit")}>View commitment</button></> : <><p>{member.verified ? "You’re verified and ready to record a quantity." : "Verify your phone only when you choose to participate."}</p><button className="button button--lime button--full button--large" onClick={() => navigate(member.verified ? "/commit" : "/verify")}>{member.verified ? "Choose quantity" : "Verify and record interest"} <ArrowRight size={18} /></button></>}<small><LockKeyhole size={13} /> Your contact details are not shared with suppliers.</small></aside>
      </section>
    </AppShell>
  );
}

function Verify({ member, setMember, navigate, notify }: { member: MemberState; setMember: MemberSetter; navigate: Navigate; notify: (message: string) => void }) {
  const [phone, setPhone] = useState(member.mobileNumber || "+60 ");
  const [consent, setConsent] = useState(member.mobileNotificationsConsent);
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);

  const saveMobile = async (event: FormEvent) => {
    event.preventDefault();
    setPending(true);
    setError("");
    try {
      const normalizedPhone = await updateCurrentMemberMobileContact(phone, consent);
      setMember({ ...member, mobileNumber: normalizedPhone, mobileNotificationsConsent: consent, verified: true });
      notify("Mobile contact verified.");
      navigate("/home");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "We could not save your mobile number. Try again.");
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="verification-page">
      <header className="onboarding-header"><Logo wordmark /><PrototypeBadge /><button className="icon-text-button desktop-header-action" onClick={() => navigate("/home")}><X size={18} /> Close</button><StandardMobileMenu navigate={navigate} member={member} /></header>
      <main className="verification-main verification-main--single">
        <section className="verification-card">
          <form onSubmit={saveMobile} className="form-stack"><h2>Mobile phone details</h2><p>Enter you mobile number:</p><label className="field-label">Mobile number<div className="phone-field"><span>MY</span><input value={phone} onChange={(event) => { setPhone(event.target.value); setError(""); }} autoComplete="tel" inputMode="tel" placeholder="+60 12 345 6789" /></div></label><label className="notification-consent"><input type="checkbox" checked={consent} onChange={(event) => { setConsent(event.target.checked); setError(""); }} /><span><strong>Allow Kitanego notifications</strong>I agree that Kitanego may use this number for account and opportunity notifications. I can withdraw this consent at any time.</span></label>{error && <p className="field-error" role="alert">{error}</p>}<button className="button button--ink button--full button--large" disabled={pending || phone.replace(/\D/g, "").length < 10 || !consent}>{pending ? "Saving…" : "Save mobile number"} <ArrowRight size={18} /></button><button type="button" className="text-button" onClick={() => navigate("/home")} disabled={pending}>Not now</button></form>
        </section>
      </main>
    </div>
  );
}

function Commit({ member, setMember, navigate, onSignOut }: { member: MemberState; setMember: MemberSetter; navigate: Navigate; onSignOut: () => Promise<void> }) {
  return <Opportunity member={member} navigate={navigate} onSignOut={onSignOut} />;

  const [quantity, setQuantity] = useState(member.committedQuantity || 1);
  const [complete, setComplete] = useState(member.committedQuantity > 0);
  const confirm = () => { setMember({ ...member, committedQuantity: quantity, verified: true }); setComplete(true); };

  return (
    <div className="commit-page">
      <header className="onboarding-header"><Logo wordmark /><PrototypeBadge /><button className="icon-text-button desktop-header-action" onClick={() => navigate("/opportunity")}><X size={18} /> Close</button><StandardMobileMenu navigate={navigate} member={member} /></header>
      <main className="commit-main">
        {!complete ? <section className="commit-card"><span className="success-icon success-icon--small"><Package /></span><span className="pilot-badge">Pilot interest test</span><h1 tabIndex={-1}>How many would you consider?</h1><p>Choose a quantity to help make the demand signal more useful.</p><div className="commit-product"><div className="small-rice-bag"><Leaf /><strong>Everyday rice</strong><small>10kg · prototype</small></div><div><span>Maximum 3 units</span><div className="quantity-stepper"><button onClick={() => setQuantity(Math.max(1, quantity - 1))} aria-label="Decrease quantity"><Minus /></button><strong>{quantity}</strong><button onClick={() => setQuantity(Math.min(3, quantity + 1))} aria-label="Increase quantity"><Plus /></button></div></div></div><div className="intent-disclosure"><ShieldCheck /><span><strong>Intent only—no payment and no order.</strong>You can withdraw this expression of interest before the pilot closes on 30 November 2026.</span></div><button className="button button--lime button--full button--large" onClick={confirm}>Count me in for {quantity} {quantity === 1 ? "unit" : "units"} <ArrowRight size={18} /></button><button className="text-button" onClick={() => navigate("/opportunity")}>Not now</button></section> : <section className="success-card"><div className="celebration-orbit"><span className="success-icon"><Check /></span><i /><i /><i /></div><span className="deal-status">COMMITTED · THIS PILOT ONLY</span><h1 tabIndex={-1}>You’re counted—<em>not charged.</em></h1><p>Your interest in <strong>{member.committedQuantity || quantity} × 10kg bags</strong> has been recorded. Your account remains VERIFIED; this commitment belongs only to this pilot.</p><div className="receipt"><div><span>Opportunity</span><strong>Everyday rice · 10kg</strong></div><div><span>Quantity</span><strong>{member.committedQuantity || quantity}</strong></div><div><span>Payment</span><strong>None</strong></div><div><span>Status</span><strong>Expression of intent</strong></div></div><div className="success-actions"><button className="button button--ink button--large" onClick={() => navigate("/home")}>Back to home</button><button className="button button--ghost button--large" onClick={() => navigate("/opportunity")}>View opportunity</button></div></section>}
      </main>
    </div>
  );
}

function MemberAvatar({ member, className = "" }: { member: Pick<MemberState, "firstName" | "lastName" | "avatarUrl">; className?: string }) {
  const initials = [member.firstName, member.lastName].filter(Boolean).map((part) => part.charAt(0)).join("").slice(0, 2).toUpperCase() || "M";
  return <span className={`member-avatar ${className}`} aria-hidden="true"><span>{initials}</span>{member.avatarUrl && <img src={member.avatarUrl} alt="" referrerPolicy="no-referrer" onError={(event) => { event.currentTarget.style.display = "none"; }} />}</span>;
}

function formatRegisteredSince(value: string) {
  if (!value) return "Not available";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not available";
  return new Intl.DateTimeFormat("en-MY", { day: "numeric", month: "long", year: "numeric" }).format(date);
}

function Profile({ member, setMember, navigate, notify, onSignOut }: { member: MemberState; setMember: MemberSetter; navigate: Navigate; notify: (message: string) => void; onSignOut: () => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const memberName = [member.firstName, member.lastName].filter(Boolean).join(" ") || "Member";
  return (
    <AppShell member={member} navigate={navigate} active="profile" onSignOut={onSignOut}>
      <section className="dashboard-heading compact"><div><h1 className="member-page-title" tabIndex={-1}>User Details</h1><p>Manage your member information and account details.</p></div></section>
      <section className="profile-grid profile-grid--single"><article className="content-card profile-card"><MemberAccountMenu member={member} navigate={navigate} onSignOut={onSignOut} profileTrigger /><h2>{memberName}</h2><p>{member.email}</p><div className="profile-status"><span className="status-chip"><i /> MEMBER</span>{member.committedQuantity > 0 && <span className="deal-status">INTEREST RECORDED</span>}</div><dl><div><dt>District</dt><dd>{member.district}</dd></div><div><dt>Status</dt><dd>Member</dd></div><div><dt>Mobile number</dt><dd>{member.mobileNumber || "Not added"}{member.mobileNumber && <small className="profile-unverified">{member.verified ? "Verified" : "Unverified"}</small>}</dd></div><div><dt>Notifications</dt><dd>{member.mobileNotificationsConsent ? "Allowed" : "Not allowed"}</dd></div><div><dt>Registered since</dt><dd>{formatRegisteredSince(member.registeredAt)}</dd></div></dl><button className="button button--ghost button--full" onClick={() => setEditing(true)}>Edit profile</button></article></section>
      {editing && <ProfileEditor member={member} districts={districts} onClose={() => setEditing(false)} onSaved={(patch) => setMember((current) => ({ ...current, ...patch }))} notify={notify} />}
    </AppShell>
  );
}

function MemberInformationPage({ kind, member, navigate, onSignOut }: { kind: "privacy" | "help"; member: MemberState; navigate: Navigate; onSignOut: () => Promise<void> }) {
  const privacy = kind === "privacy";
  const { opportunitiesEnabled } = useFeatureAvailability();
  return <AppShell member={member} navigate={navigate} active="profile" onSignOut={onSignOut}>
    <button className="back-link" type="button" onClick={() => navigate("/profile")}><ArrowLeft size={18} /> Back to Profile</button>
    <section className="dashboard-heading compact"><div><p className="eyebrow">Your account</p><h1 className="member-page-title" tabIndex={-1}>{privacy ? "Privacy & security" : "Help centre"}</h1><p>{privacy ? "How Kitanego protects and uses your member information." : "Guidance and support for using your Kitanego account."}</p></div></section>
    <section className="member-information-grid">
      {(privacy ? [
        ["Your information", "Your profile details, wishlist selections and district help Kitanego understand grouped community demand."],
        ["Protected reporting", "Supplier reports use grouped totals and do not include your name, email address, mobile number or precise coordinates."],
        ["Account security", "Keep your sign-in details private. Sign out when using a shared device and review your profile if information changes."],
        ["Your choices", "You can update your profile and mobile notification preference at any time from your Profile page."],
      ] : [
        ["Managing your profile", "Open Profile and select Personal details to update your name, district, mobile contact or profile photo."],
        ["Updating your wishlists", "Use Wishlists to choose product categories, products and favourite brands that reflect your current needs."],
        ...(opportunitiesEnabled ? [["Membership plans", "Opportunity information is available on Pro and Premium plans. Membership plans are managed by Kitanego administrators."]] : []),
        ["Need more help?", "Email support@kitanego.com and include a short description of the issue. Do not send passwords or verification codes."],
      ]).map(([title, body]) => <article className="content-card member-information-card" key={title}><h2>{title}</h2><p>{body}</p></article>)}
    </section>
  </AppShell>;
}

function AdminPreview({ activeView, settingsSection, member, profiles, profilesStatus, adminEmail, adminRole, adminLastLoginAt, navigate, notify, deploymentState, colorTheme, onColorThemeChange, latestNews, latestNewsSyncStatus, onLatestNewsChange, deploymentSyncStatus = "ready", onDeploymentModeChange, onSignOut }: { activeView: AdminView; settingsSection?: SettingsSection; member: MemberState; profiles: LiveMemberProfile[]; profilesStatus: "idle" | "loading" | "ready" | "error"; adminEmail: string; adminRole: AdminRole | null; adminLastLoginAt: string | null; navigate: Navigate; notify: (message: string) => void; deploymentState: DeploymentState; colorTheme: ColorTheme; onColorThemeChange: (theme: ColorTheme) => void; latestNews: LatestNews | null; latestNewsSyncStatus: LatestNewsSyncStatus; onLatestNewsChange: (news: LatestNews | null) => void; deploymentSyncStatus?: DeploymentSyncStatus; onDeploymentModeChange: (mode: DeploymentMode) => Promise<void>; onSignOut: () => Promise<void> }) {
  const { opportunitiesEnabled } = useFeatureAvailability();
  const period = "Last 30 days";
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [brandNotifications, setBrandNotifications] = useState<ManagedBrandSuggestion[]>([]);
  const [notificationStatus, setNotificationStatus] = useState<"loading" | "ready" | "error">("loading");
  const mobileNavRef = useRef<HTMLElement>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const notificationRef = useRef<HTMLDivElement>(null);
  const isSystemAdmin = adminRole === "system_admin";
  const matchingAdminProfile = profiles.find((profile) => profile.email.trim().toLowerCase() === adminEmail.trim().toLowerCase());
  const adminAvatarUrl = matchingAdminProfile?.avatarUrl || (member.email.trim().toLowerCase() === adminEmail.trim().toLowerCase() ? member.avatarUrl : "");
  const adminDisplayName = matchingAdminProfile
    ? `${matchingAdminProfile.firstName} ${matchingAdminProfile.lastName}`.trim()
    : member.email.trim().toLowerCase() === adminEmail.trim().toLowerCase()
      ? `${member.firstName} ${member.lastName}`.trim()
      : adminEmail.split("@")[0] || "Administrator";
  const adminRoleLabel = isSystemAdmin ? "System administrator" : "Administrator";

  useEffect(() => {
    if (!profileMenuOpen) return;
    const closeProfileMenu = (event: MouseEvent | KeyboardEvent) => {
      if (event instanceof KeyboardEvent && event.key !== "Escape") return;
      if (event instanceof MouseEvent && profileMenuRef.current?.contains(event.target as Node)) return;
      setProfileMenuOpen(false);
    };
    document.addEventListener("mousedown", closeProfileMenu);
    document.addEventListener("keydown", closeProfileMenu);
    return () => {
      document.removeEventListener("mousedown", closeProfileMenu);
      document.removeEventListener("keydown", closeProfileMenu);
    };
  }, [profileMenuOpen]);

  const refreshAdminNotifications = async () => {
    try {
      const suggestions = await loadManagedBrandSuggestions();
      setBrandNotifications(suggestions.filter((suggestion) => suggestion.status === "pending"));
      setNotificationStatus("ready");
    } catch {
      setNotificationStatus("error");
    }
  };

  useEffect(() => {
    void refreshAdminNotifications();
    const refreshOnFocus = () => { void refreshAdminNotifications(); };
    window.addEventListener("focus", refreshOnFocus);
    window.addEventListener("kitanego:catalog-updated", refreshOnFocus);
    const timer = window.setInterval(refreshOnFocus, 60_000);
    return () => {
      window.removeEventListener("focus", refreshOnFocus);
      window.removeEventListener("kitanego:catalog-updated", refreshOnFocus);
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (!notificationOpen) return;
    const closeNotifications = (event: MouseEvent | KeyboardEvent) => {
      if (event instanceof KeyboardEvent && event.key !== "Escape") return;
      if (event instanceof MouseEvent && notificationRef.current?.contains(event.target as Node)) return;
      setNotificationOpen(false);
    };
    document.addEventListener("mousedown", closeNotifications);
    document.addEventListener("keydown", closeNotifications);
    return () => {
      document.removeEventListener("mousedown", closeNotifications);
      document.removeEventListener("keydown", closeNotifications);
    };
  }, [notificationOpen]);

  useEffect(() => {
    const navigation = mobileNavRef.current;
    const activeButton = navigation?.querySelector<HTMLButtonElement>('[aria-current="page"]');
    if (!navigation || !activeButton) return;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    navigation.scrollTo({ left: activeButton.offsetLeft - (navigation.clientWidth - activeButton.clientWidth) / 2, behavior: reducedMotion ? "auto" : "smooth" });
  }, [activeView]);

  const content = activeView === "members"
    ? <AdminMembers profiles={profiles} profilesStatus={profilesStatus} period={period} notify={notify} />
    : activeView === "demand"
      ? <AdminDemand profiles={profiles} profilesStatus={profilesStatus} />
      : activeView === "opportunities"
        ? <AdminOpportunities period={period} notify={notify} />
        : activeView === "reports"
          ? <AdminReports period={period} notify={notify} />
          : activeView === "settings"
            ? <AdminSettings section={settingsSection} profiles={profiles} navigate={navigate} deploymentState={deploymentState} colorTheme={colorTheme} onColorThemeChange={onColorThemeChange} latestNews={latestNews} latestNewsSyncStatus={latestNewsSyncStatus} onLatestNewsChange={onLatestNewsChange} isSystemAdmin={isSystemAdmin} notify={notify} />
            : activeView === "admin-settings"
              ? isSystemAdmin ? <AdminSystemSettings profiles={profiles} navigate={navigate} /> : <SystemAdminRequired navigate={navigate} resource="Admin Settings" />
              : activeView === "admin-users"
                ? isSystemAdmin ? <AdminUserManagement navigate={navigate} notify={notify} /> : <SystemAdminRequired navigate={navigate} resource="Administrator management" backTo="/admin/admin-settings" />
              : activeView === "registered-users"
                ? isSystemAdmin ? <AdminSettings section="users" adminUserManagement profiles={profiles} navigate={navigate} deploymentState={deploymentState} colorTheme={colorTheme} onColorThemeChange={onColorThemeChange} latestNews={latestNews} latestNewsSyncStatus={latestNewsSyncStatus} onLatestNewsChange={onLatestNewsChange} isSystemAdmin notify={notify} /> : <SystemAdminRequired navigate={navigate} resource="Registered user management" backTo="/admin/admin-settings" />
              : activeView === "audit-trail"
                ? isSystemAdmin ? <AdminAuditTrail navigate={navigate} notify={notify} /> : <SystemAdminRequired navigate={navigate} resource="Audit trail" backTo="/admin/admin-settings" />
            : activeView === "deployment"
              ? isSystemAdmin
                ? <AdminDeployment deploymentState={deploymentState} deploymentSyncStatus={deploymentSyncStatus} onDeploymentModeChange={onDeploymentModeChange} notify={notify} />
                : <SystemAdminRequired navigate={navigate} />
              : <AdminOverview profiles={profiles} profilesStatus={profilesStatus} adminLastLoginAt={adminLastLoginAt} pendingBrandSuggestions={brandNotifications.length} navigate={navigate} notify={notify} />;

  const isNavItemActive = (view: AdminView) => activeView === view || (view === "settings" && activeView === "deployment") || (view === "admin-settings" && (activeView === "audit-trail" || activeView === "admin-users" || activeView === "registered-users"));
  const visibleAdminNavItems = adminNavItems.filter((item) =>
    (opportunitiesEnabled || item.view !== "opportunities") &&
    (!item.systemAdminOnly || isSystemAdmin),
  );

  return (
    <div className="admin-shell">
      <aside className="admin-sidebar">
        <Logo inverse wordmark />
        <span className="admin-label">Admin Centre</span>
        <nav aria-label="Admin navigation">
          {visibleAdminNavItems.map(({ view, label, route, icon: Icon }) => (
            <button type="button" key={view} className={isNavItemActive(view) ? "active" : ""} aria-current={isNavItemActive(view) ? "page" : undefined} onClick={() => navigate(route)}>
              <Icon /> {label}
            </button>
          ))}
        </nav>
        <div><button type="button" className="member-switch" onClick={() => navigate(member.registered ? "/home" : "/")}><ArrowLeft size={16} /> Public site</button></div>
      </aside>
      <main className="admin-main">
        <header className="admin-topbar">
          <div className="admin-mobile-brand"><Logo wordmark onClick={() => navigate("/admin")} ariaLabel="Admin Centre home" />{!isSystemAdmin && <PrototypeBadge label="Admin" />}</div>
          <div className="admin-actions">
            <div className="admin-notifications" ref={notificationRef}>
              <button type="button" className="admin-notification-trigger" aria-label={`${brandNotifications.length} pending brand suggestion notifications`} aria-expanded={notificationOpen} aria-haspopup="dialog" onClick={() => { setNotificationOpen((open) => !open); setProfileMenuOpen(false); if (!notificationOpen) void refreshAdminNotifications(); }}><Bell size={18} />{brandNotifications.length > 0 && <span>{brandNotifications.length > 99 ? "99+" : brandNotifications.length}</span>}</button>
              {notificationOpen && <section className="admin-notification-panel" role="dialog" aria-label="Admin notifications">
                <header><span><strong>Notifications</strong><small>Suggested brands requiring action</small></span><button type="button" onClick={() => setNotificationOpen(false)} aria-label="Close notifications"><X size={16} /></button></header>
                <div className="admin-notification-list">
                  {notificationStatus === "loading" && <p>Loading notifications…</p>}
                  {notificationStatus === "error" && <p>Notifications are temporarily unavailable.</p>}
                  {notificationStatus === "ready" && brandNotifications.map((suggestion) => <button type="button" key={suggestion.id} onClick={() => { setNotificationOpen(false); navigate("/admin/settings/product-brand"); }}><span className="admin-notification-icon"><Tag size={16} /></span><span><strong>New suggested brand</strong><em>{suggestion.name}</em><small>{suggestion.requestCount} {suggestion.requestCount === 1 ? "request" : "requests"} · Review and take action</small></span><ChevronRight size={16} /></button>)}
                  {notificationStatus === "ready" && brandNotifications.length === 0 && <div className="admin-notification-empty"><CheckCircle2 /><strong>All caught up</strong><small>No suggested brands require action.</small></div>}
                </div>
                {brandNotifications.length > 0 && <footer><button type="button" onClick={() => { setNotificationOpen(false); navigate("/admin/settings/product-brand"); }}>Review all suggestions <ArrowRight size={15} /></button></footer>}
              </section>}
            </div>
            <div className="admin-profile" ref={profileMenuRef}>
              <button type="button" className="admin-profile-trigger" aria-label={`Open account options for ${adminDisplayName}`} aria-expanded={profileMenuOpen} aria-haspopup="menu" title={adminDisplayName} onClick={() => setProfileMenuOpen((open) => !open)}>{adminAvatarUrl ? <img src={adminAvatarUrl} alt="" /> : <span>{(adminDisplayName.charAt(0) || "A").toUpperCase()}</span>}</button>
              {profileMenuOpen && <div className="admin-profile-menu" role="menu" aria-label="Administrator account options"><div className="admin-profile-menu__identity"><span className="admin-profile-menu__avatar">{adminAvatarUrl ? <img src={adminAvatarUrl} alt="" /> : <span>{(adminDisplayName.charAt(0) || "A").toUpperCase()}</span>}</span><span><strong>{adminDisplayName}</strong><em>{adminEmail}</em><small>{adminRoleLabel}</small></span></div><button type="button" role="menuitem" onClick={() => { setProfileMenuOpen(false); void onSignOut(); }}><LogOut size={17} /><span><strong>Sign out</strong></span></button></div>}
            </div>
          </div>
        </header>
        <nav ref={mobileNavRef} className="admin-mobile-nav" aria-label="Admin sections">
          {visibleAdminNavItems.map(({ view, label, route, icon: Icon }) => (
            <button type="button" key={view} className={isNavItemActive(view) ? "active" : ""} aria-current={isNavItemActive(view) ? "page" : undefined} onClick={() => navigate(route)}><Icon size={16} /> {label}</button>
          ))}
        </nav>
        <div className="admin-content">{content}</div>
      </main>
    </div>
  );
}

function AdminPageHeader({ eyebrow, title, description, action, onAction, actionIcon: ActionIcon = Download, compact = false }: { eyebrow?: string; title: string; description?: string; action?: string; onAction?: () => void; actionIcon?: LucideIcon; compact?: boolean }) {
  return (
    <section className={`admin-title${compact ? " admin-title--compact" : ""}`}>
      <div>{eyebrow && <p className="eyebrow">{eyebrow}</p>}<h1 tabIndex={-1}>{title}</h1>{description && <p>{description}</p>}</div>
      {action && onAction && <button type="button" className="button button--ink" onClick={onAction}><ActionIcon size={17} /> {action}</button>}
    </section>
  );
}

function AdminSettings({ section, adminUserManagement = false, profiles, navigate, deploymentState, colorTheme, onColorThemeChange, latestNews, latestNewsSyncStatus, onLatestNewsChange, isSystemAdmin, notify }: { section?: SettingsSection; adminUserManagement?: boolean; profiles: LiveMemberProfile[]; navigate: Navigate; deploymentState: DeploymentState; colorTheme: ColorTheme; onColorThemeChange: (theme: ColorTheme) => void; latestNews: LatestNews | null; latestNewsSyncStatus: LatestNewsSyncStatus; onLatestNewsChange: (news: LatestNews | null) => void; isSystemAdmin: boolean; notify: (message: string) => void }) {
  const { opportunitiesEnabled, setOpportunitiesEnabled } = useFeatureAvailability();
  const { info: publishedScrollingInfo, status: scrollingInfoSyncStatus, setInfo: setPublishedScrollingInfo } = useScrollingInfo();
  const { enabledLanguages, setEnabledLanguages } = useContext(InterfaceLanguageContext);
  const [newsLanguage, setNewsLanguage] = useState<Language>("en");
  const [newsTranslations, setNewsTranslations] = useState<Record<Language, LatestNewsTranslation>>(() => createLatestNewsEditorTranslations(latestNews));
  const [newsPublished, setNewsPublished] = useState(latestNews?.published ?? false);
  const [newsEditorStatus, setNewsEditorStatus] = useState<LatestNewsSyncStatus>(latestNewsSyncStatus);
  const [newsSaving, setNewsSaving] = useState(false);
  const [scrollingMessage, setScrollingMessage] = useState(publishedScrollingInfo?.message ?? "");
  const [scrollingPublished, setScrollingPublished] = useState(publishedScrollingInfo?.published ?? false);
  const [scrollingEditorStatus, setScrollingEditorStatus] = useState<ScrollingInfoSyncStatus>(scrollingInfoSyncStatus);
  const [scrollingSaving, setScrollingSaving] = useState(false);
  const [adminDemandShowcase, setAdminDemandShowcase] = useState<DemandShowcase | null>(null);
  const [demandShowcaseLoading, setDemandShowcaseLoading] = useState(false);
  const [demandShowcaseSaving, setDemandShowcaseSaving] = useState(false);
  const [featureSaving, setFeatureSaving] = useState(false);
  const [languageSaving, setLanguageSaving] = useState(false);
  const [memberQuery, setMemberQuery] = useState("");
  const [memberStatuses, setMemberStatuses] = useState<Record<string, MemberAccountStatus>>(() => Object.fromEntries(profiles.map((profile) => [profile.id, profile.accountStatus])));
  const [memberStatusSaving, setMemberStatusSaving] = useState<string | null>(null);
  const [memberPlans, setMemberPlans] = useState<Record<string, MemberPlan>>(() => Object.fromEntries(profiles.map((profile) => [profile.id, profile.plan])));
  const [memberPlanSaving, setMemberPlanSaving] = useState<string | null>(null);
  const [memberArchiveTarget, setMemberArchiveTarget] = useState<LiveMemberProfile | null>(null);
  const [memberArchiveSaving, setMemberArchiveSaving] = useState(false);
  const [archivedMemberIds, setArchivedMemberIds] = useState<Set<string>>(() => new Set());
  const [heroImages, setHeroImages] = useState<LandingHeroImage[]>([]);
  const [heroImagesLoading, setHeroImagesLoading] = useState(true);
  const [heroImagesSaving, setHeroImagesSaving] = useState(false);
  const [adminBrands, setAdminBrands] = useState<BrandSuggestion[]>([]);
  const [brandQuery, setBrandQuery] = useState("");
  const [brandsLoading, setBrandsLoading] = useState(true);
  const [brandImageSaving, setBrandImageSaving] = useState("");
  const activeNewsTranslation = newsTranslations[newsLanguage];

  const updateActiveNewsTranslation = (field: keyof LatestNewsTranslation, value: string) => {
    setNewsTranslations((current) => ({
      ...current,
      [newsLanguage]: { ...current[newsLanguage], [field]: value },
    }));
  };

  const toggleLanguage = async (language: Language) => {
    const next = enabledLanguages.includes(language)
      ? enabledLanguages.filter((item) => item !== language)
      : (["en", "ms", "sabah", "zh"] as Language[]).filter((item) => item === language || enabledLanguages.includes(item));
    if (next.length === 0) {
      notify("At least one language must remain enabled");
      return;
    }
    const previous = enabledLanguages;
    setEnabledLanguages(next);
    setLanguageSaving(true);
    try {
      const saved = await writeGlobalEnabledLanguages(next);
      setEnabledLanguages(saved);
      notify("Language choices updated across Kitanego");
    } catch {
      setEnabledLanguages(previous);
      notify("Language settings could not be updated");
    } finally {
      setLanguageSaving(false);
    }
  };

  useEffect(() => {
    setMemberStatuses((current) => ({ ...Object.fromEntries(profiles.map((profile) => [profile.id, profile.accountStatus])), ...current }));
    setMemberPlans((current) => ({ ...Object.fromEntries(profiles.map((profile) => [profile.id, profile.plan])), ...current }));
  }, [profiles]);

  const filteredMemberProfiles = profiles.filter((profile) => !archivedMemberIds.has(profile.id) && `${profile.firstName} ${profile.lastName} ${profile.email} ${profile.district}`.toLowerCase().includes(memberQuery.trim().toLowerCase()));
  const filteredAdminBrands = adminBrands.filter((brand) => brand.name.toLocaleLowerCase().includes(brandQuery.trim().toLocaleLowerCase()));
  const changeMemberStatus = async (profile: LiveMemberProfile, status: MemberAccountStatus) => {
    const previous = memberStatuses[profile.id] ?? profile.accountStatus;
    setMemberStatuses((current) => ({ ...current, [profile.id]: status }));
    setMemberStatusSaving(profile.id);
    try {
      await updateMemberAccountStatus(profile.id, status);
      notify(`${profile.firstName} ${profile.lastName} is now ${status.replace("_", " ")}`);
    } catch {
      setMemberStatuses((current) => ({ ...current, [profile.id]: previous }));
      notify("Member status could not be updated");
    } finally {
      setMemberStatusSaving(null);
    }
  };

  const changeMemberPlan = async (profile: LiveMemberProfile, plan: MemberPlan) => {
    const previous = memberPlans[profile.id] ?? profile.plan;
    setMemberPlans((current) => ({ ...current, [profile.id]: plan }));
    setMemberPlanSaving(profile.id);
    try {
      await updateMemberPlan(profile.id, plan);
      notify(`${profile.firstName} ${profile.lastName} is now on the ${plan.charAt(0).toUpperCase()}${plan.slice(1)} plan`);
    } catch {
      setMemberPlans((current) => ({ ...current, [profile.id]: previous }));
      notify("Member plan could not be updated");
    } finally {
      setMemberPlanSaving(null);
    }
  };

  const confirmMemberArchive = async () => {
    if (!memberArchiveTarget) return;
    const profile = memberArchiveTarget;
    setMemberArchiveSaving(true);
    try {
      await archiveMemberProfile(profile.id);
      setArchivedMemberIds((current) => new Set(current).add(profile.id));
      setMemberArchiveTarget(null);
      window.dispatchEvent(new Event("kitanego:member-archived"));
      notify(`${profile.firstName} ${profile.lastName} was archived and removed from web app records and reports`);
    } catch (error) {
      notify(error instanceof Error ? error.message : "Registered user could not be archived");
    } finally {
      setMemberArchiveSaving(false);
    }
  };

  useEffect(() => {
    if (section !== "latest-news") return;
    let active = true;
    setNewsEditorStatus("loading");
    void readAdminLatestNews().then((news) => {
      if (!active) return;
      setNewsTranslations(createLatestNewsEditorTranslations(news));
      setNewsPublished(news?.published ?? false);
      setNewsEditorStatus("ready");
    }).catch(() => {
      if (active) setNewsEditorStatus("error");
    });
    return () => { active = false; };
  }, [section]);

  useEffect(() => {
    if (section !== "scrolling-info") return;
    let active = true;
    setScrollingEditorStatus("loading");
    void readAdminScrollingInfo().then((info) => {
      if (!active) return;
      setScrollingMessage(info?.message ?? "");
      setScrollingPublished(info?.published ?? false);
      setScrollingEditorStatus("ready");
    }).catch(() => {
      if (active) setScrollingEditorStatus("error");
    });
    return () => { active = false; };
  }, [section]);

  useEffect(() => {
    if (section !== "demand-showcase" || !isSystemAdmin) return;
    let active = true;
    setDemandShowcaseLoading(true);
    void readAdminDemandShowcase().then((showcase) => {
      if (active) setAdminDemandShowcase(showcase);
    }).catch(() => {
      if (active) notify("Weekly demand showcase could not be loaded");
    }).finally(() => {
      if (active) setDemandShowcaseLoading(false);
    });
    return () => { active = false; };
  }, [section, isSystemAdmin]);

  useEffect(() => {
    if (section !== "landing-images") return;
    let active = true;
    setHeroImagesLoading(true);
    void readLandingHeroImages().then((images) => {
      if (active) setHeroImages(images);
    }).catch(() => {
      if (active) notify("Landing header images could not be loaded");
    }).finally(() => {
      if (active) setHeroImagesLoading(false);
    });
    return () => { active = false; };
  }, [section]);

  useEffect(() => {
    if (section !== "brand-images") return;
    let active = true;
    setBrandsLoading(true);
    void loadAllBrandsForAdmin().then((brands) => {
      if (active) setAdminBrands(brands);
    }).catch(() => {
      if (active) notify("Brands could not be loaded");
    }).finally(() => {
      if (active) setBrandsLoading(false);
    });
    return () => { active = false; };
  }, [section]);

  const addHeroImages = async (files: FileList | null) => {
    if (!files?.length) return;
    const remaining = 5 - heroImages.length;
    const selected = Array.from(files).slice(0, remaining);
    if (remaining <= 0) {
      notify("Remove an image before uploading another. The limit is five.");
      return;
    }
    setHeroImagesSaving(true);
    try {
      const uploaded: LandingHeroImage[] = [];
      const availablePositions = [0, 1, 2, 3, 4].filter((position) => !heroImages.some((image) => image.position === position));
      for (const [index, file] of selected.entries()) uploaded.push(await uploadLandingHeroImage(file, availablePositions[index]));
      setHeroImages((current) => [...current, ...uploaded]);
      notify(`${uploaded.length} landing header image${uploaded.length === 1 ? "" : "s"} uploaded`);
    } catch (error) {
      notify(error instanceof Error ? error.message : "Landing header images could not be uploaded");
      const refreshed = await readLandingHeroImages().catch(() => null);
      if (refreshed) setHeroImages(refreshed);
    } finally {
      setHeroImagesSaving(false);
    }
  };

  const deleteHeroImage = async (image: LandingHeroImage) => {
    setHeroImagesSaving(true);
    try {
      await removeLandingHeroImage(image);
      const remaining = heroImages.filter((candidate) => candidate.id !== image.id);
      setHeroImages(remaining);
      notify("Landing header image removed");
    } catch {
      notify("Landing header image could not be removed");
    } finally {
      setHeroImagesSaving(false);
    }
  };

  const updateBrandImage = async (brand: BrandSuggestion, file: File | undefined) => {
    if (!file) return;
    setBrandImageSaving(brand.name);
    try {
      const updated = await uploadBrandImage(brand, file);
      setAdminBrands((current) => current.map((candidate) => candidate.name.toLocaleLowerCase() === brand.name.toLocaleLowerCase() ? updated : candidate));
      notify(`${brand.name} image updated`);
    } catch (error) {
      notify(error instanceof Error ? error.message : "Brand image could not be uploaded");
    } finally {
      setBrandImageSaving("");
    }
  };

  const saveNews = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const english = newsTranslations.en;
    if (!english.title.trim() || !english.body.trim()) {
      setNewsLanguage("en");
      notify("Add the English news title and message before saving");
      return;
    }
    const incompleteLanguage = latestNewsLanguageOptions.find(({ id }) => {
      const translation = newsTranslations[id];
      return Boolean(translation.title.trim()) !== Boolean(translation.body.trim());
    });
    if (incompleteLanguage) {
      setNewsLanguage(incompleteLanguage.id);
      notify(`Add both the title and message for ${incompleteLanguage.name}, or leave both empty to use the English fallback`);
      return;
    }
    setNewsSaving(true);
    try {
      const translations: LatestNewsTranslations = Object.fromEntries(
        latestNewsLanguageOptions.flatMap(({ id }) => {
          const translation = newsTranslations[id];
          return translation.title.trim() && translation.body.trim() ? [[id, translation]] : [];
        }),
      );
      const saved = await writeLatestNews({ title: english.title, body: english.body, translations, published: newsPublished });
      onLatestNewsChange(saved);
      setNewsTranslations(createLatestNewsEditorTranslations(saved));
      setNewsEditorStatus("ready");
      notify(saved.published ? "Latest news published to member Home" : "Latest news saved as a draft");
    } catch {
      setNewsEditorStatus("error");
      notify("Latest news could not be saved");
    } finally {
      setNewsSaving(false);
    }
  };

  const saveScrollingInfo = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!scrollingMessage.trim()) {
      notify("Add scrolling information before saving");
      return;
    }
    setScrollingSaving(true);
    try {
      const saved = await writeScrollingInfo({ message: scrollingMessage, published: scrollingPublished });
      setPublishedScrollingInfo(saved.published ? saved : null);
      setScrollingEditorStatus("ready");
      notify(saved.published ? "Scrolling info published to the member header" : "Scrolling info saved as a draft");
    } catch {
      setScrollingEditorStatus("error");
      notify("Scrolling info could not be saved");
    } finally {
      setScrollingSaving(false);
    }
  };

  const refreshDemandShowcase = async () => {
    setDemandShowcaseSaving(true);
    try {
      const refreshed = await refreshAdminDemandShowcase();
      setAdminDemandShowcase(refreshed);
      notify("Products on demand updated from the latest member selections");
    } catch {
      notify("Products on demand could not be updated");
    } finally {
      setDemandShowcaseSaving(false);
    }
  };

  const changeDemandShowcaseAutoUpdate = async (enabled: boolean) => {
    if (!adminDemandShowcase) return;
    const previous = adminDemandShowcase;
    setAdminDemandShowcase({ ...previous, autoUpdate: enabled });
    setDemandShowcaseSaving(true);
    try {
      const saved = await setDemandShowcaseAutoUpdate(enabled);
      setAdminDemandShowcase(saved);
      notify(enabled ? "Automatic weekly updates enabled" : "Automatic weekly updates paused");
    } catch {
      setAdminDemandShowcase(previous);
      notify("Weekly update setting could not be changed");
    } finally {
      setDemandShowcaseSaving(false);
    }
  };

  if (section === "demand-showcase" && !isSystemAdmin) {
    return <SystemAdminRequired navigate={navigate} resource="Demand showcase" />;
  }

  if (!section) {
    return (
      <>
        <AdminPageHeader title="Settings" description="Manage visitor and administrator experiences across Kitanego." />
        <section className="settings-page-grid settings-page-grid--hub" aria-label="Settings categories">
          {isSystemAdmin && <button type="button" className="settings-page-card" onClick={() => navigate("/admin/settings/opportunities")}><span className="settings-page-card__icon"><Eye /></span><span className={`deployment-state-pill deployment-state-pill--${opportunitiesEnabled ? "live" : "maintenance"}`}><i /> {opportunitiesEnabled ? "Enabled" : "Hidden"}</span><h2>Opportunities page</h2><p>Control whether the Opportunities feature is available to members and administrators.</p><strong>Manage opportunities <ChevronRight size={17} /></strong></button>}
          {isSystemAdmin && <button type="button" className="settings-page-card" onClick={() => navigate("/admin/settings/deployment")}><span className="settings-page-card__icon"><Rocket /></span><span className={`deployment-state-pill deployment-state-pill--${deploymentState.mode}`}><i /> {deploymentState.mode === "live" ? "Enabled" : "Disabled"}</span><h2>Deployment status</h2><p>Control whether the web app is live or replaced by the maintenance notice.</p><strong>Manage deployment <ChevronRight size={17} /></strong></button>}
          <button type="button" className="settings-page-card" onClick={() => navigate("/admin/settings/appearance")}><span className="settings-page-card__icon"><Palette /></span><span className="theme-current-pill">{colorTheme === "sabah" ? "Sabah active" : "Sunny active"}</span><h2>Color theme</h2><p>Choose the visual theme used across the public site on every device.</p><strong>Manage appearance <ChevronRight size={17} /></strong></button>
          <button type="button" className="settings-page-card" onClick={() => navigate("/admin/settings/languages")}><span className="settings-page-card__icon"><Globe2 /></span><span className="theme-current-pill">{enabledLanguages.length} enabled</span><h2>Languages</h2><p>Control which interface languages visitors and members can choose.</p><strong>Manage languages <ChevronRight size={17} /></strong></button>
          <button type="button" className="settings-page-card" onClick={() => navigate("/admin/settings/landing-images")}><span className="settings-page-card__icon"><ImagePlus /></span><h2>Landing header images</h2><p>Upload and manage the image rotation displayed in the landing-page header.</p><strong>Manage header images <ChevronRight size={17} /></strong></button>
          <button type="button" className="settings-page-card" onClick={() => navigate("/admin/settings/product-brand")}><span className="settings-page-card__icon"><FileDown /></span><h2>Product and Brand</h2><p>Download Excel templates and import approved products and brands into member choice lists.</p><strong>Manage catalog imports <ChevronRight size={17} /></strong></button>
          <button type="button" className="settings-page-card" onClick={() => navigate("/admin/settings/brand-images")}><span className="settings-page-card__icon"><Tag /></span><h2>Brand management</h2><p>Review suggested brands and manage the image associated with every selectable brand.</p><strong>Manage brands <ChevronRight size={17} /></strong></button>
          <button type="button" className="settings-page-card" onClick={() => navigate("/admin/settings/latest-news")}><span className="settings-page-card__icon"><Bell /></span><span className={`news-state-pill news-state-pill--${latestNews?.published ? "published" : "draft"}`}>{latestNews?.published ? "Published" : "Draft"}</span><h2>Latest news</h2><p>Prepare the announcement displayed at the top of every member’s Home page.</p><strong>Manage latest news <ChevronRight size={17} /></strong></button>
          <button type="button" className="settings-page-card" onClick={() => navigate("/admin/settings/scrolling-info")}><span className="settings-page-card__icon"><Info /></span><span className={`news-state-pill news-state-pill--${publishedScrollingInfo?.published ? "published" : "draft"}`}>{publishedScrollingInfo?.published ? "Published" : "Draft"}</span><h2>Scrolling Info</h2><p>Manage the continuously scrolling update displayed in the member header.</p><strong>Manage scrolling info <ChevronRight size={17} /></strong></button>
          {isSystemAdmin && <button type="button" className="settings-page-card" onClick={() => navigate("/admin/settings/demand-showcase")}><span className="settings-page-card__icon"><TrendingUp /></span><span className="theme-current-pill">Weekly</span><h2>Demand showcase</h2><p>Manage the member-ranked product list scrolling across the public landing page.</p><strong>Manage demand showcase <ChevronRight size={17} /></strong></button>}
          <button type="button" className="settings-page-card" onClick={() => navigate("/admin/settings/users")}><span className="settings-page-card__icon"><Users /></span><span className="theme-current-pill">{profiles.length} users</span><h2>Registered users</h2><p>Manage account status and membership plans without deleting user records.</p><strong>Manage users <ChevronRight size={17} /></strong></button>
        </section>
      </>
    );
  }

  const sectionDetails: Record<SettingsSection, { title: string; description: string }> = {
    opportunities: { title: "Opportunities page", description: "Control whether the complete Opportunities feature is available." },
    appearance: { title: "Color theme", description: "Choose the visual theme used across the public site." },
    languages: { title: "Languages", description: "Choose which interface languages are available to visitors and members." },
    "landing-images": { title: "Landing header images", description: "Manage the image rotation displayed in the landing-page header." },
    "product-brand": { title: "Product and Brand", description: "Manage linked product and brand choices with one Excel catalogue." },
    "brand-images": { title: "Brand management", description: "Review member suggestions and manage images for selectable brands." },
    "latest-news": { title: "Latest news", description: "Manage the announcement shown on every member’s Home page." },
    "scrolling-info": { title: "Scrolling Info", description: "Manage the scrolling update displayed across the member header." },
    "demand-showcase": { title: "Demand showcase", description: "Manage the weekly member-ranked products displayed on the public landing page." },
    users: { title: "Registered users", description: "Manage user access status and membership plans." },
  };
  const sectionDetail = sectionDetails[section];

  return (
    <>
      <button type="button" className="back-link settings-back-link" onClick={() => navigate(adminUserManagement ? "/admin/admin-settings" : "/admin/settings")}><ArrowLeft size={17} /> Back to {adminUserManagement ? "Admin Settings" : "Settings"}</button>
      <AdminPageHeader title={sectionDetail.title} description={sectionDetail.description} />
      <section className="settings-detail-page" aria-label={`${sectionDetail.title} settings`}>
        {section === "product-brand" && <ProductBrandSettings notify={notify} />}
        {section === "opportunities" && isSystemAdmin && <article className="settings-page-card settings-detail-card" aria-labelledby="opportunities-visibility-title">
          <span className="settings-page-card__icon"><Eye /></span>
          <span className={`deployment-state-pill deployment-state-pill--${opportunitiesEnabled ? "live" : "maintenance"}`}><i /> {opportunitiesEnabled ? "Enabled" : "Hidden"}</span>
          <h2 id="opportunities-visibility-title">Opportunities page</h2>
          <p>Hide or reveal the complete Opportunities feature. When hidden, member and admin links, upgrade prompts, summaries and direct page access are removed.</p>
          <button type="button" className="button button--ink button--full" disabled={featureSaving} onClick={() => {
            setFeatureSaving(true);
            void setOpportunitiesEnabled(!opportunitiesEnabled).then(() => notify(!opportunitiesEnabled ? "Opportunities enabled globally" : "Opportunities hidden globally")).catch(() => notify("Opportunities visibility could not be updated")).finally(() => setFeatureSaving(false));
          }}>{featureSaving ? "Saving…" : opportunitiesEnabled ? "Disable and hide" : "Enable and reveal"}</button>
        </article>}
        {section === "appearance" && <article className="settings-page-card settings-page-card--theme settings-detail-card" aria-labelledby="color-theme-title">
          <span className="settings-page-card__icon"><Palette /></span>
          <span className="theme-current-pill">{colorTheme === "sabah" ? "Sabah active" : "Sunny active"}</span>
          <h2 id="color-theme-title">Color theme</h2>
          <p>Choose the visual theme used across the public site on every device.</p>
          <div className="theme-settings-options" role="group" aria-label="Choose color theme">
            <button type="button" className={colorTheme === "sunny" ? "selected" : ""} aria-pressed={colorTheme === "sunny"} onClick={() => onColorThemeChange("sunny")}>
              <span className="theme-preview theme-preview--sunny" aria-hidden="true"><i /><i /><i /></span>
              <span><strong>Sunny</strong><small>Warm yellow and navy</small></span>
              {colorTheme === "sunny" && <Check size={17} />}
            </button>
            <button type="button" className={colorTheme === "sabah" ? "selected" : ""} aria-pressed={colorTheme === "sabah"} onClick={() => onColorThemeChange("sabah")}>
              <span className="theme-preview theme-preview--sabah" aria-hidden="true"><i /><i /><i /></span>
              <span><strong>Sabah</strong><small>Blue, white and red</small></span>
              {colorTheme === "sabah" && <Check size={17} />}
            </button>
          </div>
        </article>}
        {section === "languages" && <article className="settings-page-card settings-page-card--theme settings-detail-card settings-language-manager" aria-labelledby="language-settings-title">
          <span className="settings-page-card__icon"><Globe2 /></span>
          <span className="theme-current-pill">{enabledLanguages.length} enabled</span>
          <h2 id="language-settings-title">Language selection</h2>
          <p>Enable the languages shown in Kitanego’s language selectors. If a member’s current language is disabled, the app automatically switches to the first enabled language.</p>
          <div className="settings-language-options" role="group" aria-label="Available interface languages">
            {([
              { id: "en" as const, code: "EN", name: "English", description: "English interface" },
              { id: "ms" as const, code: "BM", name: "Bahasa Malaysia", description: "Standard Bahasa Malaysia" },
              { id: "sabah" as const, code: "BS", name: "Bahasa Sabah", description: "Sabahan language style" },
              { id: "zh" as const, code: "中文", name: "Chinese", description: "Simplified Chinese interface" },
            ]).map((option) => {
              const enabled = enabledLanguages.includes(option.id);
              return <button type="button" key={option.id} className={enabled ? "selected" : ""} aria-pressed={enabled} disabled={languageSaving} onClick={() => { void toggleLanguage(option.id); }}>
                <span className="settings-language-code" aria-hidden="true">{option.code}</span>
                <span><strong>{option.name}</strong><small>{option.description}</small></span>
                <span className={`settings-language-state${enabled ? " enabled" : ""}`}>{enabled ? <><Check size={15} /> Enabled</> : "Hidden"}</span>
              </button>;
            })}
          </div>
          <small className="settings-hero-note">At least one language must remain enabled. When only one is enabled, language selectors are hidden from users.</small>
        </article>}
        {section === "landing-images" && <article className="settings-page-card settings-hero-editor settings-detail-card" aria-labelledby="landing-hero-title">
          <div className="settings-hero-editor__head"><span className="settings-page-card__icon"><ImagePlus /></span><span className="theme-current-pill">{heroImages.length}/5 images</span></div>
          <h2 id="landing-hero-title">Landing header images</h2>
          <p>Upload up to five images for the landing-page header. Multiple images transition automatically every six seconds.</p>
          <div className="settings-hero-grid" aria-live="polite">
            {heroImages.map((image, index) => <figure key={image.id}><img src={image.imageUrl} alt={`Landing header image ${index + 1}`} /><figcaption>Image {index + 1}<button type="button" aria-label={`Remove landing header image ${index + 1}`} disabled={heroImagesSaving} onClick={() => { void deleteHeroImage(image); }}><Trash2 size={15} /></button></figcaption></figure>)}
            {!heroImagesLoading && heroImages.length === 0 && <div className="settings-hero-empty"><ImagePlus /><span>No custom images yet. The default header remains visible.</span></div>}
          </div>
          <label className={`button button--ink settings-hero-upload${heroImages.length >= 5 || heroImagesSaving ? " disabled" : ""}`}><Upload size={17} /> {heroImagesSaving ? "Updating…" : "Upload images"}<input type="file" accept="image/jpeg,image/png,image/webp,image/avif" multiple disabled={heroImages.length >= 5 || heroImagesSaving} onChange={(event) => { void addHeroImages(event.target.files); event.currentTarget.value = ""; }} /></label>
          <small className="settings-hero-note">JPG, PNG, WebP or AVIF · 8 MB maximum each · recommended 1600 × 900 or wider</small>
        </article>}
        {section === "brand-images" && <><article className="settings-page-card settings-brand-editor settings-detail-card" aria-labelledby="brand-images-title">
          <div className="settings-brand-editor__head"><span className="settings-page-card__icon"><Tag /></span><span className="theme-current-pill">{adminBrands.filter((brand) => brand.imageUrl).length}/{adminBrands.length} updated</span></div>
          <h2 id="brand-images-title">Catalogue brand images</h2>
          <p>Give each brand its own image. New community brands keep the standard tag icon until an administrator uploads an image.</p>
          <label className="settings-brand-search"><Search /><span className="sr-only">Search brands</span><input type="search" value={brandQuery} onChange={(event) => setBrandQuery(event.target.value)} placeholder="Search brand name" /></label>
          <div className="settings-brand-grid" aria-live="polite">
            {brandsLoading && <div className="settings-brand-empty">Loading brands…</div>}
            {!brandsLoading && filteredAdminBrands.map((brand) => <div className="settings-brand-item" key={brand.name}>
              <span className="settings-brand-preview">{brand.imageUrl ? <BrandImage src={brand.imageUrl} alt={`${brand.name} product`} /> : <Tag aria-hidden="true" />}</span>
              <span className="settings-brand-copy"><strong>{brand.name}</strong><small>{brand.imageUrl ? "Custom image" : "Using generic icon"}</small></span>
              <label className={`settings-brand-upload${brandImageSaving === brand.name ? " disabled" : ""}`}><Upload /><span>{brandImageSaving === brand.name ? "Uploading…" : brand.imageUrl ? "Replace" : "Upload"}</span><input type="file" accept="image/jpeg,image/png,image/webp,image/avif" disabled={Boolean(brandImageSaving)} onChange={(event) => { void updateBrandImage(brand, event.target.files?.[0]); event.currentTarget.value = ""; }} /></label>
            </div>)}
            {!brandsLoading && filteredAdminBrands.length === 0 && <div className="settings-brand-empty">No brands match your search.</div>}
          </div>
          <small className="settings-hero-note">JPG, PNG, WebP or AVIF · 4 MB maximum · square images work best</small>
        </article></>}
        {section === "latest-news" && <article className="settings-page-card settings-news-editor settings-detail-card" aria-labelledby="latest-news-title">
          <div className="settings-news-editor__head"><span className="settings-page-card__icon"><Bell /></span><span className={`news-state-pill news-state-pill--${newsPublished ? "published" : "draft"}`}>{newsPublished ? "Published" : "Draft"}</span></div>
          <h2 id="latest-news-title">Latest news</h2>
          <p>Post the latest Kitanego announcement in every member language. Missing translations automatically use English.</p>
          <form onSubmit={saveNews} className="settings-news-form">
            <div className="settings-news-languages" role="tablist" aria-label="News language">
              {latestNewsLanguageOptions.map(({ id, code, name }) => {
                const complete = Boolean(newsTranslations[id].title.trim() && newsTranslations[id].body.trim());
                return <button type="button" role="tab" aria-selected={newsLanguage === id} className={newsLanguage === id ? "selected" : ""} key={id} onClick={() => setNewsLanguage(id)} disabled={newsEditorStatus === "loading" || newsSaving}><span>{code}</span><strong>{name}</strong><small className={complete ? "complete" : "fallback"}>{complete ? "Ready" : id === "en" ? "Required" : "English fallback"}</small></button>;
              })}
            </div>
            <p className="settings-news-language-note">Editing {latestNewsLanguageOptions.find(({ id }) => id === newsLanguage)?.name}. Paragraphs, line breaks and spacing are preserved on the member Home card.</p>
            <label>News title<input value={activeNewsTranslation.title} onChange={(event) => updateActiveNewsTranslation("title", event.target.value)} maxLength={120} placeholder="Add a short news headline" disabled={newsEditorStatus === "loading" || newsSaving} /></label>
            <label>News message<textarea value={activeNewsTranslation.body} onChange={(event) => updateActiveNewsTranslation("body", event.target.value)} maxLength={600} rows={8} placeholder="Write the update members should see" disabled={newsEditorStatus === "loading" || newsSaving} /></label>
            <label className="settings-news-publish"><input type="checkbox" checked={newsPublished} onChange={(event) => setNewsPublished(event.target.checked)} disabled={newsEditorStatus === "loading" || newsSaving} /><span><strong>Publish on member Home</strong><small>Turn this off to keep the news as an admin draft.</small></span></label>
            {newsEditorStatus === "error" && <p className="field-error" role="alert">Latest news settings are unavailable. Check the data-service connection and try again.</p>}
            <div className="settings-news-actions"><small>{activeNewsTranslation.title.length}/120 · {activeNewsTranslation.body.length}/600</small><button type="submit" className="button button--ink" disabled={newsEditorStatus === "loading" || newsSaving || !newsTranslations.en.title.trim() || !newsTranslations.en.body.trim()}><Save size={17} /> {newsSaving ? "Saving…" : "Save all languages"}</button></div>
          </form>
        </article>}
        {section === "scrolling-info" && <article className="settings-page-card settings-news-editor settings-detail-card" aria-labelledby="scrolling-info-title">
          <div className="settings-news-editor__head"><span className="settings-page-card__icon"><Info /></span><span className={`news-state-pill news-state-pill--${scrollingPublished ? "published" : "draft"}`}>{scrollingPublished ? "Published" : "Draft"}</span></div>
          <h2 id="scrolling-info-title">Scrolling Info</h2>
          <p>Publish a short update that moves continuously across the top header in the member area.</p>
          <form onSubmit={saveScrollingInfo} className="settings-news-form settings-scrolling-info-form">
            <label>Scrolling message<textarea value={scrollingMessage} onChange={(event) => setScrollingMessage(event.target.value)} maxLength={240} rows={3} placeholder="Write the update members should see" disabled={scrollingEditorStatus === "loading" || scrollingSaving} /></label>
            <label className="settings-news-publish"><input type="checkbox" checked={scrollingPublished} onChange={(event) => setScrollingPublished(event.target.checked)} disabled={scrollingEditorStatus === "loading" || scrollingSaving} /><span><strong>Show in member header</strong><small>Turn this off to keep the message as an admin draft.</small></span></label>
            {scrollingEditorStatus === "error" && <p className="field-error" role="alert">Scrolling info settings are unavailable. Check the data-service connection and try again.</p>}
            <div className="settings-news-actions"><small>{scrollingMessage.length}/240</small><button type="submit" className="button button--ink" disabled={scrollingEditorStatus === "loading" || scrollingSaving || !scrollingMessage.trim()}><Save size={17} /> {scrollingSaving ? "Saving…" : "Save scrolling info"}</button></div>
          </form>
        </article>}
        {section === "demand-showcase" && isSystemAdmin && <article className="settings-page-card settings-news-editor settings-detail-card settings-demand-showcase" aria-labelledby="demand-showcase-settings-title">
          <div className="settings-news-editor__head"><span className="settings-page-card__icon"><TrendingUp /></span><span className="theme-current-pill">Weekly snapshot</span></div>
          <h2 id="demand-showcase-settings-title">Products on demand</h2>
          <p>The landing-page list ranks unique member selections and remains unchanged during the week. It refreshes automatically on the first visit of each new week.</p>
          {demandShowcaseLoading && <p className="settings-demand-showcase__state">Loading the current weekly snapshot…</p>}
          {!demandShowcaseLoading && adminDemandShowcase && <>
            <div className="settings-demand-showcase__meta"><span><small>Week started</small><strong>{new Date(`${adminDemandShowcase.weekStartedOn}T12:00:00`).toLocaleDateString("en-MY", { day: "numeric", month: "short", year: "numeric" })}</strong></span><span><small>Products shown</small><strong>{adminDemandShowcase.products.length}</strong></span><span><small>Last updated</small><strong>{adminDemandShowcase.updatedAt ? new Date(adminDemandShowcase.updatedAt).toLocaleString("en-MY", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "Not yet"}</strong></span></div>
            <div className="settings-demand-showcase__products" role="list" aria-label="Current products on demand">{adminDemandShowcase.products.map(({ product, memberCount }, index) => <div role="listitem" key={product}><span>{index + 1}</span><img src={getProductImage(product)} alt="" /><strong>{product}</strong><small>{memberCount} member{memberCount === 1 ? "" : "s"}</small></div>)}</div>
            <label className="settings-news-publish"><input type="checkbox" checked={adminDemandShowcase.autoUpdate} disabled={demandShowcaseSaving} onChange={(event) => { void changeDemandShowcaseAutoUpdate(event.target.checked); }} /><span><strong>Automatic weekly updates</strong><small>Refresh from the latest member selections once per week, not daily.</small></span></label>
            <div className="settings-news-actions"><small>System administrators can refresh early when required.</small><button type="button" className="button button--ink" disabled={demandShowcaseSaving} onClick={() => { void refreshDemandShowcase(); }}><RefreshCw size={17} /> {demandShowcaseSaving ? "Updating…" : "Update now"}</button></div>
          </>}
        </article>}
      </section>
      {section === "users" && <section className="admin-card member-status-manager settings-user-manager" aria-labelledby="member-status-manager-title">
        <div className="member-status-manager__head"><div><h2 id="member-status-manager-title">Manage registered users</h2></div><label className="member-status-search"><Search /><span className="sr-only">Search registered users</span><input type="search" value={memberQuery} onChange={(event) => setMemberQuery(event.target.value)} placeholder="Search name, email or district" /></label></div>
        <div className="member-status-list">
          <div className={`member-status-row member-status-row--head${adminUserManagement ? "" : " member-status-row--no-actions"}`}><span>Member</span><span>Verification</span><span>District</span><span>Plan</span><span>Status</span>{adminUserManagement && <span>Actions</span>}</div>
          {filteredMemberProfiles.map((profile) => <div className={`member-status-row${adminUserManagement ? "" : " member-status-row--no-actions"}`} key={profile.id}><span><strong>{profile.firstName} {profile.lastName}</strong><small>{profile.email}</small></span><span className="member-verification-badges">{profile.emailVerified && <em>Email verified</em>}{profile.mobileVerified && <em>Mobile verified</em>}{!profile.emailVerified && !profile.mobileVerified && <em className="unverified">Not verified</em>}</span><span data-label="District">{profile.district}</span><label className="member-plan-control" data-label="Plan"><span className="sr-only">Plan for {profile.firstName} {profile.lastName}</span><select value={memberPlans[profile.id] ?? profile.plan} disabled={memberPlanSaving === profile.id || memberArchiveSaving} onChange={(event) => { void changeMemberPlan(profile, event.target.value as MemberPlan); }}><option value="basic">Basic</option><option value="pro">Pro</option><option value="premium">Premium</option></select></label><label className="member-status-control" data-label="Status"><span className="sr-only">Status for {profile.firstName} {profile.lastName}</span><select value={memberStatuses[profile.id] ?? profile.accountStatus} disabled={memberStatusSaving === profile.id || memberArchiveSaving} onChange={(event) => { void changeMemberStatus(profile, event.target.value as MemberAccountStatus); }}><option value="active">Active</option><option value="inactive">Inactive</option><option value="banned">Banned</option><option value="under_review">Under review</option></select></label>{adminUserManagement && <span className="member-archive-control"><button type="button" disabled={memberArchiveSaving || memberPlanSaving === profile.id || memberStatusSaving === profile.id} onClick={() => setMemberArchiveTarget(profile)} aria-label={`Delete ${profile.firstName} ${profile.lastName}`} title="Delete registered user"><Trash2 size={17} /> Delete</button></span>}</div>)}
          {filteredMemberProfiles.length === 0 && <div className="admin-empty"><Users /><strong>No registered users found</strong><span>Try a different name, email address or district.</span></div>}
        </div>
      </section>}
      {adminUserManagement && memberArchiveTarget && <DeleteMonthlyDemandDialog monthLabel={`${memberArchiveTarget.firstName} ${memberArchiveTarget.lastName} · ${memberArchiveTarget.email}`} busy={memberArchiveSaving} labels={{ eyebrow: "Delete registered user", title: "Archive this registered user?", body: "The user and their live app data will be removed from Kitanego and excluded from every report. Their historical profile and email remain in the restricted archive, while the email is released to register again as a new user.", cancel: "Keep user", confirm: "Archive user", busy: "Archiving…", close: "Close user archive confirmation" }} onCancel={() => setMemberArchiveTarget(null)} onConfirm={() => { void confirmMemberArchive(); }} />}
    </>
  );
}

function AdminSystemSettings({ profiles, navigate }: { profiles: LiveMemberProfile[]; navigate: Navigate }) {
  return (
    <>
      <AdminPageHeader title="Admin Settings" description="Manage security, accountability and administrative governance." />
      <section className="settings-page-grid admin-system-settings-grid" aria-label="Administrator settings">
        <button type="button" className="settings-page-card" onClick={() => navigate("/admin/admin-settings/users")}>
          <span className="settings-page-card__icon"><Users /></span>
          <h2>Web app administrators</h2>
          <p>Add or remove wep app admin user.</p>
          <strong className="settings-page-card__edit-icon" aria-hidden="true"><Pencil size={19} /></strong>
        </button>
        <button type="button" className="settings-page-card" onClick={() => navigate("/admin/admin-settings/registered-users")}>
          <span className="settings-page-card__icon"><UserRound /></span>
          <span className="theme-current-pill">{profiles.length} users</span>
          <h2>Registered users</h2>
          <p>Manage registered user access, membership plans and archived accounts.</p>
          <strong className="settings-page-card__edit-icon" aria-hidden="true"><Pencil size={19} /></strong>
        </button>
        <button type="button" className="settings-page-card" onClick={() => navigate("/admin/admin-settings/audit-trail")}>
          <span className="settings-page-card__icon"><FileText /></span>
          <h2>Audit trail</h2>
          <p>Historical transaction records.</p>
          <strong className="settings-page-card__edit-icon" aria-hidden="true"><Pencil size={19} /></strong>
        </button>
      </section>
    </>
  );
}

function AdminUserManagement({ navigate, notify }: { navigate: Navigate; notify: (message: string) => void }) {
  const [administrators, setAdministrators] = useState<ManagedAdminUser[]>([]);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [removingEmail, setRemovingEmail] = useState("");

  const refresh = async () => {
    setStatus("loading");
    try {
      setAdministrators(await loadManagedAdminUsers());
      setStatus("ready");
    } catch (error) {
      setStatus("error");
      notify(error instanceof Error ? error.message : "Administrator access could not be loaded.");
    }
  };

  useEffect(() => { void refresh(); }, []);

  const assignAdministrator = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    if (!normalizedEmail) return;
    setSaving(true);
    try {
      const assigned = await addWebAppAdmin(normalizedEmail);
      setAdministrators((current) => [assigned, ...current.filter((administrator) => administrator.email.toLowerCase() !== assigned.email.toLowerCase())].sort((left, right) => {
        if (left.role !== right.role) return left.role === "system_admin" ? -1 : 1;
        return left.email.localeCompare(right.email);
      }));
      setEmail("");
      setStatus("ready");
      notify(`${assigned.email} can now access the Admin Centre`);
    } catch (error) {
      const message = error instanceof Error
        ? error.message
        : error && typeof error === "object" && "message" in error && typeof error.message === "string"
          ? error.message
          : "Administrator access could not be assigned.";
      notify(message);
    } finally {
      setSaving(false);
    }
  };

  const removeAdministrator = async (administrator: ManagedAdminUser) => {
    if (!window.confirm(`Remove Admin Centre access for ${administrator.email}?`)) return;
    setRemovingEmail(administrator.email);
    try {
      await removeWebAppAdmin(administrator.email);
      setAdministrators((current) => current.filter((item) => item.email !== administrator.email));
      notify(`${administrator.email} no longer has Admin Centre access`);
    } catch (error) {
      notify(error instanceof Error ? error.message : "Administrator access could not be removed.");
    } finally {
      setRemovingEmail("");
    }
  };

  const webAppAdministrators = administrators.filter((administrator) => administrator.role === "admin");
  const systemAdministrators = administrators.filter((administrator) => administrator.role === "system_admin");

  return (
    <>
      <button type="button" className="back-link settings-back-link" onClick={() => navigate("/admin/admin-settings")}><ArrowLeft size={17} /> Back to Admin Settings</button>
      <AdminPageHeader title="Web app administrators" description="Assign people who can manage content and operations across the Admin Centre." />
      <section className="admin-card admin-user-manager" aria-labelledby="admin-user-manager-title">
        <div className="admin-user-manager__head">
          <div><h2 id="admin-user-manager-title">Assigned Web App admin</h2></div>
          <form className="admin-access-form" onSubmit={assignAdministrator}>
            <label><Mail aria-hidden="true" /><span className="sr-only">Administrator email address</span><input type="email" required maxLength={320} value={email} onChange={(event) => setEmail(event.target.value)} placeholder="name@example.com" autoComplete="email" /></label>
            <button type="submit" className="button button--ink" disabled={saving || !email.trim()}><Plus size={18} /> {saving ? "Adding…" : "Add"}</button>
          </form>
        </div>

        {status === "loading" && <div className="admin-empty admin-user-manager__empty"><RefreshCw className="audit-loading-icon" /><strong>Loading administrators</strong><span>Checking secured Admin Centre access…</span></div>}
        {status === "error" && <div className="admin-empty admin-user-manager__empty"><ShieldCheck /><strong>Administrator list unavailable</strong><span>Apply the latest database update, then try again.</span><button type="button" className="button button--outline" onClick={() => { void refresh(); }}>Try again</button></div>}
        {status === "ready" && <div className="admin-role-groups">
          <section aria-labelledby="web-app-admins-title">
            <div className="admin-role-group__heading"><div><h3 id="web-app-admins-title">Web App Admins</h3></div><span>{webAppAdministrators.length}</span></div>
            <div className="admin-role-list">
              {webAppAdministrators.map((administrator) => <div className="admin-role-row" key={administrator.email}>
                <span className="admin-role-avatar" aria-hidden="true"><UserRound /></span>
                <span className="admin-role-copy"><strong>{administrator.email}</strong><small>{administrator.userId ? "Active account linked" : "Access ready when this email signs in"}</small></span>
                <span className={`admin-role-state ${administrator.userId ? "admin-role-state--active" : ""}`}>{administrator.userId ? "Active" : "Pre-authorized"}</span>
                <button type="button" className="admin-role-remove" disabled={removingEmail === administrator.email} onClick={() => { void removeAdministrator(administrator); }}><Trash2 size={17} /> {removingEmail === administrator.email ? "Removing…" : "Remove"}</button>
              </div>)}
              {webAppAdministrators.length === 0 && <div className="admin-role-list__empty"><Users /><span><strong>No Web App Admins assigned</strong><small>Add an email address above to grant access.</small></span></div>}
            </div>
          </section>

          <section aria-labelledby="system-admins-title">
            <div className="admin-role-group__heading"><div><h3 id="system-admins-title">System administrators</h3><p>Full access, including Admin Settings and security controls.</p></div><span>{systemAdministrators.length}</span></div>
            <div className="admin-role-list">
              {systemAdministrators.map((administrator) => <div className="admin-role-row admin-role-row--system" key={administrator.email}>
                <span className="admin-role-avatar" aria-hidden="true"><ShieldCheck /></span>
                <span className="admin-role-copy"><strong>{administrator.email}</strong><small>Protected system administrator</small></span>
                <span className="admin-role-state admin-role-state--system">System admin</span>
                <span className="admin-role-protected"><LockKeyhole size={15} /> Protected</span>
              </div>)}
            </div>
          </section>
        </div>}
      </section>
    </>
  );
}

function formatAuditEntity(value: string) {
  return value.split("_").map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(" ");
}

function formatAuditJson(value: Record<string, unknown> | null) {
  return value ? JSON.stringify(value, null, 2) : "No record snapshot";
}

function AdminAuditTrail({ navigate, notify }: { navigate: Navigate; notify: (message: string) => void }) {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [query, setQuery] = useState("");
  const [action, setAction] = useState("all");
  const [entity, setEntity] = useState("all");

  const refresh = async (announce = false) => {
    setStatus("loading");
    try {
      const next = await loadAuditEvents();
      setEvents(next);
      setStatus("ready");
      if (announce) notify("Audit trail refreshed");
    } catch {
      setStatus("error");
      if (announce) notify("Audit trail could not be refreshed");
    }
  };

  useEffect(() => { void refresh(); }, []);

  const entityOptions = Array.from(new Set(events.map((event) => event.entityType))).sort();
  const normalizedQuery = query.trim().toLowerCase();
  const filteredEvents = events.filter((event) => {
    if (action !== "all" && event.action !== action) return false;
    if (entity !== "all" && event.entityType !== entity) return false;
    if (!normalizedQuery) return true;
    return `${event.actorEmail} ${event.actorRole} ${event.action} ${event.entityType} ${event.entityId} ${event.changedFields.join(" ")}`.toLowerCase().includes(normalizedQuery);
  });

  return (
    <>
      <button type="button" className="back-link settings-back-link" onClick={() => navigate("/admin/admin-settings")}><ArrowLeft size={17} /> Back to Admin Settings</button>
      <AdminPageHeader title="Audit trail" description="A chronological record of state-changing transactions across Kitanego." action="Refresh" actionIcon={RefreshCw} onAction={() => { void refresh(true); }} />
      <div className="admin-notice admin-notice--privacy"><ShieldCheck size={17} /><span><strong>System administrator access only.</strong> Audit entries are read-only. Sensitive location and avatar data are excluded from stored snapshots.</span></div>
      <section className="admin-card audit-trail-card" aria-label="Audit events">
        <div className="audit-trail-toolbar">
          <label className="member-status-search audit-trail-search"><Search /><span className="sr-only">Search audit trail</span><input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search actor, record or field" /></label>
          <label><span>Action</span><select value={action} onChange={(event) => setAction(event.target.value)}><option value="all">All actions</option><option value="insert">Created</option><option value="update">Updated</option><option value="delete">Deleted</option></select></label>
          <label><span>Resource</span><select value={entity} onChange={(event) => setEntity(event.target.value)}><option value="all">All resources</option>{entityOptions.map((value) => <option value={value} key={value}>{formatAuditEntity(value)}</option>)}</select></label>
        </div>
        <div className="audit-trail-summary"><span>{filteredEvents.length} of {events.length} events</span><small>Newest first · up to 250 recent events</small></div>
        {status === "loading" && <div className="admin-empty audit-trail-empty"><RefreshCw className="audit-loading-icon" /><strong>Loading audit trail</strong><span>Retrieving secured transaction records…</span></div>}
        {status === "error" && <div className="admin-empty audit-trail-empty"><ShieldCheck /><strong>Audit trail unavailable</strong><span>Check the database update and your system administrator access.</span></div>}
        {status === "ready" && filteredEvents.length === 0 && <div className="admin-empty audit-trail-empty"><FileText /><strong>No audit events found</strong><span>Try changing the filters, or return after a state-changing transaction occurs.</span></div>}
        {status === "ready" && filteredEvents.length > 0 && <div className="audit-event-list">
          {filteredEvents.map((event) => <details className="audit-event" key={event.id}>
            <summary>
              <span className={`audit-action audit-action--${event.action}`}>{event.action === "insert" ? "Created" : event.action === "delete" ? "Deleted" : "Updated"}</span>
              <span className="audit-event__resource"><strong>{formatAuditEntity(event.entityType)}</strong><small>{event.entityId || "Record ID unavailable"}</small></span>
              <span className="audit-event__actor"><strong>{event.actorEmail}</strong><small>{formatAuditEntity(event.actorRole)}</small></span>
              <time dateTime={event.occurredAt}>{new Intl.DateTimeFormat("en-MY", { dateStyle: "medium", timeStyle: "medium", timeZone: "Asia/Kuching" }).format(new Date(event.occurredAt))}</time>
              <ChevronDown aria-hidden="true" />
            </summary>
            <div className="audit-event__details">
              <dl><div><dt>Event ID</dt><dd>#{event.id}</dd></div><div><dt>Outcome</dt><dd>{event.outcome}</dd></div><div><dt>Source</dt><dd>{event.source}</dd></div><div><dt>Actor user ID</dt><dd>{event.actorUserId ?? "System"}</dd></div></dl>
              <div className="audit-event__fields"><strong>Changed fields</strong><span>{event.changedFields.length ? event.changedFields.map((field) => <em key={field}>{formatAuditEntity(field)}</em>) : <small>None recorded</small>}</span></div>
              <div className="audit-event__snapshots"><section><h3>Before</h3><pre>{formatAuditJson(event.beforeData)}</pre></section><section><h3>After</h3><pre>{formatAuditJson(event.afterData)}</pre></section></div>
            </div>
          </details>)}
        </div>}
      </section>
    </>
  );
}

function SystemAdminRequired({ navigate, resource = "Deployment status", backTo = "/admin/settings" }: { navigate: Navigate; resource?: string; backTo?: Route }) {
  return (
    <section className="admin-card system-admin-required" role="alert">
      <span><LockKeyhole /></span>
      <p className="eyebrow">Restricted setting</p>
      <h1>System administrator access required</h1>
      <p>{resource} can only be viewed and changed by a Kitanego system administrator.</p>
      <button type="button" className="button button--ink" onClick={() => navigate(backTo)}><ArrowLeft size={17} /> Back to Settings</button>
    </section>
  );
}

function AdminDeployment({ deploymentState, deploymentSyncStatus, onDeploymentModeChange, notify }: { deploymentState: DeploymentState; deploymentSyncStatus: DeploymentSyncStatus; onDeploymentModeChange: (mode: DeploymentMode) => Promise<void>; notify: (message: string) => void }) {
  const [draftMode, setDraftMode] = useState<DeploymentMode>(deploymentState.mode);
  const [isSaving, setIsSaving] = useState(false);
  const hasChanges = deploymentSyncStatus === "ready" && draftMode !== deploymentState.mode;

  useEffect(() => setDraftMode(deploymentState.mode), [deploymentState.mode]);

  const applyDeploymentMode = async () => {
    setIsSaving(true);
    try {
      await onDeploymentModeChange(draftMode);
      notify(draftMode === "live" ? "Site enabled globally" : "Maintenance enabled globally");
    } catch {
      notify("Global deployment status could not be updated");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <AdminPageHeader title="Deployment status" />

      <div className="deployment-layout">
        <section className="admin-card deployment-controls" aria-labelledby="deployment-mode-title">
          <div className="admin-card__head"><div><strong id="deployment-mode-title">Select site status</strong></div></div>
          <div className="deployment-options" role="radiogroup" aria-label="Site mode">
            <button type="button" className={draftMode === "live" ? "selected" : ""} role="radio" aria-checked={draftMode === "live"} onClick={() => setDraftMode("live")}>
              <span className="deployment-option__icon"><Globe2 /></span><span><strong>On (Live)</strong><small>All web app pages accessible to public.</small></span><i><Check /></i>
            </button>
            <button type="button" className={draftMode === "maintenance" ? "selected" : ""} role="radio" aria-checked={draftMode === "maintenance"} onClick={() => setDraftMode("maintenance")}>
              <span className="deployment-option__icon"><Wrench /></span><span><strong>Off (Under maintenance)</strong><small>Detail web app is not accessible to public.</small></span><i><Check /></i>
            </button>
          </div>
          <div className="deployment-apply">
            <button type="button" className="button button--ink" disabled={!hasChanges || isSaving} onClick={applyDeploymentMode}><Save size={17} /> {isSaving ? "Applying…" : "Apply"}</button>
          </div>
        </section>

        <aside className="admin-card deployment-preview" aria-label="Visitor page preview">
          <div className="admin-card__head"><div><span>Preview</span></div><Eye /></div>
          <div className="deployment-preview__snapshot" aria-hidden="true">
            <iframe key={draftMode} src={`/?deployment-preview=${draftMode}#/`} title={`${draftMode} site preview`} tabIndex={-1} />
          </div>
        </aside>
      </div>
    </>
  );
}

function downloadAdminCsv(label: string, notify: (message: string) => void) {
  const reportType = label.toLowerCase();
  const rows = reportType.includes("member")
    ? [["metric", "value"], ["registered_members", "8246"], ["new_members", "912"], ["profiles_complete", "6267"], ["verified_members", "2184"]]
    : reportType.includes("district") || reportType.includes("coverage")
      ? [["district", "illustrative_registrations"], ...sampleRegistrationPoints.map((point) => [point.label, String(point.count)])]
      : reportType.includes("supplier") || reportType.includes("evidence") || reportType.includes("rice")
        ? [["product", "interested", "verified", "committed"], ...momentumProducts.map((row) => [row.product, String(row.interested), String(row.verified), String(row.committed)])]
        : reportType.includes("demand") || reportType.includes("pulse")
          ? [["category", "active_members", "period_change"], ...demandCategoryRows.map((row) => [row.label, String(row.members), row.change])]
          : [["metric", "value", "scope"], ["registered_members", "8246", "aggregate"], ["verified_members", "2184", "aggregate"], ["deal_commitments", "684", "aggregate"], ["active_products", "38", "aggregate"]];
  const csv = rows.map((row) => row.join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
  notify(`${label} downloaded`);
}

function filterLiveProfiles(profiles: LiveMemberProfile[], period: string) {
  if (period === "All time") return profiles;
  const days = period === "Last 7 days" ? 7 : 30;
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  return profiles.filter((profile) => new Date(profile.createdAt).getTime() >= cutoff);
}

async function exportMemberSummaryPdf(period: string, periodProfiles: LiveMemberProfile[], allProfiles: LiveMemberProfile[], notify: (message: string) => void) {
  const districtCounts = new Map<string, number>();
  const categoryCounts = new Map<string, number>();
  periodProfiles.forEach((profile) => {
    districtCounts.set(profile.district || "Not specified", (districtCounts.get(profile.district || "Not specified") ?? 0) + 1);
    profile.categories.forEach((categoryId) => {
      const label = categories.find((category) => category.id === categoryId)?.label ?? categoryId;
      categoryCounts.set(label, (categoryCounts.get(label) ?? 0) + 1);
    });
  });

  try {
    await generateMemberSummaryPdf({
      period,
      periodMembers: periodProfiles.length,
      allTimeMembers: allProfiles.length,
      profilesWithProducts: periodProfiles.filter((profile) => profile.products.length > 0).length,
      profilesWithMobile: periodProfiles.filter((profile) => profile.mobileNumber.trim().length > 0).length,
      profilesWithLocation: periodProfiles.filter((profile) => profile.latitude != null && profile.longitude != null).length,
      districts: Array.from(districtCounts, ([label, count]) => ({ label, count })).sort((a, b) => b.count - a.count || a.label.localeCompare(b.label)),
      categories: Array.from(categoryCounts, ([label, count]) => ({ label, count })).sort((a, b) => b.count - a.count || a.label.localeCompare(b.label)),
      recentRegistrations: [...periodProfiles]
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .map((profile) => ({
          id: `KT-${profile.id.slice(0, 8).toUpperCase()}`,
          district: profile.district || "Not specified",
          category: categories.find((category) => category.id === profile.categories[0])?.label ?? "Not selected",
          products: profile.products.length,
          joined: new Date(profile.createdAt).toLocaleDateString("en-MY", { timeZone: "Asia/Kuching", dateStyle: "medium" }),
        })),
    });
    notify("Members summary PDF downloaded");
  } catch (error) {
    console.error("Unable to generate members summary PDF", error);
    notify("Unable to generate the members summary PDF");
  }
}

function LiveDataState({ status, emptyMessage }: { status: "idle" | "loading" | "ready" | "error"; emptyMessage: string }) {
  if (status === "loading" || status === "idle") return <div className="admin-empty"><Clock3 /><strong>Loading live data</strong><span>Reading the latest database records.</span></div>;
  if (status === "error") return <div className="admin-empty"><Wrench /><strong>Live data unavailable</strong><span>Check the data-service connection and administrator permissions.</span></div>;
  return <div className="admin-empty"><Users /><strong>No live records yet</strong><span>{emptyMessage}</span></div>;
}

function createManagementReportData(profiles: LiveMemberProfile[], pendingBrandSuggestions: number): ManagementReportData {
  const countValues = (values: string[], resolveLabel: (value: string) => string = (value) => value): ManagementReportCount[] => Array.from(values.reduce((counts, value) => {
    const label = resolveLabel(value) || "Not specified";
    counts.set(label, (counts.get(label) ?? 0) + 1);
    return counts;
  }, new Map<string, number>()), ([label, count]) => ({ label, count })).sort((left, right) => right.count - left.count || left.label.localeCompare(right.label));
  const now = new Date();
  const latestCutoff = now.getTime() - 30 * 24 * 60 * 60 * 1000;
  const previousCutoff = now.getTime() - 60 * 24 * 60 * 60 * 1000;
  const last30Days = profiles.filter((profile) => new Date(profile.createdAt).getTime() >= latestCutoff).length;
  const previous30Days = profiles.filter((profile) => {
    const createdAt = new Date(profile.createdAt).getTime();
    return createdAt >= previousCutoff && createdAt < latestCutoff;
  }).length;
  const generatedAt = new Intl.DateTimeFormat("en-MY", {
    timeZone: "Asia/Kuching",
    dateStyle: "medium",
    timeStyle: "short",
  }).format(now);
  const generatedFileStamp = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kuching",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(now).replace(/[^0-9]/g, "").slice(0, 12);
  return {
    generatedAt,
    generatedFileStamp,
    members: profiles.length,
    categories: new Set(profiles.flatMap((profile) => profile.categories)).size,
    products: new Set(profiles.flatMap((profile) => profile.products)).size,
    brands: new Set(profiles.flatMap((profile) => profile.brands)).size,
    districts: new Set(profiles.map((profile) => profile.district).filter(Boolean)).size,
    last30Days,
    previous30Days,
    growthPercent: previous30Days > 0 ? ((last30Days - previous30Days) / previous30Days) * 100 : null,
    profilesWithProducts: profiles.filter((profile) => profile.products.length > 0).length,
    profilesWithBrands: profiles.filter((profile) => profile.brands.length > 0).length,
    profilesWithMobile: profiles.filter((profile) => profile.mobileNumber.trim().length > 0).length,
    profilesWithLocation: profiles.filter((profile) => profile.latitude != null && profile.longitude != null).length,
    verifiedProfiles: profiles.filter((profile) => profile.emailVerified || profile.mobileVerified).length,
    pendingBrandSuggestions,
    categoriesRanking: countValues(profiles.flatMap((profile) => profile.categories), (id) => categories.find((category) => category.id === id)?.label ?? id),
    productsRanking: countValues(profiles.flatMap((profile) => profile.products)),
    brandsRanking: countValues(profiles.flatMap((profile) => profile.brands)),
    districtsRanking: countValues(profiles.map((profile) => profile.district)),
    recentRegistrations: [...profiles].sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()).slice(0, 10).map((profile) => ({
      memberId: `KT-${profile.id.slice(0, 8).toUpperCase()}`,
      district: profile.district || "Not specified",
      joined: new Date(profile.createdAt).toLocaleDateString("en-MY", { timeZone: "Asia/Kuching", dateStyle: "medium" }),
      products: profile.products.length,
      brands: profile.brands.length,
    })),
  };
}

function ManagementReportRanking({ title, rows, tone }: { title: string; rows: ManagementReportCount[]; tone: "blue" | "green" | "gold" | "coral" }) {
  const visibleRows = rows.slice(0, 6);
  const maximum = Math.max(1, ...visibleRows.map((row) => row.count));
  return <section className={`management-report-ranking management-report-ranking--${tone}`}><h3>{title}</h3><ol>{visibleRows.map((row, index) => <li key={row.label}><span>{String(index + 1).padStart(2, "0")}</span><strong>{row.label}</strong><i aria-hidden="true"><b style={{ width: `${Math.max(5, (row.count / maximum) * 100)}%` }} /></i><em>{row.count.toLocaleString("en-MY")}</em></li>)}</ol>{visibleRows.length === 0 && <p>No records available.</p>}</section>;
}

function ManagementReportPreview({ data, onClose, notify }: { data: ManagementReportData; onClose: () => void; notify: (message: string) => void }) {
  const [downloading, setDownloading] = useState(false);
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [onClose]);
  const downloadPdf = async () => {
    setDownloading(true);
    try {
      await generateManagementReportPdf(data);
      notify("Management report PDF downloaded");
    } catch (error) {
      console.error("Unable to generate management report", error);
      notify("Unable to generate the management report PDF");
    } finally {
      setDownloading(false);
    }
  };
  const growthLabel = data.growthPercent == null ? "New baseline" : `${data.growthPercent >= 0 ? "+" : ""}${data.growthPercent.toFixed(1)}%`;
  const readiness = [
    ["Products selected", data.profilesWithProducts],
    ["Brands selected", data.profilesWithBrands],
    ["Mobile contact", data.profilesWithMobile],
    ["Registration location", data.profilesWithLocation],
    ["Verified profile", data.verifiedProfiles],
  ] as const;
  return <div className="management-report-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="management-report-dialog" role="dialog" aria-modal="true" aria-labelledby="management-report-title">
      <header className="management-report-toolbar"><div><FileText /><span><strong id="management-report-title">Management report preview</strong><small>Two-page A4 summary · Live administrator data</small></span></div><div><button type="button" className="button button--outline" onClick={() => window.print()}><Printer size={17} /> Print</button><button type="button" className="button button--ink" disabled={downloading} onClick={() => { void downloadPdf(); }}><FileDown size={17} /> {downloading ? "Preparing PDF…" : "Download PDF"}</button><button type="button" className="management-report-close" onClick={onClose} aria-label="Close report preview"><X size={19} /></button></div></header>
      <div className="management-report-canvas">
        <article className="management-report-page">
          <header className="management-report-page__header"><div><span>Kitanego</span><strong>Management Report</strong><small>Executive overview and demand intelligence</small></div><div><small>Generated</small><strong>{data.generatedAt}</strong><em>Page 1 of 2</em></div></header>
          <section className="management-report-intro"><div><span>Executive snapshot</span><h2>Community demand at a glance</h2><p>A concise view of participation, demand coverage and areas requiring management attention.</p></div><div className="management-report-period"><small>Reporting scope</small><strong>All live records</strong><span>Aggregate and privacy-safe</span></div></section>
          <section className="management-report-metrics">{[
            ["Registered members", data.members], ["Products selected", data.products], ["Brands selected", data.brands], ["Categories", data.categories], ["Districts", data.districts], ["New in 30 days", data.last30Days],
          ].map(([label, value]) => <div key={label}><small>{label}</small><strong>{Number(value).toLocaleString("en-MY")}</strong></div>)}</section>
          <section className={`management-report-movement ${data.growthPercent != null && data.growthPercent < 0 ? "negative" : ""}`}><div><span>Registration movement</span><strong>{data.last30Days.toLocaleString("en-MY")} new members</strong><small>Latest 30 days compared with {data.previous30Days.toLocaleString("en-MY")} in the preceding period</small></div><b>{growthLabel}</b></section>
          <div className="management-report-rankings"><ManagementReportRanking title="Leading product categories" rows={data.categoriesRanking} tone="blue" /><ManagementReportRanking title="Top selected products" rows={data.productsRanking} tone="green" /><ManagementReportRanking title="Top selected brands" rows={data.brandsRanking} tone="gold" /><ManagementReportRanking title="Members by district" rows={data.districtsRanking} tone="coral" /></div>
          <section className="management-report-attention"><h3>Management attention</h3><div><span><Tag /><strong>Suggested brands</strong><small>{data.pendingBrandSuggestions ? "Review and classify submissions" : "No action required"}</small><b>{data.pendingBrandSuggestions}</b></span><span><Package /><strong>Incomplete product profiles</strong><small>Opportunity to improve onboarding</small><b>{Math.max(0, data.members - data.profilesWithProducts)}</b></span><span><Smartphone /><strong>Missing mobile contact</strong><small>Consider an opt-in reminder</small><b>{Math.max(0, data.members - data.profilesWithMobile)}</b></span></div></section>
          <footer><span>Internal management summary · Aggregate information only</span><strong>Kitanego</strong></footer>
        </article>
        <article className="management-report-page">
          <header className="management-report-page__header"><div><span>Kitanego</span><strong>Management Report</strong><small>Member readiness and operational coverage</small></div><div><small>Generated</small><strong>{data.generatedAt}</strong><em>Page 2 of 2</em></div></header>
          <section className="management-report-section-heading"><span>Operational readiness</span><h2>Member profile quality</h2><p>Completion indicators help management assess how actionable the current demand signals are.</p></section>
          <section className="management-report-readiness">{readiness.map(([label, value]) => { const percent = data.members ? Math.round((value / data.members) * 100) : 0; return <div key={label}><span><strong>{label}</strong><em>{value.toLocaleString("en-MY")} members</em></span><i aria-hidden="true"><b style={{ width: `${percent}%` }} /></i><small>{percent}%</small></div>; })}</section>
          <section className="management-report-recent"><div className="management-report-section-heading"><span>Latest activity</span><h2>Recent registrations</h2></div><div className="management-report-table"><div className="management-report-table__head"><span>Member</span><span>District</span><span>Joined</span><span>Products</span><span>Brands</span></div>{data.recentRegistrations.map((registration) => <div key={registration.memberId}><strong>{registration.memberId}</strong><span>{registration.district}</span><span>{registration.joined}</span><span>{registration.products}</span><span>{registration.brands}</span></div>)}{data.recentRegistrations.length === 0 && <p>No registrations are available.</p>}</div></section>
          <section className="management-report-guidance"><Info /><div><strong>How to read this report</strong><p>Counts reflect selections stored in member profiles when this report was generated. Demand rankings represent member interest signals, not confirmed purchases. Use these insights for prioritisation, then review the detailed administrator screens before operational or supplier decisions.</p></div></section>
          <section className="management-report-decisions"><h3>Recommended management focus</h3><ol><li><span>01</span><div><strong>Strengthen actionable profiles</strong><small>Prioritise product, mobile contact and verification completion.</small></div></li><li><span>02</span><div><strong>Review emerging brand demand</strong><small>Process pending brand suggestions and connect approved brands to products.</small></div></li><li><span>03</span><div><strong>Use rankings for outreach</strong><small>Focus supplier conversations on the strongest product and district signals.</small></div></li></ol></section>
          <footer><span>Direct member contact details and coordinates are excluded</span><strong>Kitanego</strong></footer>
        </article>
      </div>
    </section>
  </div>;
}

function AdminOverview({ profiles, profilesStatus, adminLastLoginAt, pendingBrandSuggestions, navigate, notify }: { profiles: LiveMemberProfile[]; profilesStatus: "idle" | "loading" | "ready" | "error"; adminLastLoginAt: string | null; pendingBrandSuggestions: number; navigate: Navigate; notify: (message: string) => void }) {
  const [reportOpen, setReportOpen] = useState(false);
  const productCount = new Set(profiles.flatMap((profile) => profile.products)).size;
  const categoryCount = new Set(profiles.flatMap((profile) => profile.categories)).size;
  const districtCount = new Set(profiles.map((profile) => profile.district)).size;
  const locatedCount = profiles.filter((profile) => profile.latitude != null && profile.longitude != null).length;
  const mobileContactCount = profiles.filter((profile) => profile.mobileNumber.trim().length > 0).length;
  const lastLoginLabel = adminLastLoginAt
    ? new Date(adminLastLoginAt).toLocaleString("en-MY", { dateStyle: "medium", timeStyle: "short" })
    : "unavailable";
  const categoryCounts = Array.from(profiles.reduce((counts, profile) => {
    profile.categories.forEach((category) => counts.set(category, (counts.get(category) ?? 0) + 1));
    return counts;
  }, new Map<string, number>()).entries()).sort((a, b) => b[1] - a[1]).slice(0, 6);

  return (
    <>
      <AdminPageHeader title="Dashboard" description={`Recent login on ${lastLoginLabel}.`} action="Generate Report" onAction={() => setReportOpen(true)} compact />
      <section className="admin-kpis admin-overview-kpis"><Kpi label="Registered members" value={profiles.length.toLocaleString()} icon={Users} tone="mint" /><Kpi label="Categories represented" value={categoryCount.toLocaleString()} icon={PieChart} tone="sky" /><Kpi label="Products selected" value={productCount.toLocaleString()} icon={Package} tone="coral" /><Kpi label="Districts represented" value={districtCount.toLocaleString()} icon={MapPin} tone="lime" /></section>
      {profiles.length === 0 ? <section className="admin-card"><LiveDataState status={profilesStatus} emptyMessage="New member registrations will appear here as soon as they are submitted." /></section> : <section className="admin-grid admin-grid--overview">
        <article className="admin-card funnel-card"><div className="admin-card__head"><div><strong>Registrations</strong></div><button type="button" className="overview-card-action" aria-label="View members" title="View members" onClick={() => navigate("/admin/members")}><Eye /></button></div><div className="registration-vertical-chart">{[
          { label: "Email", value: profiles.length, tone: "registered" },
          { label: "Location", value: locatedCount, tone: "location" },
          { label: "Mobile", value: mobileContactCount, tone: "mobile" },
        ].map((item) => <div key={item.label} className={`registration-vertical-chart__item registration-vertical-chart__item--${item.tone}`}><strong>{item.value}</strong><div aria-hidden="true"><i style={{ height: `${Math.max(item.value > 0 ? 10 : 3, (item.value / Math.max(1, profiles.length)) * 100)}%` }} /></div><span>{item.label}</span></div>)}</div></article>
        <article className="admin-card demand-card"><div className="admin-card__head"><div><strong>Product Category</strong></div><button type="button" className="overview-card-action" aria-label="Explore product categories" title="Explore product categories" onClick={() => navigate("/admin/demand")}><Search /></button></div><div className="category-horizontal-chart">{categoryCounts.map(([id, count]) => {
          const category = categories.find((candidate) => candidate.id === id);
          const Icon = category?.icon ?? CircleHelp;
          const label = category?.label ?? id;
          return <div key={id} className="category-horizontal-chart__row"><span className="category-chart-icon" role="img" tabIndex={0} aria-label={label} data-tooltip={label} title={label}><Icon /></span><div aria-hidden="true"><i style={{ width: `${Math.max(5, (count / Math.max(1, categoryCounts[0]?.[1] ?? 1)) * 100)}%` }} /></div><strong>{count}</strong></div>;
        })}</div></article>
        <LiveRegistrationMap profiles={profiles} />
      </section>}
      {reportOpen && <ManagementReportPreview data={createManagementReportData(profiles, pendingBrandSuggestions)} onClose={() => setReportOpen(false)} notify={notify} />}
    </>
  );

  /* Legacy layout retained below only until all historical components are removed. */
  const member = initialMember;
  const bars = [72, 58, 49, 38, 29, 18];
  return (
    <>
      <AdminPageHeader eyebrow="Demand command centre" title="The community, in focus." description="Understand credible demand without exposing individual members." action="Export aggregate report" onAction={() => downloadAdminCsv("Kitanego overview", notify)} />
      <div className="admin-notice"><Sparkles size={17} /><span><strong>Prototype dataset.</strong> Totals and clusters are illustrative; a captured session location is labelled separately.</span></div>
      <section className="admin-kpis"><Kpi label="Registered members" value="8,246" change="+12.4%" icon={Users} tone="mint" /><Kpi label="Verified members" value="2,184" change="26.5% rate" icon={BadgeCheck} tone="sky" /><Kpi label="Deal commitments" value="684" change="31.3% of verified" icon={HeartHandshake} tone="coral" /><Kpi label="Active products" value="38" change="8 categories" icon={Package} tone="lime" /></section>
      <section className="admin-grid">
        <article className="admin-card funnel-card"><div className="admin-card__head"><div><span>Membership funnel</span><strong>From interest to intent</strong></div><button type="button" onClick={() => navigate("/admin/members")} aria-label="Open members">View</button></div><div className="funnel"><div className="funnel-row"><span>Registered</span><div><i style={{ width: "100%" }} /></div><strong>8,246</strong></div><div className="funnel-row"><span>Profile complete</span><div><i style={{ width: "76%" }} /></div><strong>6,267</strong></div><div className="funnel-row"><span>Verified</span><div><i style={{ width: "42%" }} /></div><strong>2,184</strong></div><div className="funnel-row"><span>Committed</span><div><i style={{ width: "24%" }} /></div><strong>684</strong></div></div><div className="funnel-note"><TrendingUp /><span><strong>Highest opportunity</strong>Improve profile completion after registration.</span></div></article>
        <article className="admin-card demand-card"><div className="admin-card__head"><div><span>Category demand</span><strong>Signals by category</strong></div><button type="button" onClick={() => navigate("/admin/demand")}>Explore</button></div><div className="vertical-chart">{["Groceries", "Home care", "Personal", "Telecom", "Transport", "Baby"].map((label, index) => <div key={label}><span style={{ height: `${bars[index]}%` }} /><small>{label}</small></div>)}</div></article>
        <RegistrationMap member={member} />
        <article className="admin-card geography-card"><div className="admin-card__head"><div><span>Geography</span><strong>Top demand areas</strong></div><MapPin /></div><div className="geo-layout"><div className="abstract-map"><i className="map-shape map-shape--a" /><i className="map-shape map-shape--b" /><i className="map-shape map-shape--c" /><span className="map-pulse map-pulse--a" /><span className="map-pulse map-pulse--b" /><span className="map-pulse map-pulse--c" /></div><ol><li><span>01</span><strong>Kota Kinabalu</strong><em>1,284</em></li><li><span>02</span><strong>Sandakan</strong><em>846</em></li><li><span>03</span><strong>Tawau</strong><em>621</em></li><li><span>04</span><strong>Lahad Datu</strong><em>574</em></li></ol></div></article>
        <article className="admin-card evidence-card"><div className="admin-card__head"><div><span>Supplier evidence</span><strong>Rice · 10kg pilot</strong></div><button type="button" className="pilot-badge" onClick={() => navigate("/admin/opportunities")}>Live test</button></div><div className="evidence-stats"><div><small>Interested</small><strong>924</strong><span>members</span></div><div><small>Verified</small><strong>742</strong><span>members</span></div><div><small>Committed</small><strong>684</strong><span>members</span></div><div><small>Units</small><strong>1,026</strong><span>expressed</span></div></div><div className="privacy-safe"><ShieldCheck /><span>Aggregate-safe export. No member identifiers or coordinates included.</span></div></article>
      </section>
    </>
  );
}

function AdminMembers({ profiles, profilesStatus, period, notify }: { profiles: LiveMemberProfile[]; profilesStatus: "idle" | "loading" | "ready" | "error"; period: string; notify: (message: string) => void }) {
  const [query, setQuery] = useState("");
  const [district, setDistrict] = useState("All districts");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const periodProfiles = filterLiveProfiles(profiles, period);
  const districtOptionsLive = Array.from(new Set(profiles.map((profile) => profile.district))).sort();
  const liveRows = periodProfiles.map((profile) => ({
    id: `KT-${profile.id.slice(0, 8).toUpperCase()}`,
    name: [profile.firstName, profile.lastName].filter(Boolean).join(" ") || "Not provided",
    email: profile.email || "Not provided",
    mobileContact: profile.mobileNumber || "Not provided",
    district: profile.district,
    category: categories.find((category) => category.id === profile.categories[0])?.label ?? "Not selected",
    joined: new Date(profile.createdAt).toLocaleDateString("en-MY", { dateStyle: "medium" }),
    locationConsent: profile.latitude != null && profile.longitude != null ? "Yes" : "No",
    products: profile.products.length,
  })).filter((row) => {
    const term = query.trim().toLowerCase();
    return (!term || `${row.id} ${row.district} ${row.category}`.toLowerCase().includes(term)) && (district === "All districts" || row.district === district);
  });

  return (
    <>
      <section className="admin-title members-title"><div><h1 tabIndex={-1}>Members</h1></div><button type="button" className="button button--outline admin-report-action" aria-label="Export members PDF" title="Export members PDF" data-tooltip="Export members PDF" onClick={() => { void exportMemberSummaryPdf(period, periodProfiles, profiles, notify); }}><span className="demand-pdf-icon" aria-hidden="true">PDF</span></button></section>
      <section className="admin-kpis members-kpis"><Kpi label="Registered members" value={periodProfiles.length.toLocaleString()} icon={Users} tone="mint" /><Kpi label="All-time members" value={profiles.length.toLocaleString()} icon={TrendingUp} tone="lime" /><Kpi label="Districts represented" value={new Set(periodProfiles.map((profile) => profile.district)).size.toLocaleString()} icon={MapPin} tone="sky" /><Kpi label="Profiles with products" value={periodProfiles.filter((profile) => profile.products.length > 0).length.toLocaleString()} icon={CheckCircle2} tone="coral" /></section>
      <section className="admin-card admin-data-card">
        <div className="admin-toolbar">
          <label className="admin-search"><Search size={17} /><span className="sr-only">Search members</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search ID, district or category" /></label>
          <label><span className="sr-only">Filter by district</span><select value={district} onChange={(event) => setDistrict(event.target.value)}><option value="All districts">All districts</option>{districtOptionsLive.map((option) => <option key={option} value={option}>{option}</option>)}</select></label>
        </div>
        <div className="admin-member-list">
          <div className="admin-member-row admin-member-row--head" aria-hidden="true"><span>Member</span><span>District</span><span>Strongest signal</span><span>Products</span><span>Joined</span><span /></div>
          {liveRows.map((row) => <div key={row.id} className={`admin-member-item ${selectedId === row.id ? "open" : ""}`}><button type="button" className="admin-member-row" onClick={() => setSelectedId(selectedId === row.id ? null : row.id)} aria-expanded={selectedId === row.id}><span><strong>{row.id}</strong></span><span data-label="District">{row.district}</span><span data-label="Strongest signal">{row.category}</span><span data-label="Products">{row.products}</span><span data-label="Joined">{row.joined}</span><ChevronRight size={17} /></button>{selectedId === row.id && <div className="member-detail"><div><small>Name</small><strong>{row.name}</strong></div><div><small>Email</small><strong>{row.email}</strong></div><div><small>Mobile contact</small><strong>{row.mobileContact}</strong></div><div><small>Location consent</small><strong>{row.locationConsent}</strong></div></div>}</div>)}
          {liveRows.length === 0 && <LiveDataState status={profilesStatus} emptyMessage={profiles.length ? "No registrations match the current filters." : "New member registrations will appear here after submission."} />}
        </div>
      </section>
    </>
  );

  const member = initialMember;
  const status = "All statuses";
  const setStatus = (_value: string) => undefined;
  const currentCategory = categories.find((category) => category.id === member.categories[0])?.label ?? "Profile building";
  const currentRow: AdminMemberRow | null = member.district ? {
    id: "CURRENT",
    district: member.district,
    category: currentCategory,
    profile: member.registered ? "Complete" : "Needs attention",
    status: member.verified ? "Verified" : "Registered",
    joined: "This browser",
    location: member.registrationLocation ? "Location allowed" : "District only",
    current: true,
  } : null;
  const rows: AdminMemberRow[] = [currentRow, ...sampleAdminMembers].filter((row): row is AdminMemberRow => row !== null);
  const districtOptions = Array.from(new Set(rows.map((row) => row.district))).sort();
  const filtered = rows.filter((row) => {
    const term = query.trim().toLowerCase();
    const matchesQuery = !term || `${row.id} ${row.district} ${row.category}`.toLowerCase().includes(term);
    return matchesQuery && (status === "All statuses" || row.status === status) && (district === "All districts" || row.district === district);
  });

  return (
    <>
      <AdminPageHeader eyebrow="Community health" title="Members, without the guesswork." description={`Track registration quality and readiness across ${period.toLowerCase()}, without exposing personal details.`} action="Export aggregate members" onAction={() => downloadAdminCsv("Kitanego members", notify)} />
      <div className="admin-notice admin-notice--privacy"><ShieldCheck size={17} /><span><strong>Anonymised prototype view.</strong> Names, emails, phone numbers and precise coordinates are not shown here.</span></div>
      <section className="admin-kpis"><Kpi label="Registered members" value="8,246" change="+12.4%" icon={Users} tone="mint" /><Kpi label="New this period" value="912" change="11.1% of total" icon={TrendingUp} tone="lime" /><Kpi label="Profiles complete" value="6,267" change="76.0% rate" icon={CheckCircle2} tone="sky" /><Kpi label="Verified members" value="2,184" change="26.5% rate" icon={BadgeCheck} tone="coral" /></section>
      <section className="admin-card admin-data-card">
        <div className="admin-toolbar">
          <label className="admin-search"><Search size={17} /><span className="sr-only">Search members</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search ID, district or category" /></label>
          <label><span className="sr-only">Filter by district</span><select value={district} onChange={(event) => setDistrict(event.target.value)}><option value="All districts">All districts</option>{districtOptions.map((option) => <option key={option} value={option}>{option}</option>)}</select></label>
          <label><span className="sr-only">Filter by status</span><select value={status} onChange={(event) => setStatus(event.target.value)}><option value="All statuses">All statuses</option><option value="Registered">Registered</option><option value="Verified">Verified</option></select></label>
        </div>
        <div className="admin-member-list">
          <div className="admin-member-row admin-member-row--head" aria-hidden="true"><span>Member</span><span>District</span><span>Strongest signal</span><span>Status</span><span>Joined</span><span /></div>
          {filtered.map((row) => (
            <div key={row.id} className={`admin-member-item ${selectedId === row.id ? "open" : ""}`}>
              <button type="button" className="admin-member-row" onClick={() => setSelectedId(selectedId === row.id ? null : row.id)} aria-expanded={selectedId === row.id}>
                <span><strong>{row.id}</strong>{row.current && <em>Current browser</em>}</span><span data-label="District">{row.district}</span><span data-label="Strongest signal">{row.category}</span><span data-label="Status"><i className={`row-status ${row.status === "Verified" ? "verified" : ""}`} />{row.status}</span><span data-label="Joined">{row.joined}</span><ChevronRight size={17} />
              </button>
              {selectedId === row.id && <div className="member-detail"><div><small>Profile state</small><strong>{row.profile}</strong></div><div><small>Location consent</small><strong>{row.location}</strong></div><div><small>Privacy</small><strong>No direct identifiers shown</strong></div></div>}
            </div>
          ))}
          {filtered.length === 0 && <div className="admin-empty"><Search /><strong>No matching members</strong><span>Adjust the search or filters to see more prototype records.</span><button type="button" className="text-button" onClick={() => { setQuery(""); setStatus("All statuses"); setDistrict("All districts"); }}>Clear filters</button></div>}
        </div>
      </section>
    </>
  );
}

function AdminDemand({ profiles, profilesStatus }: { profiles: LiveMemberProfile[]; profilesStatus: "idle" | "loading" | "ready" | "error" }) {
  const currentMonth = new Date();
  const currentMonthKey = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, "0")}`;
  const defaultFilters = { mode: "month" as "month" | "year" | "custom", month: currentMonthKey, year: String(currentMonth.getFullYear()), startDate: "", endDate: "", district: "All districts" };
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters);
  const [draftFilters, setDraftFilters] = useState(defaultFilters);
  const [demandQuery, setDemandQuery] = useState("");
  const [rankingType, setRankingType] = useState<"brand" | "product" | "category" | "district">("brand");
  const [selectedRanking, setSelectedRanking] = useState<string | null>(null);
  const updateFilters = (patch: Partial<typeof draftFilters>) => {
    const next = { ...draftFilters, ...patch };
    setDraftFilters(next);
    setAppliedFilters(next);
  };
  const resetFilters = () => {
    setDraftFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
  };
  const monthOptions = Array.from({ length: 12 }, (_, monthIndex) => {
    const date = new Date(currentMonth.getFullYear(), monthIndex, 1);
    return { value: `${date.getFullYear()}-${String(monthIndex + 1).padStart(2, "0")}`, label: new Intl.DateTimeFormat("en", { month: "short" }).format(date) };
  });
  const yearOptions = Array.from(new Set([currentMonth.getFullYear(), ...profiles.map((profile) => new Date(profile.createdAt).getFullYear()).filter(Number.isFinite)])).sort((a, b) => b - a);
  const districtOptions = Array.from(new Set(profiles.map((profile) => profile.district).filter(Boolean))).sort();
  const periodProfiles = profiles.filter((profile) => {
    const createdAt = new Date(profile.createdAt);
    if (Number.isNaN(createdAt.getTime())) return false;
    if (appliedFilters.district !== "All districts" && profile.district !== appliedFilters.district) return false;
    if (appliedFilters.mode === "month") return `${createdAt.getFullYear()}-${String(createdAt.getMonth() + 1).padStart(2, "0")}` === appliedFilters.month;
    if (appliedFilters.mode === "year") return String(createdAt.getFullYear()) === appliedFilters.year;
    const profileDay = profile.createdAt.slice(0, 10);
    return (!appliedFilters.startDate || profileDay >= appliedFilters.startDate) && (!appliedFilters.endDate || profileDay <= appliedFilters.endDate);
  });
  const normalizedDemandQuery = demandQuery.trim().toLowerCase();
  const categoryLabel = (categoryId: string) => categories.find((category) => category.id === categoryId)?.label ?? categoryId;
  const scopedProfiles = normalizedDemandQuery ? periodProfiles.filter((profile) =>
    profile.district.toLowerCase().includes(normalizedDemandQuery)
    || profile.categories.some((category) => categoryLabel(category).toLowerCase().includes(normalizedDemandQuery))
    || profile.products.some((product) => product.toLowerCase().includes(normalizedDemandQuery))
    || profile.brands.some((brand) => brand.toLowerCase().includes(normalizedDemandQuery)),
  ) : periodProfiles;
  const searchSuggestions = Array.from(new Map([
    ...profiles.flatMap((profile) => profile.categories.map((category) => [categoryLabel(category), "Category"] as const)),
    ...profiles.flatMap((profile) => profile.products.map((product) => [product, "Product"] as const)),
    ...profiles.flatMap((profile) => profile.brands.map((brand) => [brand, "Brand"] as const)),
    ...profiles.map((profile) => [profile.district, "District"] as const),
  ].filter(([value]) => value)).entries()).map(([value, type]) => ({ value, type })).sort((a, b) => a.value.localeCompare(b.value));
  const categoryRowsLive = Array.from(scopedProfiles.reduce((counts, profile) => {
    profile.categories.forEach((category) => counts.set(category, (counts.get(category) ?? 0) + 1));
    return counts;
  }, new Map<string, number>()).entries()).map(([id, members]) => ({ id, label: categories.find((category) => category.id === id)?.label ?? id, members })).sort((a, b) => b.members - a.members);
  const productRowsLive = Array.from(scopedProfiles.reduce((counts, profile) => {
    profile.products.forEach((product) => counts.set(product, (counts.get(product) ?? 0) + 1));
    return counts;
  }, new Map<string, number>()).entries()).sort((a, b) => b[1] - a[1]);
  const brandRowsLive = Array.from(scopedProfiles.reduce((counts, profile) => {
    profile.brands.forEach((brand) => counts.set(brand, (counts.get(brand) ?? 0) + 1));
    return counts;
  }, new Map<string, number>()).entries()).sort((a, b) => b[1] - a[1]);
  const districtRowsLive = Array.from(scopedProfiles.reduce((counts, profile) => {
    counts.set(profile.district, (counts.get(profile.district) ?? 0) + 1);
    return counts;
  }, new Map<string, number>()).entries()).sort((a, b) => b[1] - a[1]);
  const rankingRows = (rankingType === "brand"
    ? brandRowsLive
    : rankingType === "product"
      ? productRowsLive
      : rankingType === "category"
        ? categoryRowsLive.map((row) => [row.label, row.members] as [string, number])
        : districtRowsLive).map(([label, count]) => ({ label, count }));
  const rankingCounts = { brand: brandRowsLive.length, product: productRowsLive.length, category: categoryRowsLive.length, district: districtRowsLive.length };
  const rankingLabel = rankingType.charAt(0).toUpperCase() + rankingType.slice(1);
  const rankingInitials = (label: string) => label.split(/\s+/).map((word) => word[0]).join("").slice(0, 2).toUpperCase();
  const activeRanking = selectedRanking && rankingRows.some((row) => row.label === selectedRanking) ? selectedRanking : null;
  const mapProfiles = activeRanking ? scopedProfiles.filter((profile) => {
    if (rankingType === "brand") return profile.brands.includes(activeRanking);
    if (rankingType === "product") return profile.products.includes(activeRanking);
    if (rankingType === "category") return profile.categories.some((category) => categoryLabel(category) === activeRanking);
    return profile.district === activeRanking;
  }) : scopedProfiles;
  const selectRanking = (label: string) => setSelectedRanking((selected) => selected === label ? null : label);
  return (
    <>
      <section className="admin-title demand-title">
        <div><h1 tabIndex={-1}>Demand</h1></div>
      </section>
      <div className="demand-search-toolbar">
        <label className="demand-search"><Search aria-hidden="true" /><span className="sr-only">Search products, categories, brands or districts</span><input type="search" list="demand-search-suggestions" value={demandQuery} onChange={(event) => setDemandQuery(event.target.value)} placeholder="Search product, category, brand or district..." /><datalist id="demand-search-suggestions">{searchSuggestions.map((option) => <option key={`${option.type}-${option.value}`} value={option.value} label={option.type} />)}</datalist></label>
        <button type="button" className={`button button--ghost demand-filter-trigger${filtersOpen ? " active" : ""}`} aria-label="Open demand filters" aria-expanded={filtersOpen} aria-controls="demand-filter-panel" data-tooltip="Filter demand" onClick={() => setFiltersOpen((open) => !open)}><Filter aria-hidden="true" /></button>
      </div>
      {filtersOpen && <section id="demand-filter-panel" className="admin-card demand-filter-panel" aria-label="Demand filters">
        <div className="demand-filter-panel__head"><div><strong>Filter by Date, Month or District</strong></div><button type="button" className="icon-button" aria-label="Close demand filters" onClick={() => setFiltersOpen(false)}><X /></button></div>
        <div className={`demand-filter-fields${draftFilters.mode === "custom" ? " demand-filter-fields--custom" : ""}`}>
          <label>Period type<select value={draftFilters.mode} onChange={(event) => updateFilters({ mode: event.target.value as "month" | "year" | "custom" })}><option value="month">Month</option><option value="year">Year</option><option value="custom">Custom date</option></select></label>
          {draftFilters.mode === "month" && <label>Month<select value={draftFilters.month} onChange={(event) => updateFilters({ month: event.target.value })}>{monthOptions.map((month) => <option key={month.value} value={month.value}>{month.label} {month.value.slice(0, 4)}</option>)}</select></label>}
          {draftFilters.mode === "year" && <label>Year<select value={draftFilters.year} onChange={(event) => updateFilters({ year: event.target.value })}>{yearOptions.map((year) => <option key={year}>{year}</option>)}</select></label>}
          {draftFilters.mode === "custom" && <><label>Start date<input type="date" value={draftFilters.startDate} onChange={(event) => updateFilters({ startDate: event.target.value })} /></label><label>End date<input type="date" min={draftFilters.startDate || undefined} value={draftFilters.endDate} onChange={(event) => updateFilters({ endDate: event.target.value })} /></label></>}
          <label>District<select value={draftFilters.district} onChange={(event) => updateFilters({ district: event.target.value })}><option>All districts</option>{districtOptions.map((district) => <option key={district}>{district}</option>)}</select></label>
        </div>
        <div className="demand-filter-actions"><button type="button" className="button button--ghost" onClick={resetFilters}>Reset</button></div>
      </section>}
      {profiles.length === 0 ? <section className="admin-card"><LiveDataState status={profilesStatus} emptyMessage="Demand groups will appear after members submit their profiles." /></section> : <>
        <section className="admin-card demand-leaderboard">
          <div className="demand-leaderboard__head"><div><h2>{rankingLabel} ranking</h2></div><strong>{rankingRows.length} results</strong></div>
          <div className="demand-ranking-tabs" role="tablist" aria-label="Rank demand by">{(["brand", "product", "category", "district"] as const).map((type) => <button type="button" role="tab" aria-selected={rankingType === type} className={rankingType === type ? "active" : ""} key={type} onClick={() => { setRankingType(type); setSelectedRanking(null); }}>{type.charAt(0).toUpperCase() + type.slice(1)} ({rankingCounts[type]})</button>)}</div>
          {rankingRows.length > 0 ? <>
            <div className="demand-ranking-podium">{rankingRows.slice(0, 3).map((row, index) => <button type="button" aria-pressed={activeRanking === row.label} aria-label={`Show ${row.count} map ${row.count === 1 ? "point" : "points"} for ${row.label}`} className={`demand-ranking-winner demand-ranking-winner--${index + 1}${activeRanking === row.label ? " selected" : ""}`} key={row.label} onClick={() => selectRanking(row.label)}><span className="demand-ranking-avatar">{rankingInitials(row.label)}<i>{index + 1}</i></span><strong>{row.label}</strong><em>{row.count} {row.count === 1 ? "demand" : "demands"}</em></button>)}</div>
            {rankingRows.length > 3 && <div className="demand-ranking-list">{rankingRows.slice(3).map((row, index) => <button type="button" aria-pressed={activeRanking === row.label} aria-label={`Show ${row.count} map ${row.count === 1 ? "point" : "points"} for ${row.label}`} className={`demand-ranking-row${activeRanking === row.label ? " selected" : ""}`} key={row.label} onClick={() => selectRanking(row.label)}><span>{index + 4}</span><i>{rankingInitials(row.label)}</i><strong>{row.label}</strong><em>{row.count}</em></button>)}</div>}
          </> : <div className="demand-ranking-empty"><Search /><strong>No {rankingLabel.toLowerCase()} demand found</strong><span>Try another search or adjust the filters.</span></div>}
        </section>
        <LiveRegistrationMap profiles={mapProfiles} />
      </>}
    </>
  );

}

function AdminOpportunities({ period, notify }: { period: string; notify: (message: string) => void }) {
  return (
    <>
      <AdminPageHeader title="Opportunities" description={`Supplier opportunities created from verified demand will appear here for ${period.toLowerCase()}.`} action="Create opportunity" actionIcon={Plus} onAction={() => notify("Opportunity creation will be available after the first live demand group is ready")} />
      <div className="admin-notice admin-notice--privacy"><ShieldCheck size={17} /><span><strong>No sample opportunities.</strong> Kitanego will only show opportunities created from live member demand.</span></div>
      <section className="admin-kpis"><Kpi label="Live opportunities" value="0" change="No active offers" icon={Package} tone="coral" /><Kpi label="Ready to review" value="0" change="Awaiting live demand" icon={CheckCircle2} tone="mint" /><Kpi label="Commitments" value="0" change="No commitments yet" icon={HeartHandshake} tone="sky" /><Kpi label="Supplier reviews" value="0" change="No reviews yet" icon={BadgeCheck} tone="lime" /></section>
      <section className="admin-card"><LiveDataState status="ready" emptyMessage="When a real demand group is ready for supplier engagement, create and manage it here." /></section>
    </>
  );

  const [filter, setFilter] = useState("All");
  const [selectedId, setSelectedId] = useState("rice");
  const filtered = adminOpportunities.filter((item) => filter === "All" || item.status === filter);
  const selected = adminOpportunities.find((item) => item.id === selectedId) ?? adminOpportunities[0];

  return (
    <>
      <AdminPageHeader title="Opportunities" description={`Review which demand groups are ready for supplier engagement across ${period.toLowerCase()}.`} action="Create prototype opportunity" actionIcon={Plus} onAction={() => notify("Opportunity builder will connect in a future phase")} />
      <div className="admin-notice"><Sparkles size={17} /><span><strong>Prototype pipeline.</strong> Statuses show how a signal moves from validation to supplier-ready evidence.</span></div>
      <section className="admin-kpis"><Kpi label="Live pilots" value="1" change="Rice · 10kg" icon={Package} tone="coral" /><Kpi label="Ready to launch" value="2" change="Strong evidence" icon={CheckCircle2} tone="mint" /><Kpi label="Total commitments" value="684" change="Current live pilot" icon={HeartHandshake} tone="sky" /><Kpi label="Verified conversion" value="31.3%" change="Of verified members" icon={BadgeCheck} tone="lime" /></section>
      <div className="opportunity-filter" role="group" aria-label="Filter opportunities">{["All", "Live", "Supplier-ready", "Validating"].map((label) => <button type="button" key={label} className={filter === label ? "active" : ""} aria-pressed={filter === label} onClick={() => { setFilter(label); const firstMatch = adminOpportunities.find((item) => label === "All" || item.status === label); if (firstMatch) setSelectedId(firstMatch.id); }}>{label}</button>)}</div>
      <section className="opportunity-admin-grid">
        {filtered.map((item) => {
          const progress = Math.min(100, Math.round((item.committed / item.target) * 100));
          return <article key={item.id} className={`admin-opportunity-card ${selectedId === item.id ? "selected" : ""}`}><div className="opportunity-card-top"><span className={`opportunity-status tone-${item.tone}`}>{item.status}</span><small>{item.category}</small></div><h2>{item.title}</h2><div className="opportunity-progress"><span><strong>{item.committed}</strong> of {item.target} commitments</span><em>{progress}%</em><div><i style={{ width: `${progress}%` }} /></div></div><dl><div><dt>Coverage</dt><dd>{item.coverage}</dd></div><div><dt>Timing</dt><dd>{item.closes}</dd></div></dl><button type="button" className="button button--ghost button--full" aria-pressed={selectedId === item.id} aria-label={`View ${item.title} details`} onClick={() => setSelectedId(item.id)}>View details <ChevronRight size={16} /></button></article>;
        })}
      </section>
      <section className="admin-card opportunity-detail-panel"><div><span className="sample-label">Selected opportunity</span><h2>{selected.title}</h2><p>The signal has reached {selected.committed} prototype commitments across {selected.coverage}. The next production step is supplier review with aggregate evidence only.</p></div><div className="detail-actions"><button type="button" className="button button--ink" onClick={() => notify(`${selected.title} evidence preview opened`)}>Preview evidence</button><button type="button" className="button button--ghost" onClick={() => downloadAdminCsv(`${selected.title} evidence`, notify)}>Download CSV</button></div></section>
    </>
  );
}

function AdminReports({ notify }: { period: string; notify: (message: string) => void }) {
  return (
    <>
      <section className="admin-title reports-title">
        <div><h1 tabIndex={-1}>Reports</h1><p>Summary of available data in database record.</p></div>
        <button type="button" className="button button--ink admin-report-action" aria-label="Generate PDF report" data-tooltip="Generate PDF report" onClick={() => notify("PDF reports become available after data is collected")}><FileDown aria-hidden="true" /></button>
      </section>
      <section className="admin-card"><div className="admin-empty"><FileText /><strong>No data records yet</strong></div></section>
    </>
  );
}

function CleanMemberMap({ profiles, selectedId, onSelect }: { profiles: LiveMemberProfile[]; selectedId: string; onSelect: (id: string) => void }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    mapRef.current?.remove();
    const map = L.map(containerRef.current, { zoomControl: true, scrollWheelZoom: true, minZoom: 6, maxZoom: 18 });
    mapRef.current = map;
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap contributors",
      maxZoom: 19,
    }).addTo(map);
    const points = profiles
      .filter((profile) => profile.latitude != null && profile.longitude != null && isWithinSabah(profile.latitude, profile.longitude))
      .map((profile) => {
        const marker = L.circleMarker([profile.latitude!, profile.longitude!], {
          radius: profile.id === selectedId ? 8 : 6,
          color: "#ffffff",
          weight: 3,
          fillColor: profile.id === selectedId ? "#ff775a" : "#1187d2",
          fillOpacity: 0.92,
        }).addTo(map);
        marker.bindTooltip(profile.district, { direction: "top", offset: [0, -7], opacity: 0.95 });
        marker.on("click", () => onSelect(profile.id));
        return L.latLng(profile.latitude!, profile.longitude!);
      });
    if (points.length === 1) map.setView(points[0], 14);
    else if (points.length > 1) map.fitBounds(L.latLngBounds(points), { padding: [38, 38], maxZoom: 14 });
    else map.setView([5.55, 117.1], 7);
    return () => {
      map.remove();
      if (mapRef.current === map) mapRef.current = null;
    };
  }, [profiles, selectedId, onSelect]);

  return <div ref={containerRef} className="clean-member-map" aria-label="Interactive map showing member demand locations" />;
}

function LiveRegistrationMap({ profiles }: { profiles: LiveMemberProfile[] }) {
  const locatedProfiles = profiles.filter((profile) => profile.latitude != null && profile.longitude != null && isWithinSabah(profile.latitude, profile.longitude));
  const [selectedLocationId, setSelectedLocationId] = useState(() => locatedProfiles[0]?.id ?? "");
  const selectedLocation = locatedProfiles.find((profile) => profile.id === selectedLocationId) ?? locatedProfiles[0] ?? null;
  const districtCounts = Array.from(profiles.reduce((counts, profile) => {
    counts.set(profile.district, (counts.get(profile.district) ?? 0) + 1);
    return counts;
  }, new Map<string, number>()).entries()).sort((a, b) => b[1] - a[1]);

  return (
    <article className="admin-card registration-map-card">
      <div className="admin-card__head"><div><strong>Map Distribution</strong></div></div>
      <div className="registration-map-layout">
        <div className="google-registration-map">
          <div className="google-map-toolbar">
            <label>
              <span>Select location</span>
              <select value={selectedLocation?.id ?? ""} onChange={(event) => setSelectedLocationId(event.target.value)} disabled={locatedProfiles.length === 0}>
                {locatedProfiles.length === 0
                  ? <option value="">No captured locations</option>
                  : locatedProfiles.map((profile, index) => <option key={profile.id} value={profile.id}>{profile.district} registration {index + 1}</option>)}
              </select>
            </label>
          </div>
          <div className="google-map-frame"><CleanMemberMap profiles={locatedProfiles} selectedId={selectedLocation?.id ?? ""} onSelect={setSelectedLocationId} /></div>
        </div>
        <aside className="location-summary location-summary--districts">
          <h3>Total members by district</h3>
          {districtCounts.length > 0 && <ol className="live-district-list">{districtCounts.slice(0, 8).map(([district, count]) => <li key={district}><span>{district}</span><strong>{count}</strong></li>)}</ol>}
        </aside>
      </div>
    </article>
  );
}

function RegistrationMap({ member }: { member: MemberState }) {
  const outlinePoints = sabahOutline.map(([longitude, latitude]) => {
    const point = projectSabahCoordinate(latitude, longitude);
    return `${point.x},${point.y}`;
  }).join(" ");
  const location = member.registrationLocation && isWithinSabah(member.registrationLocation.latitude, member.registrationLocation.longitude)
    ? member.registrationLocation
    : null;
  const latestPoint = location ? projectSabahCoordinate(location.latitude, location.longitude) : null;
  const capturedTime = location ? new Date(location.capturedAt).toLocaleString(readInitialLanguage() === "ms" ? "ms-MY" : "en-MY", { dateStyle: "medium", timeStyle: "short" }) : "";

  return (
    <article className="admin-card registration-map-card">
      <div className="admin-card__head"><div><span>Registration geography</span><strong>Where demand is forming</strong></div><span className="admin-only-badge"><LockKeyhole size={13} /> Browser-local preview</span></div>
      <div className="registration-map-layout">
        <div className="sabah-map">
          <svg viewBox={`0 0 ${MAP_VIEWBOX.width} ${MAP_VIEWBOX.height}`} role="img" aria-labelledby="registration-map-title registration-map-description">
            <title id="registration-map-title">Sabah registration location map</title>
            <desc id="registration-map-description">Illustrative registration clusters across Sabah{location ? ` with the latest prototype registration plotted after ${member.district} was selected` : ". No device location has been captured in this browser"}.</desc>
            <defs>
              <linearGradient id="sabah-land" x1="0" x2="1" y1="0" y2="1"><stop offset="0" stopColor="#c8e6d6" /><stop offset="1" stopColor="#8fbea8" /></linearGradient>
              <pattern id="map-grid" width="70" height="70" patternUnits="userSpaceOnUse"><path d="M 70 0 L 0 0 0 70" fill="none" stroke="rgba(23,51,45,.08)" strokeWidth="1" /></pattern>
              <filter id="map-shadow" x="-20%" y="-20%" width="140%" height="140%"><feDropShadow dx="0" dy="10" stdDeviation="12" floodColor="#17332d" floodOpacity=".16" /></filter>
            </defs>
            <rect width="1000" height="620" rx="30" fill="#eaf2ed" />
            <rect width="1000" height="620" rx="30" fill="url(#map-grid)" />
            <polygon points={outlinePoints} fill="url(#sabah-land)" stroke="#6d9f89" strokeWidth="4" filter="url(#map-shadow)" />
            <circle cx="132" cy="112" r="19" fill="#b6d8c7" stroke="#6d9f89" strokeWidth="3" />
            <circle cx="172" cy="82" r="10" fill="#b6d8c7" />
            {sampleRegistrationPoints.map((point) => {
              const projected = projectSabahCoordinate(point.latitude, point.longitude);
              const radius = 18 + Math.min(22, point.count / 80);
              return <g key={point.label} className="map-point map-point--sample"><circle className="map-point__halo" cx={projected.x} cy={projected.y} r={radius} /><circle className="map-point__core" cx={projected.x} cy={projected.y} r="8" /><text x={projected.x + 15} y={projected.y - 13}>{point.label}</text><title>{point.label}: {point.count.toLocaleString()} illustrative registrations</title></g>;
            })}
            {location && latestPoint && <g className="map-point map-point--latest" tabIndex={0} role="img" aria-label={`Current prototype registration captured after ${member.district} was selected, accuracy approximately ${location.accuracy} metres`}><circle className="map-point__pulse" cx={latestPoint.x} cy={latestPoint.y} r="34" /><circle className="map-point__halo" cx={latestPoint.x} cy={latestPoint.y} r="22" /><circle className="map-point__core" cx={latestPoint.x} cy={latestPoint.y} r="10" /><title>Current prototype registration · selected district {member.district} · ±{location.accuracy} metres</title></g>}
          </svg>
          <div className="map-legend"><span><i className="legend-dot legend-dot--sample" /> Illustrative clusters</span><span><i className="legend-dot legend-dot--current" /> This prototype session</span></div>
        </div>
        <aside className="location-summary">
          <span className={`location-summary__status ${location ? "ready" : ""}`}>{location ? <CheckCircle2 size={15} /> : <CircleHelp size={15} />}{location ? "Location available" : "District only"}</span>
          <h3>{location ? "Latest registration located." : "No device location yet."}</h3>
          <p>{location ? "This current browser session is plotted separately from the illustrative clusters." : "Members can still register using their selected district when location permission is declined or unavailable."}</p>
          {location ? <dl className="location-metrics"><div><dt>Selected district</dt><dd>{member.district}</dd></div><div><dt>Latitude</dt><dd>{location.latitude.toFixed(5)}</dd></div><div><dt>Longitude</dt><dd>{location.longitude.toFixed(5)}</dd></div><div><dt>Device accuracy</dt><dd>±{location.accuracy} m</dd></div><div className="location-metrics__wide"><dt>Captured</dt><dd>{capturedTime}</dd></div></dl> : <div className="location-empty"><MapPin /><span><strong>Waiting for permission</strong>The map will update after a member chooses “Use my current location.”</span></div>}
          <div className="map-privacy-note"><ShieldCheck size={17} /><span><strong>Browser-local preview.</strong> Names and emails stay off the map. Supplier reports exclude coordinates.</span></div>
          <small className="local-data-note">Prototype note: the current-session point is stored only in this browser until the live data service is connected.</small>
        </aside>
      </div>
    </article>
  );
}

function Kpi({ label, value, change, icon: Icon, tone }: { label: string; value: string; change?: string; icon: LucideIcon; tone: string }) {
  return <article><span className={`stat-icon tone-${tone}`}><Icon /></span><div><small>{label}</small><strong>{value}</strong>{change && <span>{change}</span>}</div></article>;
}

export default App;
